import "./styles/app.css";
import { highlightCodeClassFor, renderHighlightedFragment, wrapHighlightLines } from "./editor/syntaxHighlight.js";
import {
  type MarkEntry,
  MARK_COLOR_PRESETS,
  bundleSharePayload,
  loadMarkupFromStorage,
  markupStorageKey,
  newMarkId,
  persistMarkupToStorage,
  renderMarkupOverlayHtml,
  unbundleSharePayload,
} from "./editor/markupLayer.js";
import { markdownToSafeHtml, markdownToSafeHtmlAnnotated } from "./editor/markdownPreview.js";
import { buildTexPreviewHtml, renderTexMathInPreviewRoot } from "./editor/texPreview.js";
import {
  buildMaxMarkdownImageLine,
  MAX_BROWSER_IMAGE_FILE_ACCEPT,
  MAX_BROWSER_PDF_FILE_ACCEPT,
  readImageFileAsDataUrl,
} from "./editor/maxBrowseImageSupport.js";
import {
  buildMaxHtmlHexEmbedMarkdownFromRawHtml,
  MAX_BROWSER_HTML_FILE_ACCEPT,
  readHtmlFileAsUtf8Text,
} from "./editor/maxBrowseHtmlSupport.js";
import {
  buildMaxVideoEmbedHtml,
  MAX_BROWSER_VIDEO_FILE_ACCEPT,
  readVideoFileAsDataUrl,
} from "./editor/maxBrowseVideoSupport.js";
import {
  buildMaxPreviewHtmlAnnotated,
  buildMaxRenderedMarkdown,
  hydrateMaxPreviewRoot,
  revokeMaxHexMediaBlobUrls,
} from "./editor/maxPreview.js";
import {
  buildMaxAudioHexMarkdownLink,
  buildMaxVideoHexMarkdownLink,
  MAX_MAX_HEX_MEDIA_CLIP_BYTES,
} from "./editor/maxBrowseHexMediaSupport.js";
import {
  getPdfPageCountFromFile,
  MAX_PDF_INSERT_PAGES_PER_ACTION,
  parsePdfInsertPageSelection,
  pdfDataUrlWithPage,
  readPdfFileAsDataUrl,
} from "./editor/maxPdfPageRaster.js";
import {
  buildBrowserLikePreviewSrcdoc,
  htmlPreviewPdfCaptureRoot,
} from "./editor/htmlPreview.js";
import {
  applyOeisSequenceIdToSource,
  applyOeisTermsSearchLineToSource,
  formatOeisEntryHtml,
  formatOeisErrorHtml,
  formatOeisHintHtml,
  formatOeisLoadingHtml,
  formatOeisSearchPickListHtml,
  isOeisOrgHostname,
  mergeOeisSearchHitRows,
  oeisAnumberFromSearchHit,
  oeisOrgHttpUrlToApiProxyPath,
  oeisSequenceIdFromOeisOrgHttpUrl,
  parseOeisSearchJson,
  parseOeisSearchJsonRows,
  parseOeisSeqIdFromInput,
  parseOeisTermsSearchFromInput,
  previewHyperlinkOpensHtmlInExternalBrowser,
  oeisSearchJsonUrl,
  oeisSearchJsonUrlForRawQuery,
  resolveOeisPreviewQuery,
  urlPathnameLooksLikeDownloadedPlainText,
  type OeisJsonEntry,
} from "./editor/oeisPreview.js";
import { CMD, FILE_RECENT_SLOT_IDS } from "./menu/commandIds.js";
import { CommandDispatcher } from "./menu/CommandDispatcher.js";
import { NPP_COMMAND_EVENT, type NppCommandDetail } from "./menu/commandEvents.js";
import { MenuBar } from "./menu/MenuBar.js";
import { buildNotepadMenuModel } from "./menu/notepadMenuModel.js";
import { LANGUAGE_MENU_ROWS } from "./menu/languageMenuItems.js";
import { DocumentManager, type TabDocument } from "./doc/DocumentManager.js";
import { inferLanguageModeFromFilename } from "./doc/inferLanguageFromFilename.js";
import { persistTabSession, tryRestoreTabSessionInto } from "./doc/tabSessionCache.js";
import type { TabColorId } from "./doc/tabColors.js";
import { encodingLabel } from "./doc/encodingLabel.js";
import { languageLabel, type LanguageMode } from "./doc/languageMode.js";
import { monacoLanguageId } from "./editor/monacoLanguageId.js";
import { MAX_FIND_ALL_HITS } from "./search/findInOpenedTabs.js";
import { TabBar } from "./ui/TabBar.js";
import {
  createMultiviewExtraSlot,
  MULTIVIEW_MAX_EXTRA,
  MULTIVIEW_MAX_PANES,
  type MultiviewExtraSlot,
} from "./ui/multiviewSlots.js";
import { mountDiffSplitPanes } from "./ui/diffSplitPanes.js";
import { FindResultsPane } from "./ui/FindResultsPane.js";
import { FolderWorkspacePane } from "./ui/FolderWorkspacePane.js";
import { GitTerminalPanel } from "./ui/GitTerminalPanel.js";
import { getLargestPackfileBytes } from "./io/git/workspacePackStats.js";
import { isWorkspaceGitRepository } from "./io/git/workspaceGitInfo.js";
import { gitWorkspaceCloneInto } from "./io/git/gitWorkspaceOperations.js";
import { pickWorkspaceDirectory } from "./io/openWorkspaceFolder.js";
import {
  getOrCreateNullapadInstanceSecret,
  isValidNullapadInstanceSecretFormat,
  normalizeNullapadInstanceSecretInput,
  persistNullapadInstanceSecret,
} from "./io/nullapadInstanceSecret.js";
import {
  clearStagedShare,
  readStagedShare,
  writeStagedShare,
  type StagedShareRecord,
} from "./io/nullapadShareStagedCache.js";
import {
  resolveNullapadShareInboxFingerprint,
  deleteNullapadShareInboxMessage,
  downloadMultipartInboxAsUtf8,
  fetchNullapadShareInboxMessage,
  getLastNullapadShareRecipientSecret,
  getSavedNullapadShareEndpoint,
  NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS,
  nullapadShareSuggestedTabTitle,
  saveLastNullapadShareRecipientSecret,
  shareTabViaShareSession,
  type NullapadShareInboxMultipartMeta,
  type NullapadShareInboxSimpleMessage,
} from "./io/nullapadShare.js";
import {
  loadNullapadOeisLastTermsQuery,
  persistNullapadOeisLastTermsQuery,
  persistNullapadOeisSearchSnapshot,
} from "./io/nullapadOeisSearchCache.js";
import {
  loadWorkspaceFolderHandle,
  persistWorkspaceFolderHandle,
} from "./io/workspaceFolderCache.js";
import { applyMarkdownPreviewTheme } from "./settings/markdownPreviewStyleApply.js";
import { loadWebPrefs, saveWebPrefs } from "./settings/webPrefs.js";
import { shortcutFromKeyboardEvent } from "./settings/keyboardChord.js";
import { buildChordToCommandMap } from "./settings/keyboardShortcuts.js";
import { openShortcutMapperDialog } from "./dialogs/shortcutMapperDialog.js";
import { findNextIndex, findPrevIndex } from "./text/findInText.js";
import { matchingBraceOffset } from "./text/matchingBrace.js";
import { createCsvGridEditor, type CsvGridEditorApi } from "./csv/csvGridEditor.js";
import {
  activeSheetCsvFromWorkbookJson,
  emptyWorkbookJson,
  mergeActiveSheetCsvIntoWorkbookJson,
  parseNppExcelDocOrNull,
  setActiveSheetIndexInJson,
} from "./io/excelWorkbook.js";
import { getFindSession, setFindSession } from "./text/findSession.js";
import { base64ToUtf8, utf8ToBase64 } from "./text/base64Utf8.js";
import { tabTextEditorSpellcheckBlockedByBuffer } from "./text/editorSpellcheckPolicy.js";
import {
  isMisspelledWord,
  normalizeSpellcheckExceptionWord,
  setSpellcheckExceptionWords,
  suggestSpellings,
  wordRangeAt,
} from "./editor/proseSpellDecorate.js";
import { requestBeautifySelection } from "./io/beautifySelectionApi.js";
import {
  openAboutNullaPadDialog,
  openMaxLanguageGuideDialog,
  openNullaPadUsersGuideDialog,
} from "./dialogs/aboutNullaPadDialog.js";
import { openExportPdfPreviewDialog } from "./dialogs/exportPdfDialog.js";
import { openHtmlFromUrlDialog } from "./dialogs/openHtmlFromUrlDialog.js";
import { openBeautifySelectionDialog } from "./dialogs/beautifySelectionDialog.js";
import { sanitizePdfFilename } from "./io/exportRenderedPdf.js";
import quickstartMaxExample from "../demo/quickstart.max?raw";
import showcaseMaxExample from "../demo/showcase.max?raw";
import mathPaperMaxExample from "../demo/math_paper.max?raw";
import {
  isNullaContent,
  removeNullaClipSectionAt,
  parseSections,
  encodeSections,
  decodeVideoBlobs,
  blobToVideoSection,
  blobToVoiceEnrichment,
  hex64ToBytes,
  lastNullaMicTargetContentSectionIndex,
  setTextSections,
  insertVideoAfterText,
  type NullaSection,
} from "./editor/nullaCodec.js";
import {
  duplicateLineAtCaret,
  indentLines,
  insertAt,
  joinLines,
  moveLine,
  outdentLines,
  removeEmptyLines,
  sortLines,
  transformSelectionOrWord,
  trimTrailingWhitespace,
  wordBoundsAt,
} from "./text/editOps.js";
import { buildColumnBlockPlainText } from "./text/rectangularColumnText.js";
import { RectSelectOverlay } from "./text/rectSelectOverlay.js";
import { textareaOffsetAtClientPoint } from "./text/textareaOffsetAtClientPoint.js";
import {
  openCountDialog,
  openFindDialog,
  openFindInDirectoryDialog,
  openFindInFilesDialog,
  openGotoLineDialog,
  openIncrementalSearchDialog,
  openPreferencesDialog,
  openRecentFilesDialog,
  openReplaceDialog,
} from "./dialogs/simpleDialogs.js";
import { openMarkdownPreviewStyleDialog } from "./dialogs/markdownPreviewStyleDialog.js";
import { renderLinuxObjectPreview } from "./io/libraryNmPreview.js";
import {
  buildEditorLineUniformModel,
  clampEditorScrollTop,
  paddingBottomPx,
  paddingTopPx,
  resolvedLineHeightPx,
  scrollTopToShowLogicalLineFromModel,
  type EditorLineUniformModel,
} from "./text/editorLayoutMetrics.js";
import { focusCaretOffsetFromSelection } from "./text/focusCaretOffset.js";
import {
  textareaCaretLineTopFromContentTop,
  textareaLogicalLineWrapRowCounts,
} from "./text/textareaCaretGeometry.js";
import { logicalLineRangeAtOffset } from "./text/logicalLineRange.js";
import { countLogicalLines, lineIndex0AtOffset, lineMetricsAtOffset, offsetAtLineIndex0 } from "./text/lineMetrics.js";
import { prefersCoarsePointer } from "./text/prefersCoarsePointer.js";

/** Safe localStorage helpers — happy-dom and some sandboxed environments stub the object without real methods. */
function safeLocalStorageGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeLocalStorageSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeLocalStorageRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Position a fixed context-menu so it stays fully within the viewport.
 *
 * Strategy:
 * - Horizontal: open to the right of `anchorX`; if that clips, shift left or mirror.
 * - Vertical:   open below `anchorY`;  if that clips, flip above.
 *
 * The menu must already be in the DOM and have `hidden = false` so offsetWidth/Height
 * are non-zero. Set `visibility: hidden` before calling; restore it afterward.
 */
function anchorContextMenu(menu: HTMLElement, anchorX: number, anchorY: number): void {
  const w = menu.offsetWidth;
  const h = menu.offsetHeight;
  const vpW = window.innerWidth;
  const vpH = window.innerHeight;

  // Horizontal: align left edge to click, shift left if it would clip the right edge.
  let x = Math.max(0, anchorX);
  if (x + w > vpW) x = Math.max(0, vpW - w);

  // Vertical: open below click if it fits; otherwise open above.
  let y = anchorY;
  if (y + h > vpH) {
    const yAbove = anchorY - h;
    y = yAbove >= 0 ? yAbove : Math.max(0, vpH - h);
  }

  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
}

/** Format an ISO date string as "HH:MM" in local time for the datetime gutter. */
function formatGutterTime(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

/** Return the "HH:MM" timestamp of the newest mark overlapping [lineStart, lineEnd). */
function gutterTimestampForLine(marks: MarkEntry[], lineStart: number, lineEnd: number): string {
  let best: MarkEntry | null = null;
  for (const m of marks) {
    if (m.end <= lineStart || m.start > lineEnd) continue;
    if (!best) { best = m; continue; }
    const t = m.endDate ?? m.startDate;
    const bt = best.endDate ?? best.startDate;
    if (t > bt) best = m;
  }
  return best ? formatGutterTime(best.endDate ?? best.startDate) : "";
}

// ── Context-menu icon SVG path data (Material Design 24×24) ─────────────────
const P_UNDO = '<path d="M12.5 8c-2.65 0-5.05 1-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>';
const P_REDO = '<path d="M18.4 10.6C16.55 9 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22l2.36.78c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>';
const P_CUT = '<path d="M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 10c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3h-3z"/>';
const P_COPY = '<path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>';
const P_PASTE = '<path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z"/>';
const P_DELETE = '<path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>';
const P_SELECTALL = '<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5zm18-4H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 16H7V3h14v14z"/>';
const P_FIND = '<path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>';
const P_REPLACE = '<path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>';
const P_GOTO = '<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14-1.41-1.41L14.17 14H8v-2h6.17l-1.59-1.59L14 9l4 4-4 4z"/>';
const P_SPARKLE = '<path d="M12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61z"/>';
const P_EYE = '<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>';
const P_EYE_OFF = '<path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>';
const P_CLOCK = '<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>';
const P_NUMBERS = '<path d="M4 15h16v-2H4v2zm0 4h16v-2H4v2zm0-8h16V9H4v2zm0-6v2h16V5H4z"/>';
const P_REMOVE_MARK = '<path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>';
const P_SPELLCHECK = '<path d="M12.45 16h2.09L9.43 3H7.57L2.46 16h2.09l1.12-3h5.64l1.14 3zm-6.02-5 2.09-5.56L10.61 11H6.43zm15.16.59-8.09 8.09L9.83 16l-1.41 1.41 5.09 5.09L23 13l-1.41-1.41z"/>';

/** Wrap a Material-Design path string in a 16×16 SVG icon. */
function ctxIco(path: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">${path}</svg>`;
}

/**
 * Notepad++-style WEB shell: tabs, buffer lifecycle, EOL/encoding, find/goto,
 * clipboard (PROJECT_SOURCE_CODE_DESCRIPTION §5–9, §16.1–16.5).
 */
export class AppShell {
  readonly dispatcher = new CommandDispatcher();
  readonly documents = new DocumentManager();
  private menuBar: MenuBar | null = null;
  private tabBar: TabBar | null = null;
  private findResultsPane: FindResultsPane | null = null;
  private folderWorkspacePane: FolderWorkspacePane | null = null;
  private workspaceSash: HTMLDivElement | null = null;
  private previewSash: HTMLDivElement | null = null;
  private gitTerminalPanel: GitTerminalPanel | null = null;
  private readonly gitTerminalHost: HTMLElement;
  /** Chord string → command id (from defaults + `shortcutOverrides` in web prefs). */
  private keyboardChordMap: Record<string, string> = {};
  private readonly shell: HTMLElement;
  private readonly status: HTMLElement;
  private readonly editor: HTMLTextAreaElement;
  private readonly editorRow: HTMLDivElement;
  private readonly editorHost: HTMLDivElement;
  private readonly multiviewRoot: HTMLDivElement;
  private readonly multiviewChrome: HTMLDivElement;
  private readonly multiviewBodyRow: HTMLDivElement;
  private readonly multiviewPrimaryCol: HTMLDivElement;
  /** Shown only in multiview; labels the active tab in the first column. */
  private readonly multiviewPrimaryHeader: HTMLDivElement;
  private readonly multiviewExtras: readonly MultiviewExtraSlot[];
  private readonly multiviewPaneCountSelect: HTMLSelectElement;
  private multiviewPaneCount = 2;
  /** Per extra column (length {@link MULTIVIEW_MAX_EXTRA}); first `paneCount - 1` slots are used. */
  private readonly multiviewExtraDocIds: (string | null)[];
  /** Extra pane index (0-based) that receives the next Alt+click tab assignment. */
  private multiviewAltClickTargetSlot = 0;
  private multiviewEnabled = false;
  private multiviewSashCleanup: (() => void) | null = null;
  /** Restored when leaving multiview (preview / source toggles). */
  private multiviewPreviewFlagsSnapshot: {
    mdMarkdownShowSource: boolean;
    texShowSource: boolean;
    maxShowSource: boolean;
    htmlShowSource: boolean;
    oeisShowSource: boolean;
    csvShowSource: boolean;
  } | null = null;
  private readonly multiviewSingleBtn: HTMLButtonElement;
  private readonly lineNumberGutter: HTMLDivElement;
  private readonly lineNumberPre: HTMLPreElement;
  private readonly highlightPre: HTMLPreElement;
  private readonly highlightCode: HTMLElement;
  private activeLineDiv: HTMLDivElement | null = null;
  private readonly markupOverlayPre: HTMLPreElement;
  private readonly markupOverlayCode: HTMLElement;
  /** Per-document mark entries, keyed by TabDocument.id. Lazy-loaded from localStorage. */
  private readonly markupLayerData: Map<string, MarkEntry[]> = new Map();
  /** Whether the markup overlay is shown on the editor. Default true. */
  private markupLayerVisible = true;
  /** ISO timestamp of when the current editing session started, per doc id. */
  private readonly markupEditSessionStarts: Map<string, string> = new Map();
  private readonly markupContextCluster: HTMLDivElement;
  private readonly markupToggleBtn: HTMLButtonElement;
  private readonly gutterModeToggleBtn: HTMLButtonElement;
  /** "lines" = normal line numbers, "datetime" = timestamps from markup marks. */
  private gutterMode: "lines" | "datetime" = "lines";
  /** Bumped whenever marks change; invalidates the datetime gutter cache key. */
  private markupLayerEpoch = 0;
  private readonly editorContextMenu: HTMLDivElement;
  private readonly editorContextSpellSuggestSep: HTMLDivElement;
  private readonly editorContextSpellSuggestCluster: HTMLDivElement;
  private readonly editorContextTextCluster: HTMLDivElement;
  private readonly editorContextSpellcheckToggleBtn: HTMLButtonElement;
  private readonly editorContextShareSep: HTMLDivElement;
  private readonly editorContextShareBlock: HTMLDivElement;
  private readonly editorShareRecipientInput: HTMLInputElement;
  private readonly editorContextBeautifyGeminiBtn: HTMLButtonElement;
  private readonly maxRenderedContextMenu: HTMLDivElement;
  private readonly onBinaryPreviewContextMenu = (e: MouseEvent): void => {
    if (this.documents.active?.viewMode !== "video") return;
    e.preventDefault();
    this.showEditorContextMenu(e.clientX, e.clientY, "nulla");
  };
  private readonly onMaxPreviewContextMenu = (e: MouseEvent): void => {
    const d = this.documents.active;
    if (!d || d.viewMode !== "text" || d.language !== "max" || this.maxShowSource) return;
    e.preventDefault();
    this.showMaxRenderedContextMenu(e.clientX, e.clientY);
  };

  private readonly onOeisPreviewBodyClick = (e: MouseEvent): void => {
    const d = this.documents.active;
    if (!d || d.viewMode !== "text" || d.language !== "oeis") return;
    const t = e.target;
    if (!(t instanceof Element)) return;

    const link = t.closest("a");
    if (link instanceof HTMLAnchorElement) {
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (link.classList.contains("npp-oeis-external-browser")) return;
      const href = link.getAttribute("href");
      if (
        href &&
        !href.trim().toLowerCase().startsWith("javascript:") &&
        !link.hasAttribute("download")
      ) {
        try {
          const abs = new URL(href, window.location.href).href;
          const proto = new URL(abs).protocol;
          if (proto === "http:" || proto === "https:") {
            if (previewHyperlinkOpensHtmlInExternalBrowser(abs)) {
              e.preventDefault();
              window.open(abs, "_blank", "noopener,noreferrer");
              return;
            }
            e.preventDefault();
            void this.openPreviewHyperlinkInNewTab(abs);
            return;
          }
        } catch {
          /* ignore invalid href */
        }
      }
    }

    const loadMore = t.closest("button.npp-oeis-search-load-more");
    if (loadMore instanceof HTMLButtonElement) {
      e.preventDefault();
      void this.appendOeisTermsSearchHitsPage(loadMore);
      return;
    }
    const btn = t.closest("button.npp-oeis-search-hit");
    if (!(btn instanceof HTMLButtonElement)) return;
    const id = btn.dataset.oeisId?.trim();
    if (!id) return;
    e.preventDefault();
    this.applyOeisSequenceIdAndRefresh(id);
  };

  /** True when the tab’s path or title is a `.max` file (same rule as language inference on open). */
  private tabIsMaxSourceFile(d: TabDocument): boolean {
    return inferLanguageModeFromFilename(d.filePath ?? d.title) === "max";
  }

  /** `.max` buffer in Max text mode — toolbar camera/mic can write hex64 media links here. */
  private docIsMaxHexRecordTarget(d: TabDocument | null | undefined): boolean {
    return !!(
      d &&
      d.viewMode === "text" &&
      d.language === "max"
    );
  }

  /** Close the Max Insert `<details>` when the user clicks outside it. */
  private readonly onDocumentPointerDownCloseMaxInsertMenu = (ev: PointerEvent): void => {
    if (!this.maxAttachWrap.open) return;
    const t = ev.target;
    if (!(t instanceof Node)) return;
    if (this.maxAttachWrap.contains(t)) return;
    this.maxAttachWrap.open = false;
  };

  private readonly mdPreview: HTMLDivElement;
  private readonly mdPreviewBody: HTMLDivElement;
  private readonly mdViewToggle: HTMLButtonElement;
  private readonly mdBackToPreviousBtn: HTMLButtonElement;
  private readonly texPreview: HTMLDivElement;
  private readonly texPreviewBody: HTMLDivElement;
  private readonly texViewToggle: HTMLButtonElement;
  private readonly maxPreview: HTMLDivElement;
  private readonly maxPreviewBody: HTMLDivElement;
  private readonly maxViewToggle: HTMLButtonElement;
  /** `.max` tabs + source visible: Insert-from-file menu over the source column. */
  private readonly maxAttachWrap: HTMLDetailsElement;
  private readonly htmlPreview: HTMLDivElement;
  private readonly htmlPreviewBody: HTMLIFrameElement;
  private readonly htmlViewToggle: HTMLButtonElement;
  private readonly oeisPreview: HTMLDivElement;
  private readonly oeisPreviewBody: HTMLDivElement;
  private readonly oeisSeqIdInput: HTMLInputElement;
  private readonly oeisTermsSearchInput: HTMLInputElement;
  /** Debounced persist of “Search sequence” into NullaPad localStorage. */
  private oeisTermsSearchPersistTimer: ReturnType<typeof setTimeout> | null = null;
  /** In-app OEIS terms pick list: accumulated rows and query for “Load more” pagination. */
  private oeisTermsSearchHits: OeisJsonEntry[] = [];
  private oeisTermsSearchHitsQuery = "";
  /** Next OEIS `start` offset (sum of raw row counts from prior pages, not deduped list length). */
  private oeisTermsSearchNextStart = 0;
  private oeisTermsSearchLoadMoreSeq = 0;
  private readonly oeisViewToggle: HTMLButtonElement;
  private readonly csvPreview: HTMLDivElement;
  private readonly csvTableHost: HTMLDivElement;
  private readonly csvOpsHost: HTMLDivElement;
  /** Excel tabs: pick worksheet shown in the grid. */
  private readonly excelSheetSelect: HTMLSelectElement;
  private readonly csvViewToggle: HTMLButtonElement;
  private readonly binaryPreview: HTMLDivElement;
  private readonly binaryPreviewImg: HTMLImageElement;
  private readonly binaryPreviewPdf: HTMLIFrameElement;
  private readonly binaryPreviewVideo: HTMLVideoElement;
  private readonly nullaSectionsContainer: HTMLDivElement;
  /** In-editor “document” card when a PDF/image tab was restored as a placeholder (no blob URL). */
  private readonly sessionRestorePanel: HTMLDivElement;
  private readonly sessionRestoreFileTitle: HTMLDivElement;
  private readonly sessionRestoreReloadBtn: HTMLButtonElement;
  private nullaSectionEls: HTMLElement[] = [];
  private nullaSectionObjectUrls: (string | null)[] = [];
  /** Object URLs for optional embedded mic-track playback (aligned with {@link nullaSectionEls}). */
  private nullaSectionVoiceObjectUrls: (string | null)[] = [];
  /** Parallel mic recorder during Nulla video capture (same timeline as video). */
  private nullaAudioRecorder: MediaRecorder | null = null;
  private nullaAudioChunks: Blob[] = [];
  /** Nulla toolbar mic track: hex64 on one section (video `meta.voice` or standalone `audio`); index is {@link parseSections} order. */
  private nullaMicTrackRecorder: MediaRecorder | null = null;
  private nullaMicTrackChunks: Blob[] = [];
  private nullaMicTrackStream: MediaStream | null = null;
  private nullaMicTrackSectionIndex = -1;
  private nullaMicTrackStartTime = 0;
  private nullaMicTrackDiscard = false;
  private nullaMicTrackTargetTab: TabDocument | null = null;
  private readonly diffEditorHost: HTMLDivElement;
  private readonly diffEditorInner: HTMLDivElement;
  private diffViewDispose: (() => void) | null = null;
  /** Tab indices chosen from the tab context menu for Compare (left / right). */
  private diffPickLeftIndex: number | null = null;
  private diffPickRightIndex: number | null = null;
  private readonly syncHighlightScroll: () => void;
  private readonly onEditorScroll: () => void;
  private readonly onEditorContextPointerDown: (e: PointerEvent) => void;
  private readonly onEditorContextKeyDown: (e: KeyboardEvent) => void;
  private mdPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private texPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private maxPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private htmlPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private oeisPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  private csvPreviewTimer: ReturnType<typeof setTimeout> | null = null;
  /** Keeps syntax overlay geometry in sync when the editor row resizes (split panes, toolbar wrap, …). */
  private editorRowResizeObserver: ResizeObserver | null = null;
  private surfaceRefreshScheduled = false;
  private showLineNumbers = true;
  /** When language is Markdown: false = rendered preview only; true = show source editor. */
  private mdMarkdownShowSource = false;
  /** Scroll position before last markdown TOC jump; used by "Back" in preview toolbar. */
  private mdPreviewPreviousScrollTop: number | null = null;
  /** When language is TeX / LaTeX: false = KaTeX preview only; true = show source editor. */
  private texShowSource = false;
  /** When language is Max: false = math + calc preview only; true = show source editor. */
  private maxShowSource = false;
  /** When language is HTML: false = rendered preview only; true = show source editor. */
  private htmlShowSource = false;
  /** When language is OEIS: false = fetched preview only; true = show source editor. */
  private oeisShowSource = false;
  /** Increments on each OEIS fetch so stale responses are ignored. */
  private oeisFetchSeq = 0;
  /** When language is CSV: false = table grid only; true = show textarea (split with grid). */
  private csvShowSource = false;
  private csvGridApi: CsvGridEditorApi | null = null;
  private debounceCheckpoint: ReturnType<typeof setTimeout> | null = null;
  /** Temporary high-priority status suffix (e.g. git operation progress). */
  private stickyStatusExtra: string | null = null;
  private stickyStatusTimer: ReturnType<typeof setTimeout> | null = null;
  private zoomPercent = 100;
  private editorBaseFontPx = 14;
  /** Bumped when buffer content changes; invalidates cached total line count for the gutter. */
  private lineCountEpoch = 0;
  private cachedTotalLogicalLines: { epoch: number; lines: number } | null = null;
  /** Bust cache when logical line count or wrap layout inputs change. */
  private gutterContentLayoutKey = "";
  private readonly gutterLayoutKeyPerMultiviewSlot: string[] = Array.from({ length: MULTIVIEW_MAX_EXTRA }, () => "");
  private scrollCoalesceRaf = 0;
  /** `select` does not fire for many collapsed-caret moves; `selectionchange` keeps overlay/status in sync. */
  private selectionCoalesceRaf = 0;
  /**
   * Last `selectionStart`/`selectionEnd` seen by {@link onDocumentSelectionChange}.
   * Used to distinguish real editor-caret moves from `selectionchange` events fired because
   * the user clicked in the (non-focusable) preview pane and the browser cursor moved there —
   * in that case the textarea's own `selectionStart`/`selectionEnd` are unchanged, so we skip
   * the forward-sync RAF entirely (which would otherwise read a stale caret and scroll to line 0).
   */
  private lastEditorSelectionStart = -1;
  private lastEditorSelectionEnd = -1;
  /**
   * Set to `true` by {@link onPreviewClick} so the very next {@link syncPreviewToEditorCaret} call
   * (triggered by the `selectionchange` that `setSelectionRange` emits) is skipped.
   * This prevents the forward sync from re-scrolling the preview to the top of the clicked element
   * right after the user performed a reverse (preview → editor) sync.
   */
  private suppressNextPreviewSync = false;
  /** While editor is focused, keep highlight scroll hard-locked each frame. */
  private editorFocusSyncRaf = 0;
  private lastFocusSyncScrollTop = Number.NaN;
  private lastFocusSyncScrollLeft = Number.NaN;
  private tabSessionPersistTimer: ReturnType<typeof setTimeout> | null = null;
  private voiceWorker: Worker | null = null;
  private voiceModelReady = false;
  private voiceModelLoading = false;
  private voiceRecording = false;
  private voiceBtn: HTMLButtonElement | null = null;
  /** Toolbar: Nulla mic-track control (hex64 on last clip or standalone audio; replaces that block). */
  private nullaMicTrackToolbarBtn: HTMLButtonElement | null = null;
  private voiceStream: MediaStream | null = null;
  private voiceAudioCtx: AudioContext | null = null;
  private voiceChunkBuffer: Float32Array[] = [];
  private voiceInsertionPoint = 0;
  private voiceTextAfterCursor = "";
  private voiceTotalInserted = 0;
  private voiceChunkInterval: ReturnType<typeof setInterval> | null = null;
  private voiceSourceNode: MediaStreamAudioSourceNode | null = null;
  private voiceProcessorNode: ScriptProcessorNode | null = null;
  private nullaRecorder: MediaRecorder | null = null;
  /** True while toolbar camera is recording into the active `.max` buffer as hex links (not Nulla). */
  private maxHexRecordingActive = false;
  /** Tab that started a Max hex camera capture (must still be active to commit). */
  private maxHexRecordTargetTab: TabDocument | null = null;
  /** Mic-only hex capture on a `.max` tab (separate from Nulla mic-track recorder). */
  private maxHexMicRecorder: MediaRecorder | null = null;
  private maxHexMicChunks: Blob[] = [];
  private maxHexMicStream: MediaStream | null = null;
  /** FIFO queue so overlapping async Nulla finalizes cannot clobber each other's parse/write. */
  private nullaContentWriteChain: Promise<void> = Promise.resolve();
  /** Instance id in the title-bar (editable hex + copy / show / Sync). */
  private readonly nullapadSecretInput: HTMLInputElement;
  private readonly nullapadSecretSyncBtn: HTMLButtonElement;
  /** Re-persist the secret from the field and/or nudge share inbox (see {@link periodicNullapadInstanceSecretAutosync}). */
  private nullapadInstanceSecretAutosyncTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly NULLAPAD_INSTANCE_SECRET_AUTOSYNC_MS = 10_000;
  /** Polls GET `/inbox` on {@link NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS} when a share endpoint is configured. */
  private shareInboxPollTimer: ReturnType<typeof setInterval> | null = null;
  private shareInboxPollInFlight = false;
  /** When another poll is requested while one is in flight, run one more pass after the current finishes. */
  private shareInboxPollReplayPending = false;
  /** Polls `FileSystemFileHandle` backing files for external edits (shared drive / sync). */
  private externalFilePollTimer: ReturnType<typeof setInterval> | null = null;
  private externalFilePollInFlight = false;
  private static readonly EXTERNAL_FILE_POLL_MS = 4000;
  /** Abort tab-indent / Alt-column listeners on {@link destroy}. */
  private readonly textAreaInteractionAbort = new AbortController();
  /** Release listeners added during an Alt+column drag. */
  private columnDragCleanup: (() => void) | null = null;
  /** Rectangular selection overlay active during an Alt+column drag. */
  private columnDragRectOverlay: RectSelectOverlay | null = null;
  /** Persistent rectangular selection that stays visible after an Alt+drag until dismissed. */
  private activeRectSel: {
    ta: HTMLTextAreaElement;
    overlay: RectSelectOverlay;
    anchor: number;
    focus: number;
  } | null = null;
  /** Set during programmatic rect edits so onEditorInput does not dismiss the active rect sel. */
  private rectEditInProgress = false;
  /** SHA-256 hex of last inbox payload the user accepted, declined, or opened; cleared when inbox is empty or endpoint cleared. */
  private shareInboxLastHandledFingerprint: string | null = null;
  /** Throttle share-inbox heartbeat lines on the status bar (ms since epoch). */
  private shareInboxStatusLastAt = 0;
  /** Incoming share strip above the status bar (never use `confirm()` after async fetch — browsers may auto-decline and we would DELETE the slot). */
  private readonly shareInboxOfferBar: HTMLDivElement;
  private readonly shareInboxOfferText: HTMLSpanElement;
  private readonly shareInboxAcceptBtn: HTMLButtonElement;
  private shareInboxOfferState: { msg: NullapadShareInboxSimpleMessage; fp: string } | null = null;
  /** Dedup concurrent multipart GET /inbox → segment downloads for the same offer. */
  private shareInboxMultipartInflightKey: string | null = null;
  /** After one successful download+stage (or present), skip re-fetching segments while the server slot still returns multipart. */
  private shareInboxMultipartResolvedKey: string | null = null;
  private shareInboxAcceptEnableTimer: ReturnType<typeof setTimeout> | null = null;
  /** Fingerprint last written to {@link writeStagedShare} for the current offer (skip duplicate writes). */
  private shareInboxStagedFp: string | null = null;
  /** User preference: browser spellcheck in editors (see {@link loadWebPrefs} `nativeSpellcheckEnabled`). */
  private nativeSpellcheckEnabled = false;
  /** Central user dictionary (exceptions) applied across prose spell checks and suggestions. */
  private spellcheckExceptions: string[] = [];
  private editorContextTargetTextarea: HTMLTextAreaElement | null = null;
  private editorContextMisspelledRange: { start: number; end: number; word: string } | null = null;
  private nullaStream: MediaStream | null = null;
  private nullaChunks: Blob[] = [];
  private nullaStartTime = 0;
  private readonly onPageHidePersistSession = (): void => {
    this.persistTabSessionToStorage();
  };
  /** Refresh / close tab: `beforeunload` is more reliable than `pagehide` alone for writing localStorage. */
  private readonly onBeforeUnloadPersistSession = (): void => {
    this.persistTabSessionToStorage();
  };
  private readonly onVisibilityPersistSession = (): void => {
    if (document.visibilityState === "hidden") {
      this.persistTabSessionToStorage();
      this.cancelShareInboxAcceptEnableTimer();
      this.shareInboxAcceptBtn.disabled = true;
    } else {
      void this.tryPresentStagedShareIfNoBar();
      if (this.shareInboxOfferState && !this.shareInboxOfferBar.hidden) {
        this.scheduleShareInboxAcceptEnable(this.shareInboxOfferState.fp);
      }
      void this.pollShareInbox();
      void this.pollExternalFileChanges();
    }
  };
  private readonly onDocumentSelectionChange = (): void => {
    if (document.activeElement !== this.editor) return;
    // Guard: the `selectionchange` event fires for ANY selection change in the document, including
    // when the user clicks in the (non-focusable) preview pane.  In that case the textarea's own
    // selectionStart/selectionEnd are still unchanged.  Reacting to those events would queue a
    // sync RAF that reads the stale caret (e.g. 0) and scrolls the preview to line 0.
    const ss = this.editor.selectionStart;
    const se = this.editor.selectionEnd;
    const selectionActuallyChanged = ss !== this.lastEditorSelectionStart || se !== this.lastEditorSelectionEnd;
    this.lastEditorSelectionStart = ss;
    this.lastEditorSelectionEnd = se;
    if (!selectionActuallyChanged) return;
    // Keep the syntax layer aligned whenever the selection moves (e.g. Select All at EOF);
    // avoids a frame where the textarea selection scrolls but the highlight pre does not.
    this.syncHighlightScroll();
    this.armFocusedEditorSyncLoop();
    this.updateSelectionVisualMode();
    if (this.selectionCoalesceRaf !== 0) return;
    this.selectionCoalesceRaf = requestAnimationFrame(() => {
      this.selectionCoalesceRaf = 0;
      this.updateStatusLine();
      this.syncPreviewToEditorCaret();
    });
  };
  private readonly onEditorWheel = (): void => {
    this.armFocusedEditorSyncLoop();
  };
  private readonly onEditorFocus = (): void => {
    this.armFocusedEditorSyncLoop();
    this.updateSelectionVisualMode();
  };
  private readonly onEditorBlur = (): void => {
    if (this.editorFocusSyncRaf) {
      cancelAnimationFrame(this.editorFocusSyncRaf);
      this.editorFocusSyncRaf = 0;
    }
    this.lastFocusSyncScrollTop = Number.NaN;
    this.lastFocusSyncScrollLeft = Number.NaN;
    this.updateSelectionVisualMode();
  };

  constructor(root: HTMLElement) {
    root.classList.add("npp-shell");
    root.innerHTML = "";
    tryRestoreTabSessionInto(this.documents);
    getOrCreateNullapadInstanceSecret();

    const title = document.createElement("header");
    title.className = "npp-titlebar";
    title.setAttribute("aria-label", "Tab colors and merge tools");

    const titleCaption = document.createElement("span");
    titleCaption.className = "npp-titlebar-caption";
    titleCaption.textContent = "NullaPad";

    const menubarHost = document.createElement("div");
    menubarHost.className = "npp-menubar-mount";

    const maxModeBtn = document.createElement("button");
    maxModeBtn.type = "button";
    maxModeBtn.className = "npp-brand-max-mode-btn";
    maxModeBtn.textContent = "Max";
    maxModeBtn.title =
      "Switch this tab to Max mode. Live KaTeX for $$…$$ and \\(...\\) math, safe @@…@@ calculation blocks (arithmetic, functions, sums, integrals, repeats), and normal text between formulas. Use “Edit source” in the preview bar for the raw buffer.";
    maxModeBtn.setAttribute(
      "aria-label",
      "Switch the active tab to Max mode: math preview, @@ calculations, Edit source for raw text.",
    );
    maxModeBtn.addEventListener("click", () => this.applyMaxModeToActiveTab());

    const brandMenubarRow = document.createElement("div");
    brandMenubarRow.className = "npp-brand-menubar-row";
    brandMenubarRow.append(titleCaption, maxModeBtn, menubarHost);
    const toolbarCluster = document.createElement("div");
    toolbarCluster.className = "npp-titlebar-toolbar-cluster";
    const toolbarHost = document.createElement("div");
    toolbarHost.className = "npp-toolbar npp-toolbar--in-titlebar";
    const nullaRecordMicGroup = document.createElement("div");
    nullaRecordMicGroup.className = "npp-toolbar-nulla-record-mic";

    const secretWrap = document.createElement("div");
    secretWrap.className = "npp-instance-secret";
    secretWrap.title =
      "4–48 hex characters — this browser’s NullaPad id (localStorage). Auto-saved every 10s when valid; empty field is restored from storage after refresh. Sync applies immediately; copy copies the field.";
    const secretInput = document.createElement("input");
    secretInput.type = "password";
    secretInput.className = "npp-instance-secret-input";
    secretInput.maxLength = 48;
    secretInput.autocomplete = "off";
    secretInput.spellcheck = false;
    secretInput.setAttribute("aria-label", "NullaPad instance secret");
    secretInput.value = getOrCreateNullapadInstanceSecret();
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "npp-instance-secret-toggle";
    copyBtn.setAttribute("aria-label", "Copy instance secret");
    copyBtn.title = "Copy to clipboard";
    copyBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>';
    copyBtn.addEventListener("click", () => void this.copyNullapadInstanceSecretToClipboard());
    const eyeBtn = document.createElement("button");
    eyeBtn.type = "button";
    eyeBtn.className = "npp-instance-secret-toggle";
    eyeBtn.setAttribute("aria-label", "Show instance secret");
    eyeBtn.setAttribute("aria-pressed", "false");
    const eyeSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>';
    eyeBtn.innerHTML = eyeSvg;
    eyeBtn.addEventListener("click", () => {
      const toShow = secretInput.type === "password";
      secretInput.type = toShow ? "text" : "password";
      eyeBtn.setAttribute("aria-pressed", toShow ? "true" : "false");
      eyeBtn.setAttribute("aria-label", toShow ? "Hide instance secret" : "Show instance secret");
    });
    const syncBtn = document.createElement("button");
    syncBtn.type = "button";
    syncBtn.className = "npp-instance-secret-sync";
    syncBtn.textContent = "Sync";
    syncBtn.title =
      "Save secret and refresh share inbox now (GET /inbox). While the field is valid, it is also auto-saved every 10s.";
    syncBtn.setAttribute("aria-label", "Save instance secret and refresh share inbox");
    syncBtn.addEventListener("click", () => this.onNullapadInstanceSecretSyncClick());
    secretInput.addEventListener("input", () => this.updateNullapadSecretSyncUi());
    secretInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !syncBtn.disabled) {
        e.preventDefault();
        this.onNullapadInstanceSecretSyncClick();
      }
    });
    secretWrap.append(secretInput, copyBtn, eyeBtn, syncBtn);
    toolbarCluster.append(toolbarHost, secretWrap);
    this.nullapadSecretInput = secretInput;
    this.nullapadSecretSyncBtn = syncBtn;
    this.updateNullapadSecretSyncUi();
    this.startNullapadInstanceSecretAutosync();
    title.appendChild(toolbarCluster);
    const tabHost = document.createElement("div");
    tabHost.className = "npp-tabbar-host";

    const editorWrap = document.createElement("div");
    editorWrap.className = "npp-editor-wrap";

    const editorRow = document.createElement("div");
    editorRow.className = "npp-editor-row";

    const editorHost = document.createElement("div");
    editorHost.className = "npp-editor-host";

    const highlightPre = document.createElement("pre");
    highlightPre.className = "npp-editor-highlight";
    const highlightCode = document.createElement("code");
    highlightPre.appendChild(highlightCode);

    const markupPre = document.createElement("pre");
    markupPre.className = "npp-markup-pre";
    markupPre.setAttribute("aria-hidden", "true");
    markupPre.hidden = true;
    const markupCode = document.createElement("code");
    markupCode.className = "npp-markup-code";
    markupPre.appendChild(markupCode);

    const lineNumberGutter = document.createElement("div");
    lineNumberGutter.className = "npp-line-number-gutter";
    const lineNumberPre = document.createElement("pre");
    lineNumberPre.className = "npp-line-number-pre";
    lineNumberGutter.appendChild(lineNumberPre);

    this.editor = document.createElement("textarea");
    this.editor.className = "npp-editor";
    this.editor.placeholder = "Multi-tab editor — File / New, Open, Save…";

    const binaryPreview = document.createElement("div");
    binaryPreview.className = "npp-binary-preview";
    binaryPreview.setAttribute("role", "region");
    binaryPreview.setAttribute("aria-label", "File preview");
    const binaryPreviewImg = document.createElement("img");
    binaryPreviewImg.className = "npp-binary-preview-img";
    binaryPreviewImg.decoding = "async";
    binaryPreviewImg.alt = "";
    const binaryPreviewPdf = document.createElement("iframe");
    binaryPreviewPdf.className = "npp-binary-preview-iframe";
    binaryPreviewPdf.title = "PDF preview";
    const binaryPreviewVideo = document.createElement("video");
    binaryPreviewVideo.className = "npp-binary-preview-video";
    binaryPreviewVideo.controls = true;
    binaryPreviewVideo.playsInline = true;
    const nullaSectionsContainer = document.createElement("div");
    nullaSectionsContainer.className = "npp-nulla-sections";
    nullaSectionsContainer.hidden = true;
    binaryPreview.append(binaryPreviewImg, binaryPreviewPdf, binaryPreviewVideo, nullaSectionsContainer);

    const diffEditorHost = document.createElement("div");
    diffEditorHost.className = "npp-diff-editor-host";
    const diffEditorInner = document.createElement("div");
    diffEditorInner.className = "npp-diff-editor-inner";
    diffEditorHost.appendChild(diffEditorInner);

    const sessionRestorePanel = document.createElement("div");
    sessionRestorePanel.className = "npp-session-restore-panel";
    sessionRestorePanel.hidden = true;
    const sessionRestoreDoc = document.createElement("div");
    sessionRestoreDoc.className = "npp-session-restore-doc";
    const sessionRestoreFileTitle = document.createElement("div");
    sessionRestoreFileTitle.className = "npp-session-restore-filename";
    const sessionRestoreReloadBtn = document.createElement("button");
    sessionRestoreReloadBtn.type = "button";
    sessionRestoreReloadBtn.className = "npp-session-restore-reload-btn";
    sessionRestoreReloadBtn.textContent = "Reload";
    sessionRestoreReloadBtn.addEventListener("click", () => void this.onSessionRestorePlaceholderReload());
    sessionRestoreDoc.append(sessionRestoreFileTitle, sessionRestoreReloadBtn);
    sessionRestorePanel.appendChild(sessionRestoreDoc);

    const maxAttachWrap = document.createElement("details");
    maxAttachWrap.className = "npp-max-editor-browse-wrap npp-max-attach-details";
    maxAttachWrap.hidden = true;
    const maxAttachSummary = document.createElement("summary");
    maxAttachSummary.className = "npp-max-attach-summary npp-max-attach-trigger";
    maxAttachSummary.title =
      "Insert a file into the Max buffer at the caret (image, PDF, video, or HTML). Shown when the source editor is visible.";
    maxAttachSummary.setAttribute("aria-label", "Insert file into Max document");
    const maxAttachSummaryInner = document.createElement("span");
    maxAttachSummaryInner.className = "npp-max-attach-trigger-inner";
    const maxAttachSummaryText = document.createElement("span");
    maxAttachSummaryText.className = "npp-max-attach-trigger-text";
    maxAttachSummaryText.textContent = "Insert";
    const maxAttachSummaryChevron = document.createElement("span");
    maxAttachSummaryChevron.className = "npp-max-attach-trigger-chevron";
    maxAttachSummaryChevron.setAttribute("aria-hidden", "true");
    maxAttachSummaryChevron.textContent = "▼";
    maxAttachSummaryInner.append(maxAttachSummaryText, maxAttachSummaryChevron);
    maxAttachSummary.appendChild(maxAttachSummaryInner);

    const maxAttachMenu = document.createElement("div");
    maxAttachMenu.className = "npp-max-attach-menu";
    maxAttachMenu.setAttribute("role", "menu");
    const maxAttachMenuHeader = document.createElement("div");
    maxAttachMenuHeader.className = "npp-max-attach-menu-header";
    maxAttachMenuHeader.textContent = "Insert from file";
    maxAttachMenu.appendChild(maxAttachMenuHeader);

    const maxInsertImageInput = document.createElement("input");
    maxInsertImageInput.type = "file";
    maxInsertImageInput.className = "npp-max-editor-browse-file";
    maxInsertImageInput.accept = MAX_BROWSER_IMAGE_FILE_ACCEPT;
    maxInsertImageInput.addEventListener("change", () => {
      const f = maxInsertImageInput.files?.[0];
      if (f) this.insertMaxMarkdownImageFromRasterFile(f);
      maxInsertImageInput.value = "";
    });

    const maxInsertPdfInput = document.createElement("input");
    maxInsertPdfInput.type = "file";
    maxInsertPdfInput.className = "npp-max-editor-browse-file";
    maxInsertPdfInput.accept = MAX_BROWSER_PDF_FILE_ACCEPT;
    maxInsertPdfInput.addEventListener("change", () => {
      const f = maxInsertPdfInput.files?.[0];
      if (f) this.insertMaxMarkdownPdfFromFile(f);
      maxInsertPdfInput.value = "";
    });

    const maxInsertVideoInput = document.createElement("input");
    maxInsertVideoInput.type = "file";
    maxInsertVideoInput.className = "npp-max-editor-browse-file";
    maxInsertVideoInput.accept = MAX_BROWSER_VIDEO_FILE_ACCEPT;
    maxInsertVideoInput.addEventListener("change", () => {
      const f = maxInsertVideoInput.files?.[0];
      if (f) this.insertMaxVideoEmbedFromFile(f);
      maxInsertVideoInput.value = "";
    });

    const maxInsertHtmlInput = document.createElement("input");
    maxInsertHtmlInput.type = "file";
    maxInsertHtmlInput.className = "npp-max-editor-browse-file";
    maxInsertHtmlInput.accept = MAX_BROWSER_HTML_FILE_ACCEPT;
    maxInsertHtmlInput.addEventListener("change", () => {
      const f = maxInsertHtmlInput.files?.[0];
      if (f) this.insertMaxHtmlEmbedFromFile(f);
      maxInsertHtmlInput.value = "";
    });

    const mkInsertMenuItem = (
      primary: string,
      hint: string,
      title: string,
      input: HTMLInputElement,
    ): HTMLButtonElement => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "npp-max-attach-menu-item";
      b.title = title;
      b.setAttribute("role", "menuitem");
      const label = document.createElement("span");
      label.className = "npp-max-attach-menu-item-label";
      label.textContent = primary;
      const sub = document.createElement("span");
      sub.className = "npp-max-attach-menu-item-hint";
      sub.textContent = hint;
      b.append(label, sub);
      b.addEventListener("click", (ev) => {
        ev.preventDefault();
        maxAttachWrap.open = false;
        input.value = "";
        input.click();
      });
      return b;
    };

    maxAttachMenu.append(
      mkInsertMenuItem(
        "Image",
        "PNG, JPEG, WebP, SVG…",
        "Insert a raster image from disk as markdown (embedded data URL).",
        maxInsertImageInput,
      ),
      mkInsertMenuItem(
        "PDF",
        "One or many pages (ranges, all)",
        "Insert PDF page(s) as markdown (e.g. 1, 1-4, or all); each line uses ;npp-page=N on the same data URL.",
        maxInsertPdfInput,
      ),
      mkInsertMenuItem(
        "Video",
        "MP4, WebM…",
        "Embed a short video clip (data URL in the buffer). Large files are rejected.",
        maxInsertVideoInput,
      ),
      mkInsertMenuItem(
        "HTML",
        ".html file → hex link + preview",
        "Insert a sanitized HTML file as a hex link; preview renders it in a sandboxed iframe (scripts removed).",
        maxInsertHtmlInput,
      ),
    );

    maxAttachWrap.append(
      maxAttachSummary,
      maxAttachMenu,
      maxInsertImageInput,
      maxInsertPdfInput,
      maxInsertVideoInput,
      maxInsertHtmlInput,
    );

    editorHost.append(sessionRestorePanel, lineNumberGutter, highlightPre, markupPre, this.editor, maxAttachWrap);

    const multiviewRoot = document.createElement("div");
    multiviewRoot.className = "npp-multiview-root";
    const multiviewChrome = document.createElement("div");
    multiviewChrome.className = "npp-multiview-chrome";
    multiviewChrome.hidden = true;
    const multiviewChromeLeft = document.createElement("div");
    multiviewChromeLeft.className = "npp-multiview-chrome-left";
    const paneCountLabel = document.createElement("label");
    paneCountLabel.className = "npp-multiview-pane-label";
    paneCountLabel.textContent = "Panes ";
    const multiviewPaneCountSelect = document.createElement("select");
    multiviewPaneCountSelect.className = "npp-multiview-pane-count";
    multiviewPaneCountSelect.title = "Number of editor columns (active tab stays in the first)";
    for (let n = 2; n <= MULTIVIEW_MAX_PANES; n++) {
      const opt = document.createElement("option");
      opt.value = String(n);
      opt.textContent = String(n);
      multiviewPaneCountSelect.appendChild(opt);
    }
    multiviewPaneCountSelect.value = "2";
    multiviewPaneCountSelect.addEventListener("change", () => {
      const raw = Number.parseInt(multiviewPaneCountSelect.value, 10);
      const n = Number.isFinite(raw)
        ? Math.max(2, Math.min(MULTIVIEW_MAX_PANES, raw))
        : 2;
      multiviewPaneCountSelect.value = String(n);
      if (this.multiviewEnabled) this.setMultiviewPaneCount(n);
    });
    paneCountLabel.appendChild(multiviewPaneCountSelect);
    const multiviewChromeHint = document.createElement("span");
    multiviewChromeHint.className = "npp-multiview-chrome-hint";
    multiviewChromeHint.textContent =
      "Click a pane title, then Alt+click a tab to assign that column (plain text tabs only).";
    multiviewChromeLeft.append(paneCountLabel, multiviewChromeHint);

    const multiviewSingleBtn = document.createElement("button");
    multiviewSingleBtn.type = "button";
    multiviewSingleBtn.className = "npp-multiview-single-btn";
    multiviewSingleBtn.textContent = "Single view";
    multiviewSingleBtn.title = "Exit multiview — one editor, previews restored";
    multiviewSingleBtn.addEventListener("click", () => this.setMultiviewEnabled(false));
    multiviewChrome.append(multiviewChromeLeft, multiviewSingleBtn);

    const multiviewBodyRow = document.createElement("div");
    multiviewBodyRow.className = "npp-multiview-body";
    const multiviewPrimaryCol = document.createElement("div");
    multiviewPrimaryCol.className = "npp-multiview-primary-col";
    const multiviewPrimaryHeader = document.createElement("div");
    multiviewPrimaryHeader.className = "npp-multiview-slot-header npp-multiview-primary-pane-header";
    multiviewPrimaryHeader.hidden = true;
    multiviewPrimaryHeader.textContent = "—";
    multiviewPrimaryCol.append(multiviewPrimaryHeader, editorHost);
    multiviewBodyRow.appendChild(multiviewPrimaryCol);
    const multiviewExtras: MultiviewExtraSlot[] = [];
    for (let slot = 0; slot < MULTIVIEW_MAX_EXTRA; slot++) {
      const slotUi = createMultiviewExtraSlot(slot);
      slotUi.header.addEventListener("click", () => {
        this.multiviewAltClickTargetSlot = slot;
        this.updateMultiviewSlotHeaderStyles();
        this.syncMultiviewExtraSlotHeader(slot);
        const lab = slotUi.header.textContent;
        const where = lab === "—" ? "this empty column" : lab;
        this.updateStatusLine(`Multiview: Alt+click assigns to ${where}`);
      });
      multiviewBodyRow.append(slotUi.sash, slotUi.column);
      multiviewExtras.push(slotUi);
    }
    multiviewRoot.append(multiviewChrome, multiviewBodyRow);

    const editorContextMenu = document.createElement("div");
    editorContextMenu.className = "npp-editor-context";
    editorContextMenu.hidden = true;
    editorContextMenu.setAttribute("role", "menu");

    // ── Helpers ────────────────────────────────────────────────────────────
    const mkIconBtn = (path: string, title: string, commandId: string): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "npp-ctx-icon-btn";
      btn.setAttribute("role", "menuitem");
      btn.title = title;
      btn.setAttribute("aria-label", title);
      btn.innerHTML = ctxIco(path);
      btn.addEventListener("click", () => {
        void this.dispatcher.executeCommand(commandId);
        this.hideEditorContextMenu();
      });
      return btn;
    };
    const mkVSep = (): HTMLDivElement => {
      const v = document.createElement("div");
      v.className = "npp-ctx-icon-vsep";
      return v;
    };
    const mkHSep = (): HTMLDivElement => {
      const h = document.createElement("div");
      h.className = "npp-editor-context-sep";
      h.setAttribute("role", "separator");
      return h;
    };
    const mkIconRow = (...nodes: HTMLElement[]): HTMLDivElement => {
      const row = document.createElement("div");
      row.className = "npp-ctx-icon-row";
      for (const n of nodes) row.appendChild(n);
      return row;
    };

    // ── Spell suggestions (dynamic text items, stays text-based) ──────────
    const spellSuggestSep = document.createElement("div");
    spellSuggestSep.className = "npp-editor-context-sep";
    spellSuggestSep.setAttribute("role", "separator");
    spellSuggestSep.hidden = true;
    const spellSuggestCluster = document.createElement("div");
    spellSuggestCluster.className = "npp-editor-context-text-cluster";
    spellSuggestCluster.hidden = true;

    // ── Text-editing icon rows ─────────────────────────────────────────────
    const textCluster = document.createElement("div");
    textCluster.className = "npp-editor-context-text-cluster";

    const editHistoryRow = mkIconRow(
      mkIconBtn(P_UNDO, "Undo", CMD.EDIT_UNDO),
      mkIconBtn(P_REDO, "Redo", CMD.EDIT_REDO),
      mkVSep(),
      mkIconBtn(P_CUT, "Cut", CMD.EDIT_CUT),
      mkIconBtn(P_COPY, "Copy", CMD.EDIT_COPY),
      mkIconBtn(P_PASTE, "Paste", CMD.EDIT_PASTE),
      mkIconBtn(P_DELETE, "Delete", CMD.EDIT_DELETE),
    );

    const beautifyGeminiBtn = document.createElement("button");
    beautifyGeminiBtn.type = "button";
    beautifyGeminiBtn.className = "npp-ctx-icon-btn";
    beautifyGeminiBtn.setAttribute("role", "menuitem");
    beautifyGeminiBtn.title = "Beautify with Gemini…";
    beautifyGeminiBtn.setAttribute("aria-label", "Beautify with Gemini…");
    beautifyGeminiBtn.innerHTML = ctxIco(P_SPARKLE);
    beautifyGeminiBtn.addEventListener("click", () => {
      void this.dispatcher.executeCommand(CMD.TOOL_GEMINI_BEAUTIFY_SELECTION);
      this.hideEditorContextMenu();
    });

    const editSearchRow = mkIconRow(
      mkIconBtn(P_SELECTALL, "Select All", CMD.EDIT_SELECTALL),
      mkVSep(),
      mkIconBtn(P_FIND, "Find…", CMD.SEARCH_FIND),
      mkIconBtn(P_REPLACE, "Replace…", CMD.SEARCH_REPLACE),
      mkIconBtn(P_GOTO, "Go to line…", CMD.SEARCH_GOTOLINE),
      mkVSep(),
      beautifyGeminiBtn,
    );

    textCluster.append(editHistoryRow, editSearchRow);

    // ── Markup icon row ────────────────────────────────────────────────────
    const markupCluster = document.createElement("div");
    markupCluster.className = "npp-markup-cluster";

    const markupIconRow = mkIconRow();
    markupIconRow.className = "npp-ctx-icon-row npp-markup-icon-row";
    for (const preset of MARK_COLOR_PRESETS) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "npp-markup-color-btn";
      dot.style.setProperty("--mark-color", preset.value);
      dot.setAttribute("aria-label", `Mark selection: ${preset.label}`);
      dot.title = `Mark selection: ${preset.label}`;
      dot.addEventListener("pointerdown", (ev) => ev.stopPropagation());
      dot.addEventListener("click", () => {
        this.addMarkToSelection(preset.value);
        this.hideEditorContextMenu();
      });
      markupIconRow.appendChild(dot);
    }

    const markupRemoveBtn = document.createElement("button");
    markupRemoveBtn.type = "button";
    markupRemoveBtn.className = "npp-ctx-icon-btn";
    markupRemoveBtn.setAttribute("role", "menuitem");
    markupRemoveBtn.title = "Remove mark here";
    markupRemoveBtn.setAttribute("aria-label", "Remove mark here");
    markupRemoveBtn.innerHTML = ctxIco(P_REMOVE_MARK);
    markupRemoveBtn.addEventListener("click", () => {
      this.removeMarkAtCursor();
      this.hideEditorContextMenu();
    });

    const markupToggleBtn = document.createElement("button");
    markupToggleBtn.type = "button";
    markupToggleBtn.className = "npp-ctx-icon-btn";
    markupToggleBtn.setAttribute("role", "menuitem");
    markupToggleBtn.innerHTML = ctxIco(P_EYE_OFF);
    markupToggleBtn.title = "Hide markup";
    markupToggleBtn.setAttribute("aria-label", "Hide markup");
    markupToggleBtn.addEventListener("click", () => {
      this.toggleMarkupVisibility();
      this.hideEditorContextMenu();
    });

    const gutterModeToggleBtn = document.createElement("button");
    gutterModeToggleBtn.type = "button";
    gutterModeToggleBtn.className = "npp-ctx-icon-btn";
    gutterModeToggleBtn.setAttribute("role", "menuitem");
    gutterModeToggleBtn.innerHTML = ctxIco(P_CLOCK);
    gutterModeToggleBtn.title = "Show timestamps in gutter";
    gutterModeToggleBtn.setAttribute("aria-label", "Show timestamps in gutter");
    gutterModeToggleBtn.addEventListener("click", () => {
      this.toggleGutterMode();
      this.hideEditorContextMenu();
    });

    markupIconRow.append(mkVSep(), markupRemoveBtn, markupToggleBtn, gutterModeToggleBtn);
    markupCluster.append(mkHSep(), markupIconRow);

    // ── Spellcheck labeled toggle ──────────────────────────────────────────
    const spellcheckToggleBtn = document.createElement("button");
    spellcheckToggleBtn.type = "button";
    spellcheckToggleBtn.className = "npp-ctx-labeled-btn";
    spellcheckToggleBtn.setAttribute("role", "menuitem");
    spellcheckToggleBtn.setAttribute("aria-pressed", "false");
    spellcheckToggleBtn.innerHTML = `${ctxIco(P_SPELLCHECK)}<span>Spell check</span><span class="npp-ctx-labeled-check" aria-hidden="true">✓</span>`;
    spellcheckToggleBtn.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    spellcheckToggleBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.toggleNativeSpellcheckFromMenu();
      this.hideEditorContextMenu();
    });

    // ── Share block ────────────────────────────────────────────────────────
    const shareSep = document.createElement("div");
    shareSep.className = "npp-editor-context-sep";
    shareSep.setAttribute("role", "separator");
    const shareBlock = document.createElement("div");
    shareBlock.className = "npp-tab-context-share";
    const shareLab = document.createElement("span");
    shareLab.className = "npp-tab-context-share-label";
    shareLab.textContent = "Share with";
    const shareRow = document.createElement("div");
    shareRow.className = "npp-tab-context-share-row";
    const shareInp = document.createElement("input");
    shareInp.type = "password";
    shareInp.className = "npp-tab-context-share-secret";
    shareInp.autocomplete = "off";
    shareInp.spellcheck = false;
    shareInp.placeholder = "Recipient secret";
    shareInp.maxLength = 48;
    shareInp.setAttribute("aria-label", "Recipient NullaPad secret");
    shareInp.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    const shareEye = document.createElement("button");
    shareEye.type = "button";
    shareEye.className = "npp-tab-context-share-eye";
    shareEye.setAttribute("aria-label", "Show or hide recipient secret");
    shareEye.setAttribute("aria-pressed", "false");
    shareEye.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>';
    shareEye.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    shareEye.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const show = shareInp.type === "password";
      shareInp.type = show ? "text" : "password";
      shareEye.setAttribute("aria-pressed", show ? "true" : "false");
    });
    const shareSend = document.createElement("button");
    shareSend.type = "button";
    shareSend.className = "npp-tab-context-share-send";
    shareSend.textContent = "Send";
    shareSend.setAttribute("aria-label", "Send tab to recipient NullaPad");
    shareSend.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    shareInp.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        shareSend.click();
      }
    });
    shareSend.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const rid = shareInp.value.trim();
      if (!rid) return;
      void (async () => {
        try {
          await this.shareTabFromContextMenu(this.documents.activeIndex, rid);
          saveLastNullapadShareRecipientSecret(rid);
          shareInp.value = getLastNullapadShareRecipientSecret();
          shareInp.type = "password";
          shareEye.setAttribute("aria-pressed", "false");
          this.hideEditorContextMenu();
        } catch (err) {
          void alert((err as Error).message || String(err));
        }
      })();
    });
    shareRow.append(shareInp, shareEye, shareSend);
    shareBlock.append(shareLab, shareRow);
    editorContextMenu.append(
      spellSuggestSep,
      spellSuggestCluster,
      textCluster,
      markupCluster,
      mkHSep(),
      spellcheckToggleBtn,
      shareSep,
      shareBlock,
    );
    document.body.appendChild(editorContextMenu);

    const maxRenderedContextMenu = document.createElement("div");
    maxRenderedContextMenu.className = "npp-editor-context";
    maxRenderedContextMenu.hidden = true;
    maxRenderedContextMenu.setAttribute("role", "menu");
    const exportMaxMdBtn = document.createElement("button");
    exportMaxMdBtn.type = "button";
    exportMaxMdBtn.className = "npp-editor-context-item";
    exportMaxMdBtn.setAttribute("role", "menuitem");
    exportMaxMdBtn.textContent = "Export Max rendered Markdown…";
    exportMaxMdBtn.addEventListener("click", () => {
      void this.dispatcher.executeCommand(CMD.EXPORT_MAX_RENDERED_MD);
      this.hideMaxRenderedContextMenu();
    });
    const maxCtxSep = document.createElement("div");
    maxCtxSep.className = "npp-editor-context-sep";
    maxCtxSep.setAttribute("role", "separator");
    const exportPdfBtn = document.createElement("button");
    exportPdfBtn.type = "button";
    exportPdfBtn.className = "npp-editor-context-item";
    exportPdfBtn.setAttribute("role", "menuitem");
    exportPdfBtn.textContent = "Export to PDF…";
    exportPdfBtn.addEventListener("click", () => {
      void this.dispatcher.executeCommand(CMD.EXPORT_TO_PDF);
      this.hideMaxRenderedContextMenu();
    });
    maxRenderedContextMenu.append(exportMaxMdBtn, maxCtxSep, exportPdfBtn);
    document.body.appendChild(maxRenderedContextMenu);

    const mdPreview = document.createElement("div");
    mdPreview.className = "npp-md-preview";
    mdPreview.hidden = true;
    mdPreview.setAttribute("tabindex", "-1");
    mdPreview.setAttribute("role", "region");
    mdPreview.setAttribute("aria-label", "Markdown preview");

    const mdToolbar = document.createElement("div");
    mdToolbar.className = "npp-md-preview-toolbar";
    const mdViewToggle = document.createElement("button");
    mdViewToggle.type = "button";
    mdViewToggle.className = "npp-md-toolbar-btn";
    mdViewToggle.textContent = "Edit source";
    mdViewToggle.addEventListener("click", () => {
      this.mdMarkdownShowSource = !this.mdMarkdownShowSource;
      const _md = this.documents.active;
      if (_md) _md.showSource = this.mdMarkdownShowSource;
      this.applyMarkdownPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.mdMarkdownShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      }
    });
    const mdBackToPreviousBtn = document.createElement("button");
    mdBackToPreviousBtn.type = "button";
    mdBackToPreviousBtn.className = "npp-md-toolbar-btn";
    mdBackToPreviousBtn.textContent = "←";
    mdBackToPreviousBtn.disabled = true;
    mdBackToPreviousBtn.title = "Back to previous preview position";
    mdBackToPreviousBtn.addEventListener("click", () => this.onMarkdownPreviewBackClick());
    mdToolbar.append(mdBackToPreviousBtn, mdViewToggle);

    const mdPreviewBody = document.createElement("div");
    mdPreviewBody.className = "npp-md-preview-body";
    mdPreviewBody.spellcheck = false;
    mdPreviewBody.setAttribute("tabindex", "-1");
    mdPreviewBody.addEventListener("click", (e) => this.onMarkdownPreviewClick(e));
    mdPreview.append(mdToolbar, mdPreviewBody);

    const texPreview = document.createElement("div");
    texPreview.className = "npp-tex-preview";
    texPreview.hidden = true;
    texPreview.setAttribute("tabindex", "-1");
    texPreview.setAttribute("role", "region");
    texPreview.setAttribute("aria-label", "TeX math preview");

    const texToolbar = document.createElement("div");
    texToolbar.className = "npp-tex-preview-toolbar";
    const texViewToggle = document.createElement("button");
    texViewToggle.type = "button";
    texViewToggle.className = "npp-md-toolbar-btn";
    texViewToggle.textContent = "Edit source";
    texViewToggle.addEventListener("click", () => {
      this.texShowSource = !this.texShowSource;
      const _tex = this.documents.active;
      if (_tex) _tex.showSource = this.texShowSource;
      this.applyTexPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.texShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      }
    });
    texToolbar.appendChild(texViewToggle);

    const texPreviewBody = document.createElement("div");
    texPreviewBody.className = "npp-tex-preview-body";
    texPreviewBody.spellcheck = false;
    texPreviewBody.setAttribute("tabindex", "-1");
    texPreview.append(texToolbar, texPreviewBody);

    const maxPreview = document.createElement("div");
    maxPreview.className = "npp-max-preview";
    maxPreview.hidden = true;
    maxPreview.setAttribute("tabindex", "-1");
    maxPreview.setAttribute("role", "region");
    maxPreview.setAttribute("aria-label", "Max math and calculation preview");

    const maxToolbar = document.createElement("div");
    maxToolbar.className = "npp-max-preview-toolbar";
    const maxViewToggle = document.createElement("button");
    maxViewToggle.type = "button";
    maxViewToggle.className = "npp-md-toolbar-btn";
    maxViewToggle.textContent = "Edit source";
    maxViewToggle.addEventListener("click", () => {
      this.maxShowSource = !this.maxShowSource;
      const _max = this.documents.active;
      if (_max) _max.showSource = this.maxShowSource;
      this.applyMaxPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.maxShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      }
    });
    maxToolbar.appendChild(maxViewToggle);

    const maxPreviewBody = document.createElement("div");
    maxPreviewBody.className = "npp-max-preview-body";
    maxPreviewBody.spellcheck = false;
    maxPreviewBody.setAttribute("tabindex", "-1");
    maxPreview.append(maxToolbar, maxPreviewBody);

    const htmlPreview = document.createElement("div");
    htmlPreview.className = "npp-html-preview";
    htmlPreview.hidden = true;
    htmlPreview.setAttribute("tabindex", "-1");
    htmlPreview.setAttribute("role", "region");
    htmlPreview.setAttribute("aria-label", "HTML preview");

    const htmlToolbar = document.createElement("div");
    htmlToolbar.className = "npp-html-preview-toolbar";
    const htmlViewToggle = document.createElement("button");
    htmlViewToggle.type = "button";
    htmlViewToggle.className = "npp-md-toolbar-btn";
    htmlViewToggle.textContent = "Edit source";
    htmlViewToggle.addEventListener("click", () => {
      this.htmlShowSource = !this.htmlShowSource;
      const _html = this.documents.active;
      if (_html) _html.showSource = this.htmlShowSource;
      this.applyHtmlPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.htmlShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      }
    });
    const htmlMiniBrowserBtn = document.createElement("button");
    htmlMiniBrowserBtn.type = "button";
    htmlMiniBrowserBtn.className = "npp-md-toolbar-btn";
    htmlMiniBrowserBtn.textContent = "Mini browser";
    htmlMiniBrowserBtn.title =
      "Open this HTML in a separate popup window (native browser page — allow pop-ups if blocked)";
    htmlMiniBrowserBtn.addEventListener("click", () => this.popOutHtmlMiniBrowser());
    htmlToolbar.append(htmlViewToggle, htmlMiniBrowserBtn);

    const htmlPreviewBody = document.createElement("iframe");
    htmlPreviewBody.className = "npp-html-preview-body";
    htmlPreviewBody.spellcheck = false;
    htmlPreviewBody.title = "HTML preview (browser-like)";
    htmlPreviewBody.setAttribute(
      "sandbox",
      "allow-same-origin allow-popups-to-escape-sandbox allow-forms allow-modals allow-downloads",
    );
    htmlPreviewBody.referrerPolicy = "no-referrer";
    htmlPreview.append(htmlToolbar, htmlPreviewBody);

    const csvPreview = document.createElement("div");
    csvPreview.className = "npp-csv-preview";
    csvPreview.spellcheck = false;
    csvPreview.hidden = true;
    csvPreview.setAttribute("tabindex", "-1");
    csvPreview.setAttribute("role", "region");
    csvPreview.setAttribute("aria-label", "CSV table");

    const csvToolbar = document.createElement("div");
    csvToolbar.className = "npp-csv-preview-toolbar";
    const excelSheetSelect = document.createElement("select");
    excelSheetSelect.className = "npp-excel-sheet-select";
    excelSheetSelect.hidden = true;
    excelSheetSelect.setAttribute("aria-label", "Worksheet");
    excelSheetSelect.title = "Active worksheet (Excel)";
    excelSheetSelect.addEventListener("change", () => {
      if (!this.isSpreadsheetLanguage(this.documents.active?.language)) return;
      if (this.documents.active?.language !== "excel") return;
      const idx = parseInt(excelSheetSelect.value, 10);
      if (!Number.isFinite(idx)) return;
      const dm = this.documents;
      const next = setActiveSheetIndexInJson(this.editor.value, idx);
      this.editor.value = next;
      dm.syncFromEditor(next);
      dm.checkpointHistory(next);
      this.tabBar?.refresh();
      this.scheduleCsvGridRefresh();
      this.updateStatusLine();
      this.syncTextareaSpellcheckState();
    });
    const csvViewToggle = document.createElement("button");
    csvViewToggle.type = "button";
    csvViewToggle.className = "npp-md-toolbar-btn";
    csvViewToggle.textContent = "Edit as text";
    csvViewToggle.addEventListener("click", () => {
      this.csvShowSource = !this.csvShowSource;
      const _csv = this.documents.active;
      if (_csv) _csv.showSource = this.csvShowSource;
      this.applyCsvPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.csvShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      } else {
        this.scheduleCsvGridRefresh();
      }
    });
    const csvOpsHost = document.createElement("div");
    csvToolbar.append(excelSheetSelect, csvViewToggle, csvOpsHost);
    const csvTableHost = document.createElement("div");
    csvTableHost.className = "npp-csv-table-host";
    csvPreview.append(csvToolbar, csvTableHost);

    const oeisPreview = document.createElement("div");
    oeisPreview.className = "npp-oeis-preview";
    oeisPreview.hidden = true;
    oeisPreview.setAttribute("tabindex", "-1");
    oeisPreview.setAttribute("role", "region");
    oeisPreview.setAttribute("aria-label", "OEIS sequence preview");

    const oeisToolbar = document.createElement("div");
    oeisToolbar.className = "npp-oeis-preview-toolbar";
    const oeisSeqToolbar = document.createElement("div");
    oeisSeqToolbar.className = "npp-oeis-seq-toolbar";
    const oeisIdGroup = document.createElement("div");
    oeisIdGroup.className = "npp-oeis-seq-toolbar-group";
    const oeisSeqLabel = document.createElement("label");
    oeisSeqLabel.className = "npp-oeis-seq-label";
    oeisSeqLabel.htmlFor = "npp-oeis-seq-id";
    oeisSeqLabel.textContent = "A-number";
    const oeisSeqIdInput = document.createElement("input");
    oeisSeqIdInput.type = "text";
    oeisSeqIdInput.id = "npp-oeis-seq-id";
    oeisSeqIdInput.className = "npp-oeis-seq-id-input";
    oeisSeqIdInput.placeholder = "A000045";
    oeisSeqIdInput.spellcheck = false;
    oeisSeqIdInput.autocomplete = "off";
    oeisSeqIdInput.title = "OEIS A-number or https://oeis.org/A… URL. Press Enter or Load.";
    const oeisSeqLoadBtn = document.createElement("button");
    oeisSeqLoadBtn.type = "button";
    oeisSeqLoadBtn.className = "npp-md-toolbar-btn";
    oeisSeqLoadBtn.textContent = "Load";
    oeisSeqLoadBtn.title = "Load this sequence by A-number";
    const commitOeisIdFromToolbar = (): void => {
      this.commitOeisSequenceFromToolbar();
    };
    oeisSeqLoadBtn.addEventListener("click", commitOeisIdFromToolbar);
    oeisSeqIdInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitOeisIdFromToolbar();
      }
    });
    oeisIdGroup.append(oeisSeqLabel, oeisSeqIdInput, oeisSeqLoadBtn);

    const oeisTermsGroup = document.createElement("div");
    oeisTermsGroup.className = "npp-oeis-seq-toolbar-group npp-oeis-seq-toolbar-group--terms";
    const oeisTermsLabel = document.createElement("label");
    oeisTermsLabel.className = "npp-oeis-seq-label";
    oeisTermsLabel.htmlFor = "npp-oeis-terms-search";
    oeisTermsLabel.textContent = "Search sequence";
    const oeisTermsSearchInput = document.createElement("input");
    oeisTermsSearchInput.type = "text";
    oeisTermsSearchInput.id = "npp-oeis-terms-search";
    oeisTermsSearchInput.className = "npp-oeis-seq-id-input npp-oeis-terms-search-input";
    oeisTermsSearchInput.placeholder = "1, 2, 3, 5, 8…";
    oeisTermsSearchInput.spellcheck = false;
    oeisTermsSearchInput.autocomplete = "off";
    oeisTermsSearchInput.title =
      "At least three signed integers (comma or space separated). Inserts a terms line and searches OEIS. Press Enter or Search.";
    const oeisTermsSearchBtn = document.createElement("button");
    oeisTermsSearchBtn.type = "button";
    oeisTermsSearchBtn.className = "npp-md-toolbar-btn";
    oeisTermsSearchBtn.textContent = "Search";
    oeisTermsSearchBtn.title = "Search OEIS for sequences containing these terms";
    const commitOeisTermsFromToolbar = (): void => {
      this.commitOeisTermsSearchFromToolbar();
    };
    oeisTermsSearchBtn.addEventListener("click", commitOeisTermsFromToolbar);
    oeisTermsSearchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitOeisTermsFromToolbar();
      }
    });
    oeisTermsSearchInput.addEventListener("input", () => {
      if (this.oeisTermsSearchPersistTimer !== null) clearTimeout(this.oeisTermsSearchPersistTimer);
      this.oeisTermsSearchPersistTimer = setTimeout(() => {
        this.oeisTermsSearchPersistTimer = null;
        persistNullapadOeisLastTermsQuery(this.oeisTermsSearchInput.value.trim());
      }, 400);
    });
    oeisTermsSearchInput.addEventListener("blur", () => {
      if (this.oeisTermsSearchPersistTimer !== null) {
        clearTimeout(this.oeisTermsSearchPersistTimer);
        this.oeisTermsSearchPersistTimer = null;
      }
      persistNullapadOeisLastTermsQuery(this.oeisTermsSearchInput.value.trim());
    });
    oeisTermsGroup.append(oeisTermsLabel, oeisTermsSearchInput, oeisTermsSearchBtn);

    oeisSeqToolbar.append(oeisIdGroup, oeisTermsGroup);

    const oeisViewToggle = document.createElement("button");
    oeisViewToggle.type = "button";
    oeisViewToggle.className = "npp-md-toolbar-btn";
    oeisViewToggle.textContent = "Edit source";
    oeisViewToggle.addEventListener("click", () => {
      this.oeisShowSource = !this.oeisShowSource;
      const _oeis = this.documents.active;
      if (_oeis) _oeis.showSource = this.oeisShowSource;
      this.applyOeisPreviewLayoutClass();
      this.updateSyntaxHighlightLayer();
      if (this.oeisShowSource) {
        this.editor.focus();
        this.syncHighlightScroll();
      }
    });
    oeisToolbar.append(oeisSeqToolbar, oeisViewToggle);

    const oeisPreviewBody = document.createElement("div");
    oeisPreviewBody.className = "npp-oeis-preview-body";
    oeisPreviewBody.spellcheck = false;
    oeisPreviewBody.setAttribute("tabindex", "-1");
    oeisPreview.append(oeisToolbar, oeisPreviewBody);

    const previewSash = document.createElement("div");
    previewSash.className = "npp-preview-sash";
    previewSash.setAttribute("role", "separator");
    previewSash.setAttribute("aria-orientation", "vertical");
    previewSash.title = "Drag to resize editor/preview split";
    this.previewSash = previewSash;

    editorRow.append(
      multiviewRoot,
      previewSash,
      binaryPreview,
      diffEditorHost,
      mdPreview,
      texPreview,
      maxPreview,
      htmlPreview,
      csvPreview,
      oeisPreview,
    );
    editorWrap.appendChild(editorRow);

    this.editorRow = editorRow;
    this.editorHost = editorHost;
    this.multiviewRoot = multiviewRoot;
    this.multiviewChrome = multiviewChrome;
    this.multiviewBodyRow = multiviewBodyRow;
    this.multiviewPrimaryCol = multiviewPrimaryCol;
    this.multiviewPrimaryHeader = multiviewPrimaryHeader;
    this.multiviewExtras = multiviewExtras;
    this.multiviewPaneCountSelect = multiviewPaneCountSelect;
    this.multiviewExtraDocIds = new Array(MULTIVIEW_MAX_EXTRA).fill(null) as (string | null)[];
    this.multiviewSingleBtn = multiviewSingleBtn;
    this.lineNumberGutter = lineNumberGutter;
    this.lineNumberPre = lineNumberPre;
    this.highlightPre = highlightPre;
    this.highlightCode = highlightCode;
    this.activeLineDiv = null;
    this.markupOverlayPre = markupPre;
    this.markupOverlayCode = markupCode;
    this.markupContextCluster = markupCluster;
    this.markupToggleBtn = markupToggleBtn;
    this.gutterModeToggleBtn = gutterModeToggleBtn;
    this.editorContextMenu = editorContextMenu;
    this.editorContextSpellSuggestSep = spellSuggestSep;
    this.editorContextSpellSuggestCluster = spellSuggestCluster;
    this.editorContextTextCluster = textCluster;
    this.editorContextSpellcheckToggleBtn = spellcheckToggleBtn;
    this.editorContextShareSep = shareSep;
    this.editorContextShareBlock = shareBlock;
    this.editorShareRecipientInput = shareInp;
    this.editorContextBeautifyGeminiBtn = beautifyGeminiBtn;
    this.maxRenderedContextMenu = maxRenderedContextMenu;
    this.mdPreview = mdPreview;
    this.mdPreviewBody = mdPreviewBody;
    this.mdViewToggle = mdViewToggle;
    this.mdBackToPreviousBtn = mdBackToPreviousBtn;
    this.mdPreviewBody.addEventListener("click", (e) => this.onPreviewClick(e, "md", this.mdPreviewBody));
    this.texPreview = texPreview;
    this.texPreviewBody = texPreviewBody;
    this.texViewToggle = texViewToggle;
    this.texPreviewBody.addEventListener("click", (e) => this.onPreviewClick(e, "tex", this.texPreviewBody));
    this.maxPreview = maxPreview;
    this.maxPreviewBody = maxPreviewBody;
    this.maxViewToggle = maxViewToggle;
    this.maxAttachWrap = maxAttachWrap;
    document.addEventListener("pointerdown", this.onDocumentPointerDownCloseMaxInsertMenu, true);
    this.maxPreviewBody.addEventListener("contextmenu", this.onMaxPreviewContextMenu);
    this.maxPreviewBody.addEventListener("click", (e) => this.onPreviewClick(e, "max", this.maxPreviewBody));
    this.htmlPreview = htmlPreview;
    this.htmlPreviewBody = htmlPreviewBody;
    this.htmlViewToggle = htmlViewToggle;
    this.oeisPreview = oeisPreview;
    this.oeisPreviewBody = oeisPreviewBody;
    this.oeisPreviewBody.addEventListener("click", this.onOeisPreviewBodyClick);
    this.oeisPreviewBody.addEventListener("click", (e) => this.onPreviewClick(e, "oeis", this.oeisPreviewBody));
    this.oeisSeqIdInput = oeisSeqIdInput;
    this.oeisTermsSearchInput = oeisTermsSearchInput;
    this.oeisViewToggle = oeisViewToggle;
    this.csvPreview = csvPreview;
    this.csvTableHost = csvTableHost;
    this.csvOpsHost = csvOpsHost;
    this.excelSheetSelect = excelSheetSelect;
    this.csvViewToggle = csvViewToggle;
    this.binaryPreview = binaryPreview;
    this.binaryPreview.addEventListener("contextmenu", this.onBinaryPreviewContextMenu);
    this.binaryPreviewImg = binaryPreviewImg;
    this.binaryPreviewPdf = binaryPreviewPdf;
    this.binaryPreviewVideo = binaryPreviewVideo;
    this.nullaSectionsContainer = nullaSectionsContainer;
    this.sessionRestorePanel = sessionRestorePanel;
    this.sessionRestoreFileTitle = sessionRestoreFileTitle;
    this.sessionRestoreReloadBtn = sessionRestoreReloadBtn;
    this.diffEditorHost = diffEditorHost;
    this.diffEditorInner = diffEditorInner;
    this.syncHighlightScroll = () => {
      this.layoutSyntaxOverlayOverTextarea(this.editor, highlightPre);
      if (!this.markupOverlayPre.hidden) {
        this.layoutSyntaxOverlayOverTextarea(this.editor, this.markupOverlayPre);
      }
      const st = this.editor.scrollTop;
      highlightPre.scrollTop = st;
      highlightPre.scrollLeft = this.editor.scrollLeft;
      this.markupOverlayPre.scrollTop = st;
      this.markupOverlayPre.scrollLeft = this.editor.scrollLeft;
      lineNumberGutter.scrollTop = st;
    };
    this.onEditorScroll = () => {
      this.syncHighlightScroll();
      this.armFocusedEditorSyncLoop();
      if (this.activeRectSel?.ta === this.editor) this.activeRectSel.overlay.repaint();
      if (this.scrollCoalesceRaf) return;
      this.scrollCoalesceRaf = requestAnimationFrame(() => {
        this.scrollCoalesceRaf = 0;
        this.updateActiveLineHighlight();
        this.updateLineNumberGutter();
        this.syncPreviewToEditorCaret();
      });
    };
    this.onEditorContextPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (
        target &&
        (this.editorContextMenu.contains(target) || this.maxRenderedContextMenu.contains(target))
      ) {
        return;
      }
      this.hideEditorContextMenu();
      this.hideMaxRenderedContextMenu();
    };
    this.onEditorContextKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || (this.editorContextMenu.hidden && this.maxRenderedContextMenu.hidden)) return;
      e.preventDefault();
      this.hideEditorContextMenu();
      this.hideMaxRenderedContextMenu();
    };
    document.addEventListener("pointerdown", this.onEditorContextPointerDown, true);
    document.addEventListener("keydown", this.onEditorContextKeyDown, true);

    const prefs = loadWebPrefs();
    this.editorBaseFontPx = prefs.editorBaseFontPx;
    this.nativeSpellcheckEnabled = prefs.nativeSpellcheckEnabled === true;
    this.spellcheckExceptions = [...(prefs.spellcheckExceptions ?? [])];
    setSpellcheckExceptionWords(this.spellcheckExceptions);
    this.syncSpellcheckToggleLabels();

    const mainStack = document.createElement("div");
    mainStack.className = "npp-main-stack";
    const findHost = document.createElement("div");
    findHost.className = "npp-find-results-host";
    mainStack.append(editorWrap, findHost);

    const folderWorkspaceHost = document.createElement("div");
    folderWorkspaceHost.className = "npp-folder-workspace-host";

    const workspaceSash = document.createElement("div");
    workspaceSash.className = "npp-workspace-sash";
    workspaceSash.setAttribute("role", "separator");
    workspaceSash.setAttribute("aria-orientation", "vertical");
    workspaceSash.title = "Drag to resize workspace";
    this.workspaceSash = workspaceSash;

    const bodyRow = document.createElement("div");
    bodyRow.className = "npp-body-row";
    bodyRow.append(folderWorkspaceHost, workspaceSash, mainStack);

    this.gitTerminalHost = document.createElement("div");
    this.gitTerminalHost.className = "npp-git-terminal-host";
    this.gitTerminalHost.hidden = true;

    const bodyColumn = document.createElement("div");
    bodyColumn.className = "npp-body-column";
    bodyColumn.append(bodyRow, this.gitTerminalHost);

    this.status = document.createElement("footer");
    this.status.className = "npp-status";
    this.status.textContent = "Ready";

    const shareInboxBar = document.createElement("div");
    shareInboxBar.className = "npp-share-inbox-bar";
    shareInboxBar.hidden = true;
    shareInboxBar.setAttribute("role", "region");
    shareInboxBar.setAttribute("aria-label", "Incoming NullaPad share");
    const shareInboxText = document.createElement("span");
    shareInboxText.className = "npp-share-inbox-bar__text";
    const shareInboxAccept = document.createElement("button");
    shareInboxAccept.type = "button";
    shareInboxAccept.className = "npp-share-inbox-bar__btn npp-share-inbox-bar__btn--accept";
    shareInboxAccept.textContent = "Accept";
    shareInboxAccept.setAttribute("aria-label", "Accept incoming share into a new tab");
    shareInboxAccept.disabled = true;
    shareInboxAccept.title = "Available once this tab is active";
    const shareInboxDecline = document.createElement("button");
    shareInboxDecline.type = "button";
    shareInboxDecline.className = "npp-share-inbox-bar__btn npp-share-inbox-bar__btn--decline";
    shareInboxDecline.textContent = "Decline";
    shareInboxDecline.setAttribute("aria-label", "Decline incoming share and clear server queue");
    shareInboxAccept.addEventListener("click", () => void this.onShareInboxAcceptClick());
    shareInboxDecline.addEventListener("click", () => void this.onShareInboxDeclineClick());
    shareInboxBar.append(shareInboxText, shareInboxAccept, shareInboxDecline);
    this.shareInboxOfferBar = shareInboxBar;
    this.shareInboxOfferText = shareInboxText;
    this.shareInboxAcceptBtn = shareInboxAccept;

    // ── Mobile bottom toolbar ────────────────────────────────────────────
    // Visible only on touch/narrow viewports (CSS hides it on desktop).
    const mobileMenuBackdrop = document.createElement("div");
    mobileMenuBackdrop.className = "npp-mobile-menu-backdrop";
    mobileMenuBackdrop.setAttribute("aria-hidden", "true");
    mobileMenuBackdrop.hidden = true;

    const mobileToolbar = document.createElement("nav");
    mobileToolbar.className = "npp-mobile-toolbar";
    mobileToolbar.setAttribute("role", "toolbar");
    mobileToolbar.setAttribute("aria-label", "Quick actions");

    const mkMobileBtn = (
      label: string,
      svgBody: string,
      onClick: () => void,
    ): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "npp-mobile-toolbar-btn";
      btn.setAttribute("aria-label", label);
      const icon = document.createElement("span");
      icon.className = "npp-mobile-toolbar-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svgBody}</svg>`;
      const lbl = document.createElement("span");
      lbl.className = "npp-mobile-toolbar-label";
      lbl.textContent = label;
      btn.append(icon, lbl);
      btn.addEventListener("click", onClick);
      return btn;
    };

    let mobileMenuOpen = false;
    const closeMobileMenu = (): void => {
      if (!mobileMenuOpen) return;
      mobileMenuOpen = false;
      root.classList.remove("npp-shell--mobile-menu-open");
      mobileMenuBackdrop.hidden = true;
      mobileMenuToggleBtn.setAttribute("aria-pressed", "false");
    };
    const mobileMenuToggleBtn = mkMobileBtn(
      "Menu",
      '<line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>',
      () => {
        mobileMenuOpen = !mobileMenuOpen;
        root.classList.toggle("npp-shell--mobile-menu-open", mobileMenuOpen);
        mobileMenuBackdrop.hidden = !mobileMenuOpen;
        mobileMenuToggleBtn.setAttribute("aria-pressed", mobileMenuOpen ? "true" : "false");
      },
    );
    mobileMenuToggleBtn.setAttribute("aria-pressed", "false");

    mobileMenuBackdrop.addEventListener("click", closeMobileMenu);
    document.addEventListener(NPP_COMMAND_EVENT, closeMobileMenu);

    // ── Mobile language picker (slides up above the toolbar) ─────────────
    const mobileLangPicker = document.createElement("div");
    mobileLangPicker.className = "npp-mobile-lang-picker";
    mobileLangPicker.hidden = true;
    mobileLangPicker.setAttribute("role", "dialog");
    mobileLangPicker.setAttribute("aria-label", "Select language");

    const pickerHeader = document.createElement("div");
    pickerHeader.className = "npp-mobile-lang-picker-header";
    const pickerTitle = document.createElement("span");
    pickerTitle.className = "npp-mobile-lang-picker-title";
    pickerTitle.textContent = "Language";
    const pickerContent = document.createElement("div");
    pickerContent.className = "npp-mobile-lang-picker-content";

    let langPickerOpen = false;
    const closeLangPicker = (): void => {
      if (!langPickerOpen) return;
      langPickerOpen = false;
      mobileLangPicker.hidden = true;
      mobileLangBtn.setAttribute("aria-pressed", "false");
    };

    const pickerCloseBtn = document.createElement("button");
    pickerCloseBtn.type = "button";
    pickerCloseBtn.className = "npp-mobile-lang-picker-close";
    pickerCloseBtn.setAttribute("aria-label", "Close");
    pickerCloseBtn.textContent = "×";
    pickerCloseBtn.addEventListener("click", closeLangPicker);
    pickerHeader.append(pickerTitle, pickerCloseBtn);

    // Group languages into letter-range bands so each band has roughly equal count:
    // B–D (7), E–J (7), L–N (6), O–P (7), R–Y (8)
    type LangItem = Extract<typeof LANGUAGE_MENU_ROWS[number], { kind: "item" }>;
    const langEntries = LANGUAGE_MENU_ROWS.filter((r): r is LangItem => r.kind === "item");
    const LETTER_BAND: Record<string, string> = {
      B: "B–D", C: "B–D", D: "B–D",
      E: "E–J", F: "E–J", G: "E–J", H: "E–J", I: "E–J", J: "E–J",
      L: "L–N", M: "L–N", N: "L–N",
      O: "O–P", P: "O–P",
      R: "R–Y", S: "R–Y", T: "R–Y", U: "R–Y", V: "R–Y",
      W: "R–Y", X: "R–Y", Y: "R–Y", Z: "R–Y",
    };
    const BAND_ORDER = ["B–D", "E–J", "L–N", "O–P", "R–Y"];
    const byBand = new Map<string, LangItem[]>(BAND_ORDER.map(b => [b, []]));
    for (const entry of langEntries) {
      const ch = languageLabel(entry.mode)[0]!.toUpperCase();
      const band = LETTER_BAND[ch] ?? ch;
      if (!byBand.has(band)) byBand.set(band, []);
      byBand.get(band)!.push(entry);
    }
    for (const [band, entries] of byBand) {
      if (entries.length === 0) continue;
      const letterEl = document.createElement("div");
      letterEl.className = "npp-mobile-lang-picker-letter";
      letterEl.textContent = band;
      const groupEl = document.createElement("div");
      groupEl.className = "npp-mobile-lang-picker-group";
      for (const entry of entries) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "npp-mobile-lang-picker-item";
        btn.dataset.mode = entry.mode;
        btn.textContent = languageLabel(entry.mode);
        btn.addEventListener("click", () => {
          void this.dispatcher.executeCommand(entry.id);
          closeLangPicker();
        });
        groupEl.appendChild(btn);
      }
      const rowEl = document.createElement("div");
      rowEl.className = "npp-mobile-lang-picker-row";
      rowEl.append(letterEl, groupEl);
      pickerContent.append(rowEl);
    }
    mobileLangPicker.append(pickerHeader, pickerContent);

    // Shared state — both mobile and desktop trigger buttons register here
    const langTriggerBtns: HTMLButtonElement[] = [];

    const openLangPicker = (fromTrigger: HTMLButtonElement, desktopMode: boolean): void => {
      closeMobileMenu();
      langPickerOpen = true;
      if (desktopMode) {
        mobileLangPicker.dataset.origin = "desktop";
        const rect = fromTrigger.getBoundingClientRect();
        mobileLangPicker.style.setProperty("--npp-lp-x", `${Math.round(rect.left)}px`);
        mobileLangPicker.style.setProperty("--npp-lp-y", `${Math.round(rect.bottom + 3)}px`);
      } else {
        delete mobileLangPicker.dataset.origin;
      }
      mobileLangPicker.hidden = false;
      for (const b of langTriggerBtns) b.setAttribute("aria-pressed", "false");
      fromTrigger.setAttribute("aria-pressed", "true");
      const cur = this.documents.active?.language;
      for (const b of mobileLangPicker.querySelectorAll<HTMLButtonElement>(".npp-mobile-lang-picker-item")) {
        b.setAttribute("aria-current", b.dataset.mode === cur ? "true" : "false");
      }
    };

    // Overwrite closeLangPicker to also clear the trigger array
    const closeLangPickerFull = (): void => {
      if (!langPickerOpen) return;
      langPickerOpen = false;
      mobileLangPicker.hidden = true;
      delete mobileLangPicker.dataset.origin;
      for (const b of langTriggerBtns) b.setAttribute("aria-pressed", "false");
    };
    // Rebind the button and command listener to the full version
    pickerCloseBtn.removeEventListener("click", closeLangPicker);
    pickerCloseBtn.addEventListener("click", closeLangPickerFull);
    document.addEventListener(NPP_COMMAND_EVENT, closeLangPickerFull);
    // Close on outside click
    document.addEventListener("pointerdown", (e: PointerEvent) => {
      if (!langPickerOpen) return;
      const t = e.target as Node | null;
      if (mobileLangPicker.contains(t)) return;
      if (langTriggerBtns.some(b => b.contains(t))) return;
      closeLangPickerFull();
    }, true);

    const mobileLangBtn = mkMobileBtn(
      "Lang",
      '<path d="M10 15l-5-5 5-5"/><path d="M14 5l5 5-5 5"/>',
      () => {
        if (langPickerOpen && !mobileLangPicker.dataset.origin) {
          closeLangPickerFull();
        } else {
          openLangPicker(mobileLangBtn, false);
        }
      },
    );
    mobileLangBtn.setAttribute("aria-pressed", "false");
    langTriggerBtns.push(mobileLangBtn);

    mobileToolbar.append(
      mkMobileBtn(
        "New",
        '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>',
        () => void this.dispatcher.executeCommand(CMD.FILE_NEW),
      ),
      mkMobileBtn(
        "Open",
        '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
        () => void this.dispatcher.executeCommand(CMD.FILE_OPEN),
      ),
      mkMobileBtn(
        "Save",
        '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>',
        () => void this.dispatcher.executeCommand(CMD.FILE_SAVE),
      ),
      mkMobileBtn(
        "Find",
        '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
        () => void this.dispatcher.executeCommand(CMD.SEARCH_FIND),
      ),
      mobileLangBtn,
      mobileMenuToggleBtn,
    );

    root.append(
      brandMenubarRow,
      title,
      tabHost,
      bodyColumn,
      shareInboxBar,
      this.status,
      mobileMenuBackdrop,
      mobileLangPicker,
      mobileToolbar,
    );

    this.findResultsPane = new FindResultsPane(findHost);
    this.findResultsPane.setNavigateHandler((hit) => void this.navigateToFindHit(hit));

    this.folderWorkspacePane = new FolderWorkspacePane(folderWorkspaceHost);
    this.folderWorkspacePane.setOnOpenFile(async (relPath, handle) => {
      const rootDir = this.folderWorkspacePane?.getRoot();
      if (!rootDir) return;
      this.flushEditorToBuffer();
      const ok = await this.documents.openFromWorkspace(rootDir.name, relPath, handle);
      if (!ok) {
        void alert(
          `Could not open "${relPath}" (permission denied, unreadable, or file too large).`,
        );
        return;
      }
      this.tabBar?.refresh();
      this.loadActiveIntoEditor();
      this.updateSyntaxHighlightLayer();
      if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
      if (this.documents.active?.language === "tex") this.scheduleTexPreview();
      if (this.documents.active?.language === "max") this.scheduleMaxPreview();
      if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
      if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
      if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
      this.rebuildMenubar();
      this.updateStatusLine();
    });
    this.folderWorkspacePane.setOnClose(() => {
      this.documents.setWorkspaceRootHandle(null);
      void persistWorkspaceFolderHandle(null);
      void this.syncGitTerminalWorkspace();
      this.updateStatusLine("Folder workspace closed");
    });

    this.gitTerminalPanel = new GitTerminalPanel({
      onGitMutation: async () => {
        await this.folderWorkspacePane?.refreshFileTree();
      },
      onOpenGitDiffBatch: (snapshots) => {
        this.flushEditorToBuffer();
        for (const snapshot of snapshots) {
          this.documents.addDiffTab(snapshot);
        }
        this.tabBar?.refresh();
        this.loadActiveIntoEditor();
        const n = snapshots.length;
        this.updateStatusLine(`${n} compare tab${n !== 1 ? "s" : ""} opened from git diff`);
      },
      onOpenTextTab: ({ title, content, language }) => {
        this.flushEditorToBuffer();
        this.documents.addContentTab(title, content, language);
        this.tabBar?.refresh();
        this.loadActiveIntoEditor();
        this.updateStatusLine(`Opened "${title}" from git show`);
      },
      onRequestClose: () => {
        this.gitTerminalHost.hidden = true;
      },
      onOperationStatus: (message) => {
        this.setStickyStatus(message);
      },
      onCloneRequested: async (url, directoryName, onProgress) => {
        this.gitTerminalHost.hidden = false;
        this.setStickyStatus(`Git: starting clone from ${url}...`, 8000);
        if (!this.folderWorkspacePane) return;
        const base = await pickWorkspaceDirectory();
        if (!base) throw new Error("Clone canceled.");
        let target = base;
        if (directoryName) {
          const leaf = directoryName.trim();
          if (!leaf || leaf === "." || leaf === ".." || /[\\/]/.test(leaf)) {
            throw new Error("Clone directory must be a single folder name (no path separators).");
          }
          target = await base.getDirectoryHandle(leaf, { create: true });
        }
        await gitWorkspaceCloneInto(target, url, onProgress);
        this.setStickyStatus("Git: clone finished, opening workspace...", 5000);
        await this.folderWorkspacePane.setRoot(target);
        this.documents.setWorkspaceRootHandle(target);
        void persistWorkspaceFolderHandle(target);
        this.rebuildMenubar();
        this.updateStatusLine(`Workspace: ${target.name}`);
        await this.syncGitTerminalWorkspace();
        this.gitTerminalHost.hidden = false;
        this.gitTerminalPanel?.focusCommandInput();
        this.setStickyStatus("Git terminal ready (try: git pull)", 5000);
        await this.folderWorkspacePane.refreshFileTree();
      },
    });
    this.gitTerminalHost.appendChild(this.gitTerminalPanel.element);
    void this.syncGitTerminalWorkspace();

    this.shell = root;
    this.menuBar = new MenuBar(buildNotepadMenuModel(undefined, prefs.shortcutOverrides), {
      dispatcher: this.dispatcher,
      eventTarget: this.shell,
    });
    this.menuBar.mount(menubarHost);
    this.tabBar = new TabBar(
      this.documents,
      tabHost,
      menubarHost,
      (i, ev) => this.selectTabFromTabBar(i, ev),
      (i) => this.closeTab(i),
      (i) => this.closeOtherTabs(i),
      () => this.closeAllTabsFromContext(),
      (i) => void this.copyPathOrTitleForTab(i),
      () => this.newUntitledTabFromTabBar(),
      (i) => this.cycleTabColorFromTabBar(i),
      (i, c) => this.setTabColorFromTabBar(i, c),
      (i) => this.onDiffPickLeft(i),
      (i) => this.onDiffPickRight(i),
      (i) => this.onDiffTabMonacoLayout(i),
      (i) => this.onDiffTabSplitPanesLayout(i),
      (from, insert) => this.onTabReorder(from, insert),
      (sep, color) => this.mergeSameColorFromTabBar(sep, color),
      (sep) => this.splitActiveFromTabBar(sep),
      (c) => this.documents.setNewTabGroupColor(c),
      (i) => this.duplicateTabFromTabBar(i),
      () => this.nativeSpellcheckEnabled,
      () => this.toggleNativeSpellcheckFromMenu(),
      (i, rid) => void this.shareTabFromContextMenu(i, rid),
      nullaRecordMicGroup,
      () => this.multiviewEnabled,
      () => this.getMultiviewExtraTabIndices(),
      () => this.syncAllMultiviewExtraSlotHeaders(),
      (i, newTitle) => this.renameTabFromContext(i, newTitle),
    );
    this.syncSpellcheckToggleLabels();
    this.registerDefaultCommands();
    this.syncMarkdownPreviewChromeFromPrefs();
    const appendToolbarBtn = (
      host: HTMLElement,
      label: string,
      iconClass: string,
      commandId: string,
    ): void => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "npp-toolbar-btn";
      btn.title = label;
      btn.setAttribute("aria-label", label);
      btn.innerHTML = `<span class="npp-toolbar-icon ${iconClass}" aria-hidden="true"></span>`;
      btn.addEventListener("mousedown", (ev) => {
        // Keep typing focus in the editor when using title-bar tools (click alone would focus the button).
        ev.preventDefault();
      });
      btn.addEventListener("click", () => {
        void this.dispatcher.executeCommand(commandId);
        this.shell.dispatchEvent(
          new CustomEvent(NPP_COMMAND_EVENT, {
            bubbles: true,
            composed: true,
            detail: { commandId, source: "ui" as const },
          }),
        );
      });
      host.appendChild(btn);
    };
    const addToolbarBtn = (label: string, iconClass: string, commandId: string): void =>
      appendToolbarBtn(toolbarHost, label, iconClass, commandId);
    const addToolbarSep = (): void => {
      const sep = document.createElement("span");
      sep.className = "npp-toolbar-sep";
      toolbarHost.appendChild(sep);
    };
    addToolbarBtn("New", "npp-ico-new", CMD.FILE_NEW);
    addToolbarBtn("Open", "npp-ico-open", CMD.FILE_OPEN);
    addToolbarBtn("Save", "npp-ico-save", CMD.FILE_SAVE);
    addToolbarBtn("Save All", "npp-ico-saveall", CMD.FILE_SAVEALL);
    addToolbarSep();
    addToolbarBtn("Find", "npp-ico-find", CMD.SEARCH_FIND);
    addToolbarBtn("Replace", "npp-ico-replace", CMD.SEARCH_REPLACE);
    addToolbarBtn("Go to", "npp-ico-goto", CMD.SEARCH_GOTOLINE);
    addToolbarSep();
    addToolbarBtn("Open Workspace", "npp-ico-open-workspace", CMD.FILE_OPEN_FOLDER);
    addToolbarBtn("Multiview", "npp-ico-multiview", CMD.VIEW_MULTIVIEW);
    addToolbarSep();
    addToolbarBtn("Undo", "npp-ico-undo", CMD.EDIT_UNDO);
    addToolbarBtn("Redo", "npp-ico-redo", CMD.EDIT_REDO);
    addToolbarSep();
    // Language picker — desktop toolbar button (shared panel with mobile)
    const desktopLangBtn = document.createElement("button");
    desktopLangBtn.type = "button";
    desktopLangBtn.className = "npp-toolbar-btn npp-toolbar-btn--lang-picker";
    desktopLangBtn.title = "Select syntax language for this tab";
    desktopLangBtn.setAttribute("aria-label", "Select language");
    desktopLangBtn.setAttribute("aria-pressed", "false");
    desktopLangBtn.setAttribute("aria-haspopup", "true");
    desktopLangBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="7 8 3 12 7 16"/><polyline points="17 8 21 12 17 16"/></svg>' +
      '<span>Lang</span>';
    desktopLangBtn.addEventListener("mousedown", (e) => e.preventDefault());
    desktopLangBtn.addEventListener("click", () => {
      if (langPickerOpen && mobileLangPicker.dataset.origin === "desktop") {
        closeLangPickerFull();
      } else {
        openLangPicker(desktopLangBtn, true);
      }
    });
    toolbarHost.appendChild(desktopLangBtn);
    langTriggerBtns.push(desktopLangBtn);
    appendToolbarBtn(nullaRecordMicGroup, "Record Max video", "npp-ico-record", CMD.FILE_RECORD_NULLA);
    this.mountNullaMicTrackToolbarButton(nullaRecordMicGroup);
    this.mountDictationToolbarButton(nullaRecordMicGroup);
    this.mountOeisToolbarButton(nullaRecordMicGroup);
    this.reloadKeyboardShortcuts();
    if (prefs.defaultWordWrap) {
      this.dispatcher.executeCommand(CMD.VIEW_WRAP);
      this.menuBar.syncCheckboxStates([CMD.VIEW_WRAP]);
    }

    this.shell.addEventListener(NPP_COMMAND_EVENT, ((ev: CustomEvent<NppCommandDetail>) => {
      this.updateStatusLine(`Command: ${ev.detail.commandId} (${ev.detail.source})`);
    }) as EventListener);

    this.editor.addEventListener("input", () => this.onEditorInput());
    const taInteract = { signal: this.textAreaInteractionAbort.signal };
    const taCol = { ...taInteract, capture: true as const };
    this.editor.addEventListener("keydown", (e) => this.onTextareaTabKeydown(this.editor, e), taInteract);
    this.editor.addEventListener("keydown", this.onTextareaRectSelKeydown, taInteract);
    // Capture so Option/Alt+drag runs before Safari’s default textarea behavior (often eats the gesture).
    this.editor.addEventListener("pointerdown", (e) => this.onTextareaAltColumnPointerDown(this.editor, e), taCol);
    this.editor.addEventListener("click", () => {
      this.updateStatusLine();
      this.updateSelectionVisualMode();
    });
    this.editor.addEventListener("keyup", () => {
      this.updateStatusLine();
      this.updateSelectionVisualMode();
    });
    this.editor.addEventListener("select", () => {
      this.updateStatusLine();
      this.updateSelectionVisualMode();
    });
    document.addEventListener("selectionchange", this.onDocumentSelectionChange);
    this.editor.addEventListener("scroll", this.onEditorScroll);
    this.editor.addEventListener("wheel", this.onEditorWheel, { passive: true });
    this.editor.addEventListener("focus", this.onEditorFocus);
    this.editor.addEventListener("blur", this.onEditorBlur);
    this.editor.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.editorContextTargetTextarea = this.editor;
      this.showEditorContextMenu(e.clientX, e.clientY, "editor");
    });

    for (let slot = 0; slot < this.multiviewExtras.length; slot++) {
      const ex = this.multiviewExtras[slot]!;
      const s = slot;
      ex.editor.addEventListener("input", () => this.onMultiviewExtraEditorInput(s));
      ex.editor.addEventListener("keydown", (e) => this.onTextareaTabKeydown(ex.editor, e), taInteract);
      ex.editor.addEventListener("keydown", this.onTextareaRectSelKeydown, taInteract);
      ex.editor.addEventListener("pointerdown", (e) => this.onTextareaAltColumnPointerDown(ex.editor, e), taCol);
      ex.editor.addEventListener("scroll", () => {
        this.syncMultiviewExtraHighlightScroll(s);
        this.updateLineNumberGutterForExtraSlot(s);
        if (this.activeRectSel?.ta === ex.editor) this.activeRectSel.overlay.repaint();
      });
      ex.editor.addEventListener("wheel", this.onEditorWheel, { passive: true });
      ex.editor.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        this.editorContextTargetTextarea = ex.editor;
        this.showEditorContextMenu(e.clientX, e.clientY, "editor");
      });
    }

    if (typeof ResizeObserver !== "undefined") {
      this.editorRowResizeObserver = new ResizeObserver(() => {
        this.syncHighlightScroll();
        const n = this.multiviewExtraSlotsUsed();
        for (let slot = 0; slot < n; slot++) this.syncMultiviewExtraHighlightScroll(slot);
        this.updateLineNumberGutter();
      });
      this.editorRowResizeObserver.observe(this.editorRow);
    }

    this.mountMultiviewSash();
    this.mountWorkspaceSash();
    this.mountPreviewSash();

    document.addEventListener("keydown", this.onGlobalKeydown);

    window.addEventListener("beforeunload", this.onBeforeUnloadPersistSession);
    window.addEventListener("pagehide", this.onPageHidePersistSession);
    document.addEventListener("visibilitychange", this.onVisibilityPersistSession);

    this.loadActiveIntoEditor();
    this.tabBar.refresh();
    this.updateStatusLine();
    this.persistTabSessionToStorage();
    void this.restoreWorkspaceFolderFromCache();
    void this.tryPresentStagedShareIfNoBar();
    this.startShareInboxPolling();
    this.startExternalFilePolling();
  }

  private schedulePersistTabSession(): void {
    if (this.tabSessionPersistTimer) clearTimeout(this.tabSessionPersistTimer);
    this.tabSessionPersistTimer = setTimeout(() => {
      this.tabSessionPersistTimer = null;
      this.persistTabSessionToStorage();
    }, 600);
  }

  private persistTabSessionToStorage(): void {
    this.flushEditorToBuffer();
    persistTabSession(this.documents);
    // Only persist a non-null root. Passing null would clear IndexedDB on every load
    // (before async restore runs) and the workspace would never reappear after refresh.
    const root = this.folderWorkspacePane?.getRoot() ?? null;
    if (root !== null) {
      void persistWorkspaceFolderHandle(root);
    }
  }

  /** Re-open “folder as workspace” from IndexedDB after reload (Chrome/Edge FS Access). */
  private async restoreWorkspaceFolderFromCache(): Promise<void> {
    if (!this.folderWorkspacePane) return;
    const h = await loadWorkspaceFolderHandle();
    if (!h) return;
    await this.folderWorkspacePane.setRoot(h);
    this.documents.setWorkspaceRootHandle(h);
    this.rebuildMenubar();
    this.updateStatusLine(`Workspace: ${h.name}`);
    void this.syncGitTerminalWorkspace();
  }

  private async syncGitTerminalWorkspace(): Promise<void> {
    const panel = this.gitTerminalPanel;
    if (!panel) return;
    const root = this.folderWorkspacePane?.getRoot() ?? null;
    if (!root) {
      panel.setWorkspace(null, false);
      return;
    }
    const isGit = await isWorkspaceGitRepository(root);
    const largestPack = isGit ? await getLargestPackfileBytes(root) : 0;
    panel.setWorkspace(root, isGit, largestPack);
  }

  destroy(): void {
    this.clearColumnDragListeners();
    this.textAreaInteractionAbort.abort();
    window.removeEventListener("beforeunload", this.onBeforeUnloadPersistSession);
    window.removeEventListener("pagehide", this.onPageHidePersistSession);
    document.removeEventListener("visibilitychange", this.onVisibilityPersistSession);
    if (this.tabSessionPersistTimer) {
      clearTimeout(this.tabSessionPersistTimer);
      this.tabSessionPersistTimer = null;
    }
    if (this.stickyStatusTimer) {
      clearTimeout(this.stickyStatusTimer);
      this.stickyStatusTimer = null;
    }
    if (this.shareInboxPollTimer != null) {
      clearInterval(this.shareInboxPollTimer);
      this.shareInboxPollTimer = null;
    }
    if (this.nullapadInstanceSecretAutosyncTimer != null) {
      clearInterval(this.nullapadInstanceSecretAutosyncTimer);
      this.nullapadInstanceSecretAutosyncTimer = null;
    }
    if (this.oeisTermsSearchPersistTimer !== null) {
      clearTimeout(this.oeisTermsSearchPersistTimer);
      this.oeisTermsSearchPersistTimer = null;
    }
    this.cancelShareInboxAcceptEnableTimer();
    this.shareInboxOfferState = null;
    this.shareInboxOfferBar.hidden = true;
    this.shareInboxAcceptBtn.disabled = true;
    this.shareInboxAcceptBtn.removeAttribute("title");
    this.shareInboxStagedFp = null;
    void clearStagedShare().catch(() => {});
    this.persistTabSessionToStorage();
    for (const doc of this.documents.documents) {
      if (doc.viewMode !== "text" && doc.previewObjectUrl) {
        URL.revokeObjectURL(doc.previewObjectUrl);
        doc.previewObjectUrl = null;
      }
    }
    this.binaryPreviewImg.removeAttribute("src");
    this.binaryPreviewPdf.removeAttribute("src");
    if (this.scrollCoalesceRaf) {
      cancelAnimationFrame(this.scrollCoalesceRaf);
      this.scrollCoalesceRaf = 0;
    }
    if (this.selectionCoalesceRaf) {
      cancelAnimationFrame(this.selectionCoalesceRaf);
      this.selectionCoalesceRaf = 0;
    }
    if (this.editorFocusSyncRaf) {
      cancelAnimationFrame(this.editorFocusSyncRaf);
      this.editorFocusSyncRaf = 0;
    }
    this.lastFocusSyncScrollTop = Number.NaN;
    this.lastFocusSyncScrollLeft = Number.NaN;
    document.removeEventListener("selectionchange", this.onDocumentSelectionChange);
    document.removeEventListener("keydown", this.onGlobalKeydown);
    document.removeEventListener("pointerdown", this.onEditorContextPointerDown, true);
    document.removeEventListener("pointerdown", this.onDocumentPointerDownCloseMaxInsertMenu, true);
    document.removeEventListener("keydown", this.onEditorContextKeyDown, true);
    this.editor.removeEventListener("scroll", this.onEditorScroll);
    this.editor.removeEventListener("wheel", this.onEditorWheel);
    this.editor.removeEventListener("focus", this.onEditorFocus);
    this.editor.removeEventListener("blur", this.onEditorBlur);
    if (this.debounceCheckpoint) clearTimeout(this.debounceCheckpoint);
    if (this.mdPreviewTimer) clearTimeout(this.mdPreviewTimer);
    if (this.csvPreviewTimer) clearTimeout(this.csvPreviewTimer);
    this.editorRowResizeObserver?.disconnect();
    this.editorRowResizeObserver = null;
    this.destroyCsvGrid();
    this.disposeDiffView();
    this.menuBar?.destroy();
    this.menuBar = null;
    this.findResultsPane?.destroy();
    this.findResultsPane = null;
    this.folderWorkspacePane?.destroy();
    this.folderWorkspacePane = null;
    this.gitTerminalPanel = null;
    this.gitTerminalHost.replaceChildren();
    this.documents.setWorkspaceRootHandle(null);
    this.binaryPreview.removeEventListener("contextmenu", this.onBinaryPreviewContextMenu);
    this.maxPreviewBody.removeEventListener("contextmenu", this.onMaxPreviewContextMenu);
    this.oeisPreviewBody.removeEventListener("click", this.onOeisPreviewBodyClick);
    this.tabBar?.destroy();
    this.tabBar = null;
    this.multiviewSashCleanup?.();
    this.multiviewSashCleanup = null;
    if (this.externalFilePollTimer) {
      clearInterval(this.externalFilePollTimer);
      this.externalFilePollTimer = null;
    }
    this.editorContextMenu.remove();
    this.maxRenderedContextMenu.remove();
  }

  get textarea(): HTMLTextAreaElement {
    return this.editor;
  }

  private flushEditorToBuffer(): void {
    const d = this.documents.active;
    if (d) {
      if (d.viewMode === "text") {
        this.documents.syncFromEditor(this.editor.value);
      } else if (d.viewMode === "video" && d.content) {
        this.flushNullaSectionsToBuffer();
      }
    }
    this.flushExtraEditorsToBuffer();
  }

  private multiviewExtraSlotsUsed(): number {
    return this.multiviewEnabled ? Math.max(0, this.multiviewPaneCount - 1) : 0;
  }

  private flushExtraEditorsToBuffer(): void {
    if (!this.multiviewEnabled) return;
    const used = this.multiviewExtraSlotsUsed();
    for (let slot = 0; slot < used; slot++) {
      const id = this.multiviewExtraDocIds[slot];
      if (!id) continue;
      const idx = this.documents.documents.findIndex((x) => x.id === id);
      if (idx < 0) continue;
      const d = this.documents.documents[idx];
      if (!d || d.viewMode !== "text") continue;
      this.documents.syncFromEditorAt(idx, this.multiviewExtras[slot]!.editor.value);
    }
  }

  private flushNullaSectionsToBuffer(): void {
    const d = this.documents.active;
    if (!d || d.viewMode !== "video" || !d.content) return;
    const sections = parseSections(d.content);
    const textValues: string[] = [];
    for (const el of this.nullaSectionEls) {
      if (el instanceof HTMLTextAreaElement) textValues.push(el.value);
    }
    const textSectionCount = sections.reduce((n, s) => n + (s.type === "text" ? 1 : 0), 0);
    // If the Nulla section UI was never built (e.g. record on a fresh tab) or is torn down, `textValues`
    // is shorter than the model — applying it would clear every text block and corrupt clips/audio.
    if (textValues.length !== textSectionCount) return;
    const updated = encodeSections(setTextSections(sections, textValues));
    if (updated !== d.content) {
      d.content = updated;
      d.dirty = d.content !== d.savedContent;
    }
  }

  private onNullaSectionInput(): void {
    const d = this.documents.active;
    if (d?.viewMode !== "video") return;
    this.flushNullaSectionsToBuffer();
    this.schedulePersistTabSession();
    this.tabBar?.refresh();
    this.syncDictationToolbarButton();
    this.updateStatusLine();
  }

  /** Appends a remove (×) control for a video or audio block at {@link parseSections} index `sectionIndex`. */
  private attachNullaClipRemoveControl(wrap: HTMLElement, sectionIndex: number): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-nulla-section-remove";
    btn.textContent = "×";
    btn.title = "Remove this clip";
    btn.setAttribute("aria-label", "Remove this clip");
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.removeNullaClipSectionAtIndex(sectionIndex);
    });
    wrap.appendChild(btn);
  }

  private removeNullaClipSectionAtIndex(sectionIndex: number): void {
    const d = this.documents.active;
    if (!d || d.viewMode !== "video" || !d.content) return;

    if (
      this.nullaMicTrackRecorder &&
      this.nullaMicTrackRecorder.state !== "inactive" &&
      this.nullaMicTrackSectionIndex === sectionIndex
    ) {
      this.nullaMicTrackDiscard = true;
      this.nullaMicTrackRecorder.stop();
    } else if (
      this.nullaMicTrackRecorder &&
      this.nullaMicTrackRecorder.state !== "inactive" &&
      sectionIndex < this.nullaMicTrackSectionIndex
    ) {
      this.nullaMicTrackSectionIndex -= 1;
    }

    this.flushNullaSectionsToBuffer();
    const sections = parseSections(d.content);
    const next = removeNullaClipSectionAt(sections, sectionIndex);
    if (!next) return;

    d.content = encodeSections(next);
    d.dirty = d.content !== d.savedContent;
    this.renderNullaSections(d);
    this.tabBar?.refresh();
    this.schedulePersistTabSession();
    this.syncDictationToolbarButton();
    this.updateStatusLine("Clip removed.");
  }

  private renderNullaSections(d: import("./doc/DocumentManager.js").TabDocument): void {
    this.teardownNullaSections({ abortClipMic: false });
    if (!d.content) {
      this.nullaSectionsContainer.hidden = false;
      return;
    }
    const sections = parseSections(d.content);
    const blobs = decodeVideoBlobs(sections);
    const els: HTMLElement[] = [];
    const urls: (string | null)[] = [];
    const voiceUrls: (string | null)[] = [];

    for (let i = 0; i < sections.length; i++) {
      const s = sections[i]!;
      if (s.type === "text") {
        const ta = document.createElement("textarea");
        ta.className = "npp-nulla-text";
        ta.placeholder = "Type text here…";
        ta.spellcheck = this.nativeSpellcheckEnabled;
        ta.value = s.content;
        ta.addEventListener("input", () => this.onNullaSectionInput());
        els.push(ta);
        urls.push(null);
        voiceUrls.push(null);
      } else if (s.type === "audio") {
        const wrap = document.createElement("div");
        wrap.className = "npp-nulla-video-wrap";
        let voiceUrl: string | null = null;
        const hex = s.hex64;
        if (hex) {
          const bytes = hex64ToBytes(hex);
          if (bytes && bytes.length > 0) {
            const payload = Uint8Array.from(bytes);
            const ab = new Blob([payload], { type: s.meta.mime || "audio/webm" });
            voiceUrl = URL.createObjectURL(ab);
            const aud = document.createElement("audio");
            aud.className = "npp-nulla-mic-track-audio";
            aud.controls = true;
            aud.src = voiceUrl;
            wrap.appendChild(aud);
          }
        }
        this.attachNullaClipRemoveControl(wrap, i);
        els.push(wrap);
        urls.push(null);
        voiceUrls.push(voiceUrl);
      } else {
        const wrap = document.createElement("div");
        wrap.className = "npp-nulla-video-wrap";
        const vid = document.createElement("video");
        vid.className = "npp-nulla-video";
        vid.controls = true;
        vid.playsInline = true;
        const blob = blobs[i];
        let videoUrl: string | null = null;
        if (blob) {
          videoUrl = URL.createObjectURL(blob);
          vid.src = videoUrl;
        }
        wrap.appendChild(vid);

        let voiceUrl: string | null = null;
        const hex = s.meta.voice?.hex64;
        if (hex) {
          const bytes = hex64ToBytes(hex);
          if (bytes && bytes.length > 0) {
            const payload = Uint8Array.from(bytes);
            const ab = new Blob([payload], { type: s.meta.voice!.mime || "audio/webm" });
            voiceUrl = URL.createObjectURL(ab);
            const aud = document.createElement("audio");
            aud.className = "npp-nulla-mic-track-audio";
            aud.controls = true;
            aud.src = voiceUrl;
            wrap.appendChild(aud);
          }
        }

        this.attachNullaClipRemoveControl(wrap, i);
        els.push(wrap);
        urls.push(videoUrl);
        voiceUrls.push(voiceUrl);
      }
    }

    this.nullaSectionEls = els;
    this.nullaSectionObjectUrls = urls;
    this.nullaSectionVoiceObjectUrls = voiceUrls;
    const container = this.nullaSectionsContainer;
    container.innerHTML = "";
    for (const el of els) container.appendChild(el);
    container.hidden = false;
  }

  /**
   * @param abortClipMic When true (default), cancel an in-progress Nulla mic-track capture — use when
   *   leaving Nulla video view. When false, only revoke blob URLs and clear the section DOM (e.g. before rebuild).
   */
  private teardownNullaSections(options?: { abortClipMic?: boolean }): void {
    const abortClipMic = options?.abortClipMic !== false;
    if (
      abortClipMic &&
      this.nullaMicTrackRecorder &&
      this.nullaMicTrackRecorder.state !== "inactive"
    ) {
      this.nullaMicTrackDiscard = true;
      this.nullaMicTrackRecorder.stop();
    }
    for (const url of this.nullaSectionObjectUrls) {
      if (url) URL.revokeObjectURL(url);
    }
    for (const url of this.nullaSectionVoiceObjectUrls) {
      if (url) URL.revokeObjectURL(url);
    }
    this.nullaSectionObjectUrls = [];
    this.nullaSectionVoiceObjectUrls = [];
    this.nullaSectionEls = [];
    this.nullaSectionsContainer.innerHTML = "";
    this.nullaSectionsContainer.hidden = true;
  }

  private updateSelectionVisualMode(): void {
    const nonCollapsed =
      document.activeElement === this.editor && this.editor.selectionStart !== this.editor.selectionEnd;
    this.editorHost.classList.toggle("npp-selection-native-text", nonCollapsed);
  }

  /** When the textarea shows a non-collapsed range, use opaque text so the native selection tint is visible. */
  private syncNativeTextSelectionVisualFor(ta: HTMLTextAreaElement): void {
    const host =
      ta === this.editor ? this.editorHost : this.multiviewExtras.find((ex) => ex.editor === ta)?.editorHost;
    if (!host) return;
    if (document.activeElement !== ta) return;
    const nonCollapsed = ta.selectionStart !== ta.selectionEnd;
    host.classList.toggle("npp-selection-native-text", nonCollapsed);
  }

  private armFocusedEditorSyncLoop(): void {
    if (document.activeElement !== this.editor) return;
    if (this.editorFocusSyncRaf !== 0) return;
    const tick = (): void => {
      this.editorFocusSyncRaf = 0;
      if (document.activeElement !== this.editor) return;
      this.syncHighlightScroll();
      const st = this.editor.scrollTop;
      const sl = this.editor.scrollLeft;
      if (st !== this.lastFocusSyncScrollTop || sl !== this.lastFocusSyncScrollLeft) {
        this.lastFocusSyncScrollTop = st;
        this.lastFocusSyncScrollLeft = sl;
        this.updateLineNumberGutter();
        this.updateActiveLineHighlight();
      }
      this.editorFocusSyncRaf = requestAnimationFrame(tick);
    };
    this.editorFocusSyncRaf = requestAnimationFrame(tick);
  }

  /**
   * Rasterizes the visible preview or editor surface (html2canvas) and downloads a PDF — no extra tab.
   */
  private async exportActiveRenderedToPdf(): Promise<void> {
    this.flushEditorToBuffer();
    const d = this.documents.active;
    if (!d) return;
    const stem = d.filePath ?? d.title;

    if (d.viewMode === "video") {
      void alert("PDF export is not available for legacy video-style tabs.");
      return;
    }

    try {
      if (d.viewMode === "image") {
        const ok = await openExportPdfPreviewDialog({ element: this.binaryPreviewImg, filenameStem: stem });
        if (ok) this.updateStatusLine("PDF saved — image preview");
        return;
      }

      if (d.viewMode === "pdf") {
        const ok = await openExportPdfPreviewDialog({ element: this.binaryPreviewPdf, filenameStem: stem });
        if (ok) this.updateStatusLine("PDF saved — PDF preview");
        return;
      }

      if (d.viewMode === "diff" && d.diffPair) {
        const inner =
          this.diffEditorInner.querySelector<HTMLElement>(".npp-diff-split-scroll") ?? this.diffEditorInner;
        const ok = await openExportPdfPreviewDialog({ element: inner, filenameStem: stem });
        if (ok) this.updateStatusLine("PDF saved — compare view");
        return;
      }

      if (d.viewMode !== "text") return;

      if (d.language === "md") {
        if (this.mdPreviewTimer) {
          clearTimeout(this.mdPreviewTimer);
          this.mdPreviewTimer = null;
        }
        if (!this.mdMarkdownShowSource) {
          this.mdPreviewBody.innerHTML = this.markdownPreviewHtmlFromSource(d.content);
          const ok = await openExportPdfPreviewDialog({ element: this.mdPreviewBody, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — Markdown");
        } else {
          this.updateSyntaxHighlightLayer();
          const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — Markdown");
        }
        return;
      }

      if (d.language === "tex") {
        if (this.texPreviewTimer) {
          clearTimeout(this.texPreviewTimer);
          this.texPreviewTimer = null;
        }
        if (!this.texShowSource) {
          this.texPreviewBody.innerHTML = buildTexPreviewHtml(d.content);
          renderTexMathInPreviewRoot(this.texPreviewBody);
          const ok = await openExportPdfPreviewDialog({ element: this.texPreviewBody, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — TeX");
        } else {
          this.updateSyntaxHighlightLayer();
          const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — TeX");
        }
        return;
      }

      if (d.language === "max") {
        if (this.maxPreviewTimer) {
          clearTimeout(this.maxPreviewTimer);
          this.maxPreviewTimer = null;
        }
        if (!this.maxShowSource) {
          revokeMaxHexMediaBlobUrls(this.maxPreviewBody);
          this.maxPreviewBody.innerHTML = buildMaxPreviewHtmlAnnotated(d.content);
          hydrateMaxPreviewRoot(this.maxPreviewBody);
          const ok = await openExportPdfPreviewDialog({ element: this.maxPreviewBody, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — Max");
        } else {
          this.updateSyntaxHighlightLayer();
          const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — Max");
        }
        return;
      }

      if (d.language === "html") {
        if (this.htmlPreviewTimer) {
          clearTimeout(this.htmlPreviewTimer);
          this.htmlPreviewTimer = null;
        }
        if (!this.htmlShowSource) {
          this.htmlPreviewBody.srcdoc = buildBrowserLikePreviewSrcdoc(d.content);
          await new Promise<void>((r) =>
            requestAnimationFrame(() => requestAnimationFrame(() => r())),
          );
          const ok = await openExportPdfPreviewDialog({
            element: htmlPreviewPdfCaptureRoot(this.htmlPreviewBody),
            filenameStem: stem,
          });
          if (ok) this.updateStatusLine("PDF saved — HTML");
        } else {
          this.updateSyntaxHighlightLayer();
          const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — HTML");
        }
        return;
      }

      if (d.language === "oeis") {
        if (!this.oeisShowSource && this.oeisPreviewBody.innerHTML.trim()) {
          const ok = await openExportPdfPreviewDialog({ element: this.oeisPreviewBody, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — OEIS");
        } else {
          this.updateSyntaxHighlightLayer();
          const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
          if (ok) this.updateStatusLine("PDF saved — OEIS");
        }
        return;
      }

      if (this.isSpreadsheetLanguage(d.language) && !this.csvShowSource) {
        this.ensureCsvGrid();
        const tableSrc =
          d.language === "excel"
            ? activeSheetCsvFromWorkbookJson(d.content, d.eol)
            : d.content;
        this.csvGridApi?.syncFromText(tableSrc);
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        const scroll = this.csvTableHost.closest(".npp-csv-table-scroll") as HTMLElement | null;
        const ok = await openExportPdfPreviewDialog({ element: scroll ?? this.csvTableHost, filenameStem: stem });
        if (ok) this.updateStatusLine(d.language === "excel" ? "PDF saved — Excel table" : "PDF saved — CSV table");
        return;
      }

      this.updateSyntaxHighlightLayer();
      const ok = await openExportPdfPreviewDialog({ element: this.editorHost, filenameStem: stem });
      if (ok) this.updateStatusLine("PDF saved");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      void alert(`Could not export PDF (${msg}).`);
      this.updateStatusLine("PDF export failed");
    }
  }

  private async exportActiveMaxRenderedMarkdown(): Promise<void> {
    this.flushEditorToBuffer();
    const d = this.documents.active;
    if (!d) return;
    if (d.viewMode !== "text") {
      this.updateStatusLine("Rendered Markdown export is only available for text tabs.");
      return;
    }
    if (d.language !== "max") {
      this.updateStatusLine("Rendered Markdown export is available only for Max tabs.");
      return;
    }
    const stem = d.filePath ?? d.title;
    const rendered = buildMaxRenderedMarkdown(d.content);
    const safeName = sanitizePdfFilename(stem);
    const blob = new Blob([rendered], { type: "text/markdown;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${safeName}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.updateStatusLine("Markdown saved — Max rendered output");
  }

  private disposeDiffView(): void {
    this.diffViewDispose?.();
    this.diffViewDispose = null;
  }

  private mountActiveDiffTab(doc: TabDocument): void {
    const pair = doc.diffPair;
    if (!pair) return;
    const docId = doc.id;

    if (doc.diffSplitView) {
      const dispose = mountDiffSplitPanes(this.diffEditorInner, pair);
      if (this.documents.active?.id !== docId || this.documents.active.viewMode !== "diff") {
        dispose();
        return;
      }
      this.diffViewDispose = dispose;
      return;
    }

    void (async () => {
      try {
        const { mountMonacoDiffEditor } = await import("./ui/diffTabMonaco.js");
        if (this.documents.active?.id !== docId || this.documents.active.viewMode !== "diff") {
          return;
        }
        const dispose = await mountMonacoDiffEditor(this.diffEditorInner, pair);
        if (this.documents.active?.id !== docId || this.documents.active.viewMode !== "diff") {
          dispose();
          return;
        }
        this.diffViewDispose = dispose;
      } catch {
        this.updateStatusLine("Could not open Monaco diff editor");
      }
    })();
  }

  private onDiffPickLeft(index: number): void {
    const doc = this.documents.documents[index];
    if (!doc || doc.viewMode !== "text") return;
    this.flushEditorToBuffer();
    this.diffPickLeftIndex = index;
    this.updateStatusLine(`Compare: left = ${doc.title}`);
    this.tryOpenDiffFromPicks();
  }

  private onDiffPickRight(index: number): void {
    const doc = this.documents.documents[index];
    if (!doc || doc.viewMode !== "text") return;
    this.flushEditorToBuffer();
    this.diffPickRightIndex = index;
    this.updateStatusLine(`Compare: right = ${doc.title}`);
    this.tryOpenDiffFromPicks();
  }

  private tryOpenDiffFromPicks(): void {
    if (this.diffPickLeftIndex === null || this.diffPickRightIndex === null) return;
    if (this.diffPickLeftIndex === this.diffPickRightIndex) {
      this.updateStatusLine("Compare: pick two different text tabs for left and right.");
      return;
    }
    const left = this.documents.documents[this.diffPickLeftIndex];
    const right = this.documents.documents[this.diffPickRightIndex];
    if (!left || !right || left.viewMode !== "text" || right.viewMode !== "text") return;
    const monacoLang =
      left.language === right.language ? monacoLanguageId(left.language) : "plaintext";
    const highlightLanguage: LanguageMode =
      left.language === right.language ? left.language : "text";
    this.documents.addDiffTab({
      leftLabel: left.filePath ?? left.title,
      rightLabel: right.filePath ?? right.title,
      leftText: left.content,
      rightText: right.content,
      monacoLanguage: monacoLang,
      highlightLanguage,
    });
    this.diffPickLeftIndex = null;
    this.diffPickRightIndex = null;
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateStatusLine("Compare tab opened (Monaco, synced scroll)");
  }

  private onDiffTabMonacoLayout(index: number): void {
    this.documents.setDiffTabSplitView(index, false);
    if (this.documents.activeIndex === index) {
      this.loadActiveIntoEditor();
    }
    this.tabBar?.refresh();
    this.updateStatusLine("Compare: Monaco diff editor");
  }

  private onDiffTabSplitPanesLayout(index: number): void {
    this.documents.setDiffTabSplitView(index, true);
    if (this.documents.activeIndex === index) {
      this.loadActiveIntoEditor();
    }
    this.tabBar?.refresh();
    this.updateStatusLine("Compare: two-pane split (aligned rows, highlighted changes)");
  }

  private bumpLineCountEpoch(): void {
    this.lineCountEpoch++;
  }

  private getCachedTotalLogicalLines(text: string): number {
    const e = this.lineCountEpoch;
    if (this.cachedTotalLogicalLines?.epoch === e) {
      return this.cachedTotalLogicalLines.lines;
    }
    const lines = countLogicalLines(text);
    this.cachedTotalLogicalLines = { epoch: e, lines };
    return lines;
  }

  private textareaUsesWordWrap(ta: HTMLTextAreaElement): boolean {
    const cs = getComputedStyle(ta);
    const ws = ta.style.whiteSpace || cs.whiteSpace;
    return ws.includes("pre-wrap") || ws === "break-spaces";
  }

  private editorUsesWordWrap(): boolean {
    return this.textareaUsesWordWrap(this.editor);
  }

  /** Stable key for when line-number `<pre>` text must be recomputed (wrap expands rows). */
  private lineNumberGutterLayoutKey(ta: HTMLTextAreaElement, text: string, multiviewSlot: "primary" | number): string {
    const totalLines = Math.max(1, this.getCachedTotalLogicalLines(text));
    const cs = getComputedStyle(ta);
    const fs = ta.style.fontSize || cs.fontSize || "";
    const ws = ta.style.whiteSpace || cs.whiteSpace || "";
    const cw = ta.clientWidth;
    const slotTag = multiviewSlot === "primary" ? "p" : `x${multiviewSlot}`;
    if (this.gutterMode === "datetime") {
      // Datetime gutter content depends on marks (markupLayerEpoch) and wrap layout
      if (!this.textareaUsesWordWrap(ta)) {
        return `dt:${slotTag}:${this.markupLayerEpoch}:${totalLines}`;
      }
      return `dt:${slotTag}:${this.markupLayerEpoch}:${this.lineCountEpoch}:${text.length}:${totalLines}:${cw}:${fs}:${ws}`;
    }
    if (!this.textareaUsesWordWrap(ta)) {
      return `n:${slotTag}:${totalLines}`;
    }
    return `w:${slotTag}:${this.lineCountEpoch}:${text.length}:${totalLines}:${cw}:${fs}:${ws}`;
  }

  private buildLineNumberPreContent(ta: HTMLTextAreaElement, text: string): string {
    const lines = Math.max(1, this.getCachedTotalLogicalLines(text));
    if (!this.textareaUsesWordWrap(ta)) {
      const rows: string[] = new Array(lines);
      for (let i = 0; i < lines; i++) rows[i] = String(i + 1);
      return rows.join("\n");
    }
    const counts = textareaLogicalLineWrapRowCounts(ta, text);
    const parts: string[] = [];
    for (let i = 0; i < counts.length; i++) {
      const n = counts[i]!;
      parts.push(String(i + 1));
      for (let k = 1; k < n; k++) parts.push("");
    }
    return parts.join("\n");
  }

  /** Scroll position used for gutter / active-line math (clamped to real scroll range). */
  private editorLayoutScrollTop(): number {
    const ed = this.editor;
    return clampEditorScrollTop(ed.scrollTop, ed.scrollHeight, ed.clientHeight);
  }

  private editorLineUniformModel(): EditorLineUniformModel {
    const cs = getComputedStyle(this.editor);
    const text = this.editor.value;
    const lines = Math.max(1, this.getCachedTotalLogicalLines(text));
    return buildEditorLineUniformModel({
      scrollHeight: this.editor.scrollHeight,
      paddingTopPx: paddingTopPx(cs),
      paddingBottomPx: paddingBottomPx(cs),
      logicalLineCount: lines,
      resolvedLineHeightPx: resolvedLineHeightPx(cs),
      clientHeight: this.editor.clientHeight,
    });
  }

  /** Scroll so the line containing `startOffset` is visible (wrap-aware). */
  private scrollEditorToLineContainingOffset(startOffset: number): void {
    const ed = this.editor;
    if (this.editorUsesWordWrap()) {
      const y = textareaCaretLineTopFromContentTop(ed, startOffset);
      ed.scrollTop = Math.max(0, y - ed.clientHeight * 0.35);
    } else {
      const text = ed.value;
      const lineIndex = lineIndex0AtOffset(text, startOffset);
      ed.scrollTop = scrollTopToShowLogicalLineFromModel({
        lineIndex0: lineIndex,
        clientHeight: ed.clientHeight,
        model: this.editorLineUniformModel(),
      });
    }
    this.updateLineNumberGutter();
    this.updateActiveLineHighlight();
  }

  /**
   * Touch / mobile: textarea `scrollTop` and caret geometry often settle one frame after
   * `setSelectionRange`; the editor can also sit under the on-screen keyboard until the
   * scroll container is brought into the visual viewport.
   */
  private editorEnsureVisibleOnCoarsePointer(caretOffset: number): void {
    if (!prefersCoarsePointer()) return;
    requestAnimationFrame(() => {
      this.scrollEditorToLineContainingOffset(caretOffset);
      this.editorHost.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
      requestAnimationFrame(() => {
        this.scrollEditorToLineContainingOffset(caretOffset);
      });
    });
  }

  private shouldRevealFindInPreviewOnly(): boolean {
    const d = this.documents.active;
    if (!d || d.viewMode !== "text") return false;
    const lang = d.language;
    if (lang === "md") return !this.mdMarkdownShowSource;
    if (lang === "tex") return !this.texShowSource;
    if (lang === "max") return !this.maxShowSource;
    if (lang === "html") return !this.htmlShowSource;
    if (lang === "oeis") return !this.oeisShowSource;
    if (this.isSpreadsheetLanguage(lang)) return !this.csvShowSource;
    return false;
  }

  private previewFindLineScrollFraction(text: string, startOffset: number): number {
    const lines = Math.max(1, countLogicalLines(text));
    const line0 = lineIndex0AtOffset(text, startOffset);
    if (lines <= 1) return 0;
    return line0 / (lines - 1);
  }

  /**
   * Scroll the active preview pane so the rendered content for the current editor caret line is
   * visible. Runs only in split-view mode (editor and preview both showing).
   *
   * - Markdown: uses `data-source-line` attributes for accurate element-level sync.
   * - All other preview languages: proportional scroll (line index / total lines).
   */
  private syncPreviewToEditorCaret(): void {
    // Skip this cycle when a reverse (preview → editor) sync just fired; the editor caret position
    // may not yet reflect the offset written by onPreviewClick (race between the setSelectionRange
    // call and the next RAF), so re-running the forward sync here would scroll the preview back to
    // what the editor thinks is the current line (often the top of the document).
    if (this.suppressNextPreviewSync) {
      this.suppressNextPreviewSync = false;
      return;
    }
    const d = this.documents.active;
    if (!d || d.viewMode !== "text") return;
    const lang = d.language;

    // Only active in split-view mode where both editor and preview are visible.
    const inSplitView =
      (lang === "md" && this.mdMarkdownShowSource) ||
      (lang === "tex" && this.texShowSource) ||
      (lang === "max" && this.maxShowSource) ||
      (lang === "html" && this.htmlShowSource) ||
      (lang === "oeis" && this.oeisShowSource) ||
      (this.isSpreadsheetLanguage(lang) && this.csvShowSource);
    if (!inSplitView) return;

    const text = this.editor.value;
    const caret = Math.max(0, Math.min(this.activeCaretOffset(), text.length));
    const lineIndex0 = lineIndex0AtOffset(text, caret);

    if (lang === "md" || lang === "max") {
      // Accurate: find the last annotated element whose source line ≤ caret line.
      const host = lang === "md" ? this.mdPreviewBody : this.maxPreviewBody;
      const annotated = host.querySelectorAll<HTMLElement>("[data-source-line]");
      if (annotated.length === 0) return;
      let target: HTMLElement = annotated[0]!;
      for (const el of Array.from(annotated)) {
        const elLine = parseInt(el.dataset.sourceLine ?? "0", 10);
        if (elLine <= lineIndex0) {
          target = el;
        } else {
          break;
        }
      }
      // Scroll the preview body (not the page) so the target is near the top.
      const hostRect = host.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const offset = targetRect.top - hostRect.top + host.scrollTop;
      host.scrollTop = Math.max(0, offset - 24);
    } else {
      // Proportional: map caret line fraction to preview scroll position.
      const totalLines = Math.max(1, countLogicalLines(text));
      const frac = totalLines <= 1 ? 0 : lineIndex0 / (totalLines - 1);
      let host: HTMLElement | null = null;
      if (lang === "tex") host = this.texPreviewBody;
      else if (lang === "oeis") host = this.oeisPreviewBody;
      else if (this.isSpreadsheetLanguage(lang)) host = this.csvPreview;
      if (!host) return;
      const maxScroll = Math.max(0, host.scrollHeight - host.clientHeight);
      host.scrollTop = frac * maxScroll;
    }
  }

  /**
   * Preview-pane click → move editor caret and scroll to the corresponding source line.
   *
   * - Markdown: walks up the click target to find the nearest `[data-source-line]` ancestor.
   * - Other languages: maps the click's Y position proportionally to the source text.
   *
   * Skips when the click target is inside an `<a>` element so normal link navigation works.
   */
  private onPreviewClick(e: MouseEvent, lang: string, host: HTMLElement): void {
    // Only active in split-view mode.
    const inSplitView =
      (lang === "md" && this.mdMarkdownShowSource) ||
      (lang === "tex" && this.texShowSource) ||
      (lang === "max" && this.maxShowSource) ||
      (lang === "oeis" && this.oeisShowSource);
    if (!inSplitView) return;

    // Don't interfere with link clicks.
    const target = e.target as HTMLElement | null;
    if (target?.closest("a")) return;

    const text = this.editor.value;
    if (!text) return;

    let offset: number;

    if (lang === "md" || lang === "max") {
      // Walk up from the click target to find the nearest annotated ancestor.
      let el: HTMLElement | null = target;
      let sourceLine: number | null = null;
      while (el && el !== host) {
        const attr = el.dataset.sourceLine;
        if (attr !== undefined) {
          sourceLine = parseInt(attr, 10);
          break;
        }
        el = el.parentElement;
      }
      if (sourceLine === null) return;
      offset = offsetAtLineIndex0(text, sourceLine);
    } else {
      // Proportional: map click Y position to a text offset.
      const hostRect = host.getBoundingClientRect();
      const clickY = e.clientY - hostRect.top + host.scrollTop;
      const frac = Math.max(0, Math.min(1, clickY / Math.max(1, host.scrollHeight)));
      offset = Math.floor(frac * text.length);
    }

    offset = Math.max(0, Math.min(offset, text.length));
    // Set the flag before setSelectionRange so it is already in place when selectionchange fires.
    this.suppressNextPreviewSync = true;
    this.editor.setSelectionRange(offset, offset);
    this.scrollEditorToLineContainingOffset(offset);
    // scrollEditorToLineContainingOffset sets editor.scrollTop which queues a scrollCoalesceRaf.
    // Cancel it so the forward sync doesn't immediately re-scroll the preview back to the top of
    // the clicked element.  We reschedule the non-sync work the RAF would have done.
    if (this.scrollCoalesceRaf) {
      cancelAnimationFrame(this.scrollCoalesceRaf);
      this.scrollCoalesceRaf = 0;
      requestAnimationFrame(() => {
        this.updateActiveLineHighlight();
        this.updateLineNumberGutter();
      });
    }
  }

  private flashPreviewFindHost(host: HTMLElement): void {
    host.classList.remove("npp-preview-find-flash");
    void host.offsetWidth;
    host.classList.add("npp-preview-find-flash");
    const cleanup = (): void => {
      host.classList.remove("npp-preview-find-flash");
      host.removeEventListener("animationend", cleanup);
    };
    host.addEventListener("animationend", cleanup);
    window.setTimeout(cleanup, 1000);
  }

  private static readonly maxPreviewFindNeedleLen = 256;
  private static readonly maxPreviewFindHighlightHits = 400;

  private clearPreviewFindHighlightsInRoot(root: ParentNode): void {
    const base = root instanceof Document ? root.body : (root as Element);
    if (!base) return;
    for (const m of Array.from(base.querySelectorAll("mark.npp-preview-find-hit"))) {
      const parent = m.parentNode;
      if (!parent) continue;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
    }
    base.normalize();
  }

  private clearAllPreviewFindHighlights(): void {
    for (const host of [this.mdPreviewBody, this.texPreviewBody, this.maxPreviewBody, this.oeisPreviewBody, this.csvPreview]) {
      this.clearPreviewFindHighlightsInRoot(host);
    }
    try {
      const b = this.htmlPreviewBody.contentDocument?.body;
      if (b) this.clearPreviewFindHighlightsInRoot(b);
    } catch {
      /* ignore */
    }
  }

  /** Resolve find options for highlighting the current match in preview DOM. */
  private previewFindHighlightSpec(start: number, end: number): {
    needle: string;
    caseSensitive: boolean;
    wholeWord: boolean;
  } | null {
    const slice = this.editor.value.slice(start, end);
    if (!slice || slice.length > AppShell.maxPreviewFindNeedleLen) return null;
    const text = this.editor.value;
    const sess = getFindSession();
    if (
      sess.needle &&
      sess.needle.length === slice.length &&
      findNextIndex(text, sess.needle, start, sess.caseSensitive, sess.wholeWord) === start
    ) {
      return { needle: sess.needle, caseSensitive: sess.caseSensitive, wholeWord: sess.wholeWord };
    }
    return { needle: slice, caseSensitive: true, wholeWord: false };
  }

  private collectPreviewHighlightTextNodes(root: ParentNode): Text[] {
    const doc = root instanceof Document ? root : root.ownerDocument;
    if (!doc) return [];
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const p = (node as Text).parentElement;
        if (!p) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName;
        if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return NodeFilter.FILTER_REJECT;
        if (p.closest("mark.npp-preview-find-hit")) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const out: Text[] = [];
    let n: Node | null;
    let guard = 0;
    const maxNodes = 8000;
    while ((n = walker.nextNode()) && guard++ < maxNodes) {
      out.push(n as Text);
    }
    return out;
  }

  private wrapFindNeedleInTextNode(
    textNode: Text,
    needle: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    budget: number,
  ): number {
    if (!needle || budget <= 0) return 0;
    const full = textNode.data;
    const doc = textNode.ownerDocument;
    if (!doc) return 0;
    const frag = doc.createDocumentFragment();
    let pos = 0;
    let hits = 0;
    while (pos <= full.length - needle.length && hits < budget) {
      const i = findNextIndex(full, needle, pos, caseSensitive, wholeWord);
      if (i < 0) break;
      if (i > pos) frag.appendChild(doc.createTextNode(full.slice(pos, i)));
      const mark = doc.createElement("mark");
      mark.className = "npp-preview-find-hit";
      mark.appendChild(doc.createTextNode(full.slice(i, i + needle.length)));
      frag.appendChild(mark);
      pos = i + Math.max(1, needle.length);
      hits++;
    }
    if (hits === 0) return 0;
    if (pos < full.length) frag.appendChild(doc.createTextNode(full.slice(pos)));
    const parent = textNode.parentNode;
    if (!parent) return 0;
    parent.replaceChild(frag, textNode);
    return hits;
  }

  private applyFindHighlightsInPreviewRoot(
    root: ParentNode,
    needle: string,
    caseSensitive: boolean,
    wholeWord: boolean,
  ): void {
    const nodes = this.collectPreviewHighlightTextNodes(root);
    let budget = AppShell.maxPreviewFindHighlightHits;
    for (const tn of nodes) {
      if (budget <= 0) break;
      if (!tn.isConnected) continue;
      budget -= this.wrapFindNeedleInTextNode(tn, needle, caseSensitive, wholeWord, budget);
    }
  }

  private ensureIframePreviewFindHitStyle(doc: Document): void {
    if (doc.getElementById("npp-preview-find-hit-style")) return;
    const st = doc.createElement("style");
    st.id = "npp-preview-find-hit-style";
    st.textContent =
      "mark.npp-preview-find-hit{background:rgba(255,214,10,0.42);color:inherit;padding:0 0.06em;border-radius:2px;box-decoration-break:clone;-webkit-box-decoration-break:clone;}";
    doc.head?.appendChild(st);
  }

  /**
   * Preview-only tabs: approximate the source line of `startOffset` in the visible preview
   * scroll area (line-proportional), focus that surface for F3 / incremental find, and flash.
   */
  private scrollActivePreviewForFind(startOffset: number, findEnd: number): void {
    const d = this.documents.active;
    if (!d) return;
    this.clearAllPreviewFindHighlights();
    const text = this.editor.value;
    const frac = this.previewFindLineScrollFraction(text, startOffset);
    const spec = this.previewFindHighlightSpec(startOffset, findEnd);

    const applyScrollAndFocus = (host: HTMLElement): void => {
      const maxScroll = Math.max(0, host.scrollHeight - host.clientHeight);
      host.scrollTop = frac * maxScroll;
      host.focus({ preventScroll: true });
      this.flashPreviewFindHost(host);
      if (prefersCoarsePointer()) {
        requestAnimationFrame(() => {
          host.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
        });
      }
    };

    const lang = d.language;
    if (lang === "md") {
      applyScrollAndFocus(this.mdPreviewBody);
      if (spec) this.applyFindHighlightsInPreviewRoot(this.mdPreviewBody, spec.needle, spec.caseSensitive, spec.wholeWord);
    } else if (lang === "tex") {
      applyScrollAndFocus(this.texPreviewBody);
      if (spec) this.applyFindHighlightsInPreviewRoot(this.texPreviewBody, spec.needle, spec.caseSensitive, spec.wholeWord);
    } else if (lang === "max") {
      applyScrollAndFocus(this.maxPreviewBody);
      if (spec) this.applyFindHighlightsInPreviewRoot(this.maxPreviewBody, spec.needle, spec.caseSensitive, spec.wholeWord);
    } else if (lang === "html") {
      const iframe = this.htmlPreviewBody;
      try {
        const doc = iframe.contentDocument;
        const root = doc?.documentElement;
        if (root && doc) {
          const body = doc.body;
          const sh = Math.max(root.scrollHeight, body?.scrollHeight ?? 0);
          const ch = iframe.clientHeight;
          const maxScroll = Math.max(0, sh - ch);
          const top = frac * maxScroll;
          root.scrollTop = top;
          if (body) body.scrollTop = top;
        }
      } catch {
        // Cross-origin or unavailable document — still focus the iframe shell.
      }
      iframe.focus({ preventScroll: true });
      this.flashPreviewFindHost(iframe);
      if (prefersCoarsePointer()) {
        requestAnimationFrame(() => {
          iframe.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
        });
      }
      if (spec) {
        try {
          const idoc = iframe.contentDocument;
          const body = idoc?.body;
          if (body && idoc) {
            this.ensureIframePreviewFindHitStyle(idoc);
            this.applyFindHighlightsInPreviewRoot(body, spec.needle, spec.caseSensitive, spec.wholeWord);
          }
        } catch {
          /* ignore */
        }
      }
    } else if (lang === "oeis") {
      applyScrollAndFocus(this.oeisPreviewBody);
      if (spec) this.applyFindHighlightsInPreviewRoot(this.oeisPreviewBody, spec.needle, spec.caseSensitive, spec.wholeWord);
    } else if (this.isSpreadsheetLanguage(lang)) {
      applyScrollAndFocus(this.csvPreview);
      if (spec) this.applyFindHighlightsInPreviewRoot(this.csvPreview, spec.needle, spec.caseSensitive, spec.wholeWord);
    }
  }

  /** Focus editor, set selection, and scroll so the range at `start` is visible (wrap-aware). */
  private revealEditorSelection(start: number, end: number): void {
    const ed = this.editor;
    ed.setSelectionRange(start, end);
    if (this.shouldRevealFindInPreviewOnly()) {
      this.scrollActivePreviewForFind(start, end);
      return;
    }
    this.clearAllPreviewFindHighlights();
    ed.focus();
    this.scrollEditorToLineContainingOffset(start);
    this.editorEnsureVisibleOnCoarsePointer(start);
  }

  private markdownHeadingSlug(text: string): string {
    return text
      .toLowerCase()
      .replace(/<[^>]+>/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s/g, "-");
  }

  private findNearestRenderedMarkdownHeading(fragment: string): HTMLElement | null {
    let decoded = fragment;
    try {
      decoded = decodeURIComponent(fragment);
    } catch {
      // Keep raw fragment when it's not valid percent-encoding.
    }
    const target = this.markdownHeadingSlug(decoded);
    const headings = Array.from(this.mdPreviewBody.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6"));
    if (!headings.length) return null;
    const exact = headings.filter((h) => this.markdownHeadingSlug(h.textContent ?? "") === target);
    const pool = exact.length ? exact : headings.filter((h) => this.markdownHeadingSlug(h.textContent ?? "").includes(target));
    if (!pool.length) return null;
    const refY = this.mdPreviewBody.scrollTop + this.mdPreviewBody.clientHeight * 0.35;
    pool.sort((a, b) => Math.abs(a.offsetTop - refY) - Math.abs(b.offsetTop - refY));
    return pool[0] ?? null;
  }

  private onMarkdownPreviewClick(e: MouseEvent): void {
    const d = this.documents.active;
    if (!d || d.language !== "md") return;
    const targetNode = e.target;
    if (!(targetNode instanceof Element)) return;
    const anchor = targetNode.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;

    let fragment = "";
    if (href.startsWith("#")) {
      fragment = href.slice(1);
    } else {
      try {
        const parsed = new URL(href, window.location.href);
        fragment = parsed.hash.startsWith("#") ? parsed.hash.slice(1) : "";
      } catch {
        fragment = "";
      }
    }
    if (fragment) {
      e.preventDefault();
      this.mdPreviewPreviousScrollTop = this.mdPreviewBody.scrollTop;
      this.mdBackToPreviousBtn.disabled = false;
      const heading = this.findNearestRenderedMarkdownHeading(fragment);
      if (!heading) {
        this.updateStatusLine(`Heading not found for "${href}"`);
        return;
      }
      heading.scrollIntoView({ behavior: "smooth", block: "start" });
      if (!heading.hasAttribute("tabindex")) heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      this.updateStatusLine(`Jumped in preview: ${decodeURIComponent(fragment)}`);
      return;
    }

    if (anchor.classList.contains("npp-oeis-external-browser")) return;

    try {
      const absUrl = new URL(href, window.location.href);
      if (absUrl.protocol === "http:" || absUrl.protocol === "https:") {
        if (previewHyperlinkOpensHtmlInExternalBrowser(absUrl.href)) {
          e.preventDefault();
          window.open(absUrl.href, "_blank", "noopener,noreferrer");
          return;
        }
        e.preventDefault();
        void this.openPreviewHyperlinkInNewTab(absUrl.href);
      }
    } catch {
      /* ignore non-URL href */
    }
  }

  private onMarkdownPreviewBackClick(): void {
    if (this.mdPreviewPreviousScrollTop == null) return;
    this.mdPreviewBody.scrollTo({ top: this.mdPreviewPreviousScrollTop, behavior: "smooth" });
    this.mdPreviewBody.focus({ preventScroll: true });
    this.mdPreviewPreviousScrollTop = null;
    this.mdBackToPreviousBtn.disabled = true;
    this.updateStatusLine("Returned to previous preview position");
  }

  private positionActiveLineHighlight(lineIndex0: number): void {
    if (this.activeLineDiv) {
      this.activeLineDiv.classList.remove("hl-active");
    }
    const lineDiv = this.highlightCode.children[lineIndex0] as HTMLDivElement | undefined;
    if (lineDiv) {
      lineDiv.classList.add("hl-active");
    }
    this.activeLineDiv = lineDiv ?? null;
  }

  private replaceMisspelledWordFromContext(nextWord: string): void {
    const ta = this.editorContextTargetTextarea;
    const miss = this.editorContextMisspelledRange;
    if (!ta || !miss) return;
    const before = ta.value.slice(0, miss.start);
    const after = ta.value.slice(miss.end);
    const replacement =
      nextWord.length > 1 ? nextWord[0]!.toUpperCase() + nextWord.slice(1) : nextWord;
    ta.value = `${before}${replacement}${after}`;
    const caret = miss.start + replacement.length;
    ta.setSelectionRange(caret, caret);
    if (ta === this.editor) this.onEditorInput();
    else {
      const slot = this.multiviewExtras.findIndex((x) => x.editor === ta);
      if (slot >= 0) this.onMultiviewExtraEditorInput(slot);
    }
    ta.focus();
    this.hideEditorContextMenu();
  }

  private addSpellingExceptionFromContext(): void {
    const miss = this.editorContextMisspelledRange;
    if (!miss) return;
    const normalized = normalizeSpellcheckExceptionWord(miss.word);
    if (!normalized || this.spellcheckExceptions.includes(normalized)) return;
    this.spellcheckExceptions = [...this.spellcheckExceptions, normalized].sort();
    setSpellcheckExceptionWords(this.spellcheckExceptions);
    const prev = loadWebPrefs();
    saveWebPrefs({ ...prev, spellcheckExceptions: [...this.spellcheckExceptions] });
    this.scheduleSurfaceRefresh();
    this.hideEditorContextMenu();
    this.updateStatusLine(`Added "${normalized}" to spelling exceptions.`);
  }

  private rebuildContextSpellingSuggestionMenu(): void {
    const cluster = this.editorContextSpellSuggestCluster;
    cluster.innerHTML = "";
    this.editorContextMisspelledRange = null;
    this.editorContextSpellSuggestSep.hidden = true;
    cluster.hidden = true;
    const ta = this.editorContextTargetTextarea;
    if (!ta || !this.nativeSpellcheckEnabled) return;
    const at = wordRangeAt(ta.value, ta.selectionStart);
    if (!at || !isMisspelledWord(at.word)) return;
    this.editorContextMisspelledRange = at;
    const suggestions = suggestSpellings(at.word, 6);
    const mkBtn = (label: string, onClick: () => void): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "npp-editor-context-item";
      btn.setAttribute("role", "menuitem");
      btn.textContent = label;
      btn.addEventListener("pointerdown", (ev) => ev.stopPropagation());
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        onClick();
      });
      return btn;
    };
    if (suggestions.length) {
      for (const s of suggestions) cluster.appendChild(mkBtn(s, () => this.replaceMisspelledWordFromContext(s)));
    } else {
      const none = mkBtn("No suggestions", () => this.hideEditorContextMenu());
      none.disabled = true;
      cluster.appendChild(none);
    }
    const sep = document.createElement("div");
    sep.className = "npp-editor-context-sep";
    sep.setAttribute("role", "separator");
    cluster.appendChild(sep);
    cluster.appendChild(mkBtn(`Add "${at.word}" to spelling exceptions`, () => this.addSpellingExceptionFromContext()));
    this.editorContextSpellSuggestSep.hidden = false;
    cluster.hidden = false;
  }

  private showEditorContextMenu(clientX: number, clientY: number, surface: "editor" | "nulla" = "editor"): void {
    this.hideMaxRenderedContextMenu();
    if (surface !== "editor") this.editorContextTargetTextarea = null;
    const d = this.documents.active;
    this.editorContextSpellcheckToggleBtn.setAttribute(
      "aria-pressed",
      this.nativeSpellcheckEnabled ? "true" : "false",
    );
    const shareable = Boolean(d && (d.viewMode === "text" || d.viewMode === "video"));
    const textOn = surface === "editor";
    this.editorContextTextCluster.hidden = !textOn;
    const markupVisible = this.markupLayerVisible;
    this.markupToggleBtn.innerHTML = ctxIco(markupVisible ? P_EYE_OFF : P_EYE);
    const markupTitle = markupVisible ? "Hide markup" : "Show markup";
    this.markupToggleBtn.title = markupTitle;
    this.markupToggleBtn.setAttribute("aria-label", markupTitle);
    const gutterIsLines = this.gutterMode === "lines";
    this.gutterModeToggleBtn.innerHTML = ctxIco(gutterIsLines ? P_CLOCK : P_NUMBERS);
    const gutterTitle = gutterIsLines ? "Show timestamps in gutter" : "Show line numbers in gutter";
    this.gutterModeToggleBtn.title = gutterTitle;
    this.gutterModeToggleBtn.setAttribute("aria-label", gutterTitle);
    this.markupContextCluster.hidden = !textOn;
    this.editorContextShareBlock.hidden = !shareable;
    this.editorContextShareSep.hidden = !(textOn && shareable);
    if (textOn) {
      const ed = this.editor;
      const lo = Math.min(ed.selectionStart, ed.selectionEnd);
      const hi = Math.max(ed.selectionStart, ed.selectionEnd);
      this.editorContextBeautifyGeminiBtn.disabled = !ed.value.slice(lo, hi).trim();
      this.rebuildContextSpellingSuggestionMenu();
    } else {
      this.editorContextMisspelledRange = null;
      this.editorContextSpellSuggestCluster.hidden = true;
      this.editorContextSpellSuggestCluster.innerHTML = "";
      this.editorContextSpellSuggestSep.hidden = true;
    }
    if (shareable) {
      this.editorShareRecipientInput.value = getLastNullapadShareRecipientSecret();
      this.editorShareRecipientInput.type = "password";
      this.editorContextShareBlock.querySelector<HTMLButtonElement>(".npp-tab-context-share-eye")?.setAttribute(
        "aria-pressed",
        "false",
      );
    }
    this.editorContextMenu.style.visibility = "hidden";
    this.editorContextMenu.hidden = false;
    anchorContextMenu(this.editorContextMenu, clientX, clientY);
    this.editorContextMenu.style.visibility = "";
  }

  private hideEditorContextMenu(): void {
    this.editorContextMenu.hidden = true;
    this.editorContextTargetTextarea = null;
    this.editorContextMisspelledRange = null;
    this.editorContextSpellSuggestCluster.hidden = true;
    this.editorContextSpellSuggestCluster.innerHTML = "";
    this.editorContextSpellSuggestSep.hidden = true;
    saveLastNullapadShareRecipientSecret(this.editorShareRecipientInput.value);
    this.editorShareRecipientInput.value = getLastNullapadShareRecipientSecret();
    this.editorShareRecipientInput.type = "password";
    this.editorContextShareBlock
      .querySelector<HTMLButtonElement>(".npp-tab-context-share-eye")
      ?.setAttribute("aria-pressed", "false");
  }

  private showMaxRenderedContextMenu(clientX: number, clientY: number): void {
    this.hideEditorContextMenu();
    this.maxRenderedContextMenu.style.visibility = "hidden";
    this.maxRenderedContextMenu.hidden = false;
    anchorContextMenu(this.maxRenderedContextMenu, clientX, clientY);
    this.maxRenderedContextMenu.style.visibility = "";
  }

  private hideMaxRenderedContextMenu(): void {
    this.maxRenderedContextMenu.hidden = true;
  }

  private hideSessionRestorePlaceholderUi(): void {
    this.sessionRestorePanel.hidden = true;
    this.editorHost.classList.remove("npp-editor-host--session-restore");
  }

  private async onSessionRestorePlaceholderReload(): Promise<void> {
    this.flushEditorToBuffer();
    const fromRecent = await this.documents.replaceActiveSessionPlaceholderFromRecentByName();
    if (!fromRecent) {
      const ok = await this.documents.replaceActiveSessionPlaceholderFromPicker();
      if (!ok) return;
    }
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateSyntaxHighlightLayer();
    this.updateStatusLine();
    this.rebuildMenubar();
    this.persistTabSessionToStorage();
    this.syncDictationToolbarButton();
  }

  private loadActiveIntoEditor(): void {
    try {
      this.dismissRectSel();
      const d = this.documents.active;
      this.hideSessionRestorePlaceholderUi();
      this.editorHost.classList.remove("npp-selection-native-text");
    if (d?.viewMode !== "diff") {
      this.disposeDiffView();
    }

    if (d?.sessionRestorePlaceholder) {
      this.editorRow.classList.remove("npp-editor-row--diff-tab");
      this.editorRow.classList.remove(
        "npp-editor-row--binary-preview",
        "npp-editor-row--markdown",
        "npp-editor-row--tex",
        "npp-editor-row--max",
        "npp-editor-row--html",
        "npp-editor-row--oeis",
        "npp-editor-row--csv",
        "npp-md-preview-only",
        "npp-tex-preview-only",
        "npp-max-preview-only",
        "npp-html-preview-only",
        "npp-oeis-preview-only",
        "npp-csv-preview-only",
      );
      this.editorRow.classList.remove("npp-editor-row--nulla");
      this.mdMarkdownShowSource = false;
      this.texShowSource = false;
      this.maxShowSource = false;
      this.htmlShowSource = false;
      this.oeisShowSource = false;
      this.csvShowSource = false;
      this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image", "npp-binary-preview--video");
      this.binaryPreviewImg.removeAttribute("src");
      this.binaryPreviewPdf.removeAttribute("src");
      this.binaryPreviewVideo.removeAttribute("src");
      this.binaryPreviewVideo.hidden = true;
      this.teardownNullaSections();
      this.mdPreview.hidden = true;
      this.texPreview.hidden = true;
      this.maxPreview.hidden = true;
      this.htmlPreview.hidden = true;
      this.oeisPreview.hidden = true;
      this.csvPreview.hidden = true;
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();

      this.sessionRestorePanel.hidden = false;
      this.sessionRestoreFileTitle.textContent = d.filePath ?? d.title;
      this.editorHost.classList.add("npp-editor-host--session-restore");
      const kind = d.sessionRestorePlaceholder;
      const displayName = d.filePath ?? d.title;
      this.sessionRestoreReloadBtn.setAttribute(
        "aria-label",
        kind === "pdf"
          ? `Reload ${displayName}: try recent files by name, or choose a PDF`
          : `Reload ${displayName}: try recent files by name, or choose an image`,
      );

      this.editor.readOnly = true;
      this.editor.value = "";
      this.bumpLineCountEpoch();
      this.applyEditorLayoutForLanguage();
      this.applyZoomCss();
      this.updateSyntaxHighlightLayer();
      this.clearMarkdownPreview();
      this.clearTexPreview();
      this.clearMaxPreview();
      this.clearHtmlPreview();
      this.clearOeisPreview();
      this.destroyCsvGrid();
      this.updateLineNumberGutter();
      return;
    }

    if (d?.viewMode === "image" || d?.viewMode === "pdf") {
      this.editorRow.classList.remove("npp-editor-row--diff-tab");
      this.editorRow.classList.add("npp-editor-row--binary-preview");
      this.editorRow.classList.remove(
        "npp-editor-row--markdown",
        "npp-editor-row--tex",
        "npp-editor-row--max",
        "npp-editor-row--html",
        "npp-editor-row--oeis",
        "npp-editor-row--csv",
        "npp-md-preview-only",
        "npp-tex-preview-only",
        "npp-max-preview-only",
        "npp-html-preview-only",
        "npp-oeis-preview-only",
        "npp-csv-preview-only",
      );
      this.mdMarkdownShowSource = false;
      this.texShowSource = false;
      this.maxShowSource = false;
      this.htmlShowSource = false;
      this.oeisShowSource = false;
      this.csvShowSource = false;
      this.binaryPreview.classList.toggle("npp-binary-preview--pdf", d.viewMode === "pdf");
      this.binaryPreview.classList.toggle("npp-binary-preview--image", d.viewMode === "image");
      this.binaryPreview.classList.remove("npp-binary-preview--video");
      const url = d.previewObjectUrl ?? "";
      if (d.viewMode === "image") {
        this.binaryPreviewImg.src = url;
        this.binaryPreviewImg.alt = d.title;
        this.binaryPreviewPdf.removeAttribute("src");
      } else {
        this.binaryPreviewImg.removeAttribute("src");
        this.binaryPreviewPdf.src = url;
      }
      this.binaryPreviewVideo.removeAttribute("src");
      this.binaryPreviewVideo.hidden = true;
      this.teardownNullaSections();
      this.editor.value = "";
      this.clearMarkdownPreview();
      this.clearTexPreview();
      this.clearMaxPreview();
      this.clearHtmlPreview();
      this.clearOeisPreview();
      this.destroyCsvGrid();
      this.mdPreview.hidden = true;
      this.texPreview.hidden = true;
      this.maxPreview.hidden = true;
      this.htmlPreview.hidden = true;
      this.oeisPreview.hidden = true;
      this.csvPreview.hidden = true;
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();
      this.bumpLineCountEpoch();
      this.updateLineNumberGutter();
      return;
    }

    if (d?.viewMode === "video") {
      this.editorRow.classList.remove("npp-editor-row--diff-tab");
      this.editorRow.classList.add("npp-editor-row--binary-preview", "npp-editor-row--nulla");
      this.editorRow.classList.remove(
        "npp-editor-row--markdown",
        "npp-editor-row--tex",
        "npp-editor-row--max",
        "npp-editor-row--html",
        "npp-editor-row--oeis",
        "npp-editor-row--csv",
        "npp-md-preview-only",
        "npp-tex-preview-only",
        "npp-max-preview-only",
        "npp-html-preview-only",
        "npp-oeis-preview-only",
        "npp-csv-preview-only",
      );
      this.mdMarkdownShowSource = false;
      this.texShowSource = false;
      this.maxShowSource = false;
      this.htmlShowSource = false;
      this.oeisShowSource = false;
      this.csvShowSource = false;
      this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image");
      this.binaryPreview.classList.add("npp-binary-preview--video");
      this.binaryPreviewImg.removeAttribute("src");
      this.binaryPreviewPdf.removeAttribute("src");
      this.binaryPreviewVideo.hidden = true;
      this.editor.value = "";
      this.editor.readOnly = true;
      this.renderNullaSections(d);
      this.clearMarkdownPreview();
      this.clearTexPreview();
      this.clearMaxPreview();
      this.clearHtmlPreview();
      this.clearOeisPreview();
      this.destroyCsvGrid();
      this.mdPreview.hidden = true;
      this.texPreview.hidden = true;
      this.maxPreview.hidden = true;
      this.htmlPreview.hidden = true;
      this.oeisPreview.hidden = true;
      this.csvPreview.hidden = true;
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();
      this.bumpLineCountEpoch();
      this.updateLineNumberGutter();
      this.scheduleSurfaceRefresh();
      return;
    }

    if (d?.viewMode === "diff" && d.diffPair) {
      this.disposeDiffView();
      this.editorRow.classList.add("npp-editor-row--diff-tab");
      this.editorRow.classList.remove(
        "npp-editor-row--binary-preview",
        "npp-editor-row--markdown",
        "npp-editor-row--tex",
        "npp-editor-row--max",
        "npp-editor-row--html",
        "npp-editor-row--oeis",
        "npp-editor-row--csv",
        "npp-md-preview-only",
        "npp-tex-preview-only",
        "npp-max-preview-only",
        "npp-html-preview-only",
        "npp-oeis-preview-only",
        "npp-csv-preview-only",
      );
      this.mdMarkdownShowSource = false;
      this.texShowSource = false;
      this.maxShowSource = false;
      this.htmlShowSource = false;
      this.oeisShowSource = false;
      this.csvShowSource = false;
      this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image");
      this.binaryPreviewImg.removeAttribute("src");
      this.binaryPreviewPdf.removeAttribute("src");
      this.editor.value = "";
      this.clearMarkdownPreview();
      this.clearTexPreview();
      this.clearMaxPreview();
      this.clearHtmlPreview();
      this.clearOeisPreview();
      this.destroyCsvGrid();
      this.mdPreview.hidden = true;
      this.texPreview.hidden = true;
      this.maxPreview.hidden = true;
      this.htmlPreview.hidden = true;
      this.oeisPreview.hidden = true;
      this.csvPreview.hidden = true;
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();
      this.bumpLineCountEpoch();
      this.updateLineNumberGutter();
      this.mountActiveDiffTab(d);
      return;
    }

    this.editorRow.classList.remove("npp-editor-row--binary-preview", "npp-editor-row--diff-tab", "npp-editor-row--nulla");
    this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image", "npp-binary-preview--video");
    this.binaryPreviewImg.removeAttribute("src");
    this.binaryPreviewPdf.removeAttribute("src");
    this.binaryPreviewVideo.removeAttribute("src");
    this.binaryPreviewVideo.hidden = true;
    this.teardownNullaSections();

    this.editor.readOnly = false;
    this.editor.value = d?.content ?? "";
    this.bumpLineCountEpoch();
    this.applyEditorLayoutForLanguage();
    this.applyZoomCss();
    this.updateSyntaxHighlightLayer();
    if (d?.language === "md") this.scheduleMarkdownPreview();
    else this.clearMarkdownPreview();
    if (d?.language === "tex") this.scheduleTexPreview();
    else this.clearTexPreview();
    if (d?.language === "max") this.scheduleMaxPreview();
    else this.clearMaxPreview();
    if (d?.language === "html") this.scheduleHtmlPreview();
    else this.clearHtmlPreview();
    if (d?.language === "oeis") this.scheduleOeisPreview();
    else this.clearOeisPreview();
    if (this.isSpreadsheetLanguage(d?.language)) {
      this.ensureCsvGrid();
      this.scheduleCsvGridRefresh();
    } else {
      this.destroyCsvGrid();
    }
    this.updateLineNumberGutter();
    } finally {
      this.syncDictationToolbarButton();
      this.refreshMultiviewAfterLoad();
      this.applyMultiviewEditorRowClass();
      this.syncMultiviewPrimaryPaneHeader();
      this.syncTextareaSpellcheckState();
    }
  }

  private getMultiviewExtraTabIndices(): number[] {
    if (!this.multiviewEnabled) return [];
    const out: number[] = [];
    const used = this.multiviewExtraSlotsUsed();
    for (let slot = 0; slot < used; slot++) {
      const id = this.multiviewExtraDocIds[slot];
      if (!id) continue;
      const i = this.documents.documents.findIndex((d) => d.id === id);
      if (i >= 0) out.push(i);
    }
    return out;
  }

  private getMultiviewExtraTabIndexForSlot(slot: number): number | null {
    if (!this.multiviewEnabled) return null;
    const id = this.multiviewExtraDocIds[slot];
    if (!id) return null;
    const i = this.documents.documents.findIndex((d) => d.id === id);
    return i >= 0 ? i : null;
  }

  private syncMultiviewExtraHighlightScroll(slot: number): void {
    const ex = this.multiviewExtras[slot];
    if (!ex) return;
    this.layoutSyntaxOverlayOverTextarea(ex.editor, ex.highlightPre);
    const st = ex.editor.scrollTop;
    ex.highlightPre.scrollTop = st;
    ex.highlightPre.scrollLeft = ex.editor.scrollLeft;
    ex.lineNumberGutter.scrollTop = st;
  }

  private syncMultiviewExtraSlotHeader(slot: number): void {
    const ex = this.multiviewExtras[slot];
    if (!ex) return;
    const assignHint =
      "Click to target this column, then Alt+click a tab (plain text only) to assign a file.";
    const empty = "—";
    if (!this.multiviewEnabled || slot >= this.multiviewExtraSlotsUsed()) {
      ex.header.textContent = empty;
      ex.header.title = assignHint;
      return;
    }
    const id = this.multiviewExtraDocIds[slot];
    if (!id) {
      ex.header.textContent = empty;
      ex.header.title = assignHint;
      return;
    }
    const idx = this.documents.documents.findIndex((d) => d.id === id);
    const doc = idx >= 0 ? this.documents.documents[idx] : undefined;
    if (!doc || !this.docAllowsMultiviewEditorPane(doc)) {
      ex.header.textContent = empty;
      ex.header.title = assignHint;
      return;
    }
    const leaf = doc.filePath?.split(/[/\\]/).pop();
    ex.header.textContent = leaf || doc.title;
    const full = doc.filePath ?? doc.title;
    ex.header.title = `${full} — ${assignHint}`;
  }

  private syncAllMultiviewExtraSlotHeaders(): void {
    for (let s = 0; s < MULTIVIEW_MAX_EXTRA; s++) this.syncMultiviewExtraSlotHeader(s);
    this.syncMultiviewPrimaryPaneHeader();
  }

  private syncMultiviewPrimaryPaneHeader(): void {
    const h = this.multiviewPrimaryHeader;
    if (!this.multiviewEnabled) {
      h.hidden = true;
      return;
    }
    h.hidden = false;
    const doc = this.documents.active;
    if (!doc) {
      h.textContent = "—";
      h.title = "";
      return;
    }
    const leaf = doc.filePath?.split(/[/\\]/).pop();
    h.textContent = leaf || doc.title;
    const full = doc.filePath ?? doc.title;
    h.title = `${full} — Active tab (first pane)`;
  }

  /** Markdown / TeX / Max / HTML / OEIS / CSV / Excel / PDF use previews or session blobs — not multiview columns. */
  private isPlainMultiviewLanguage(lang: LanguageMode): boolean {
    return (
      lang !== "md" &&
      lang !== "tex" &&
      lang !== "max" &&
      lang !== "html" &&
      lang !== "oeis" &&
      lang !== "csv" &&
      lang !== "excel" &&
      lang !== "pdf"
    );
  }

  private docAllowsMultiviewEditorPane(d: TabDocument | null | undefined): boolean {
    return Boolean(
      d &&
      d.viewMode === "text" &&
      !d.sessionRestorePlaceholder &&
      this.isPlainMultiviewLanguage(d.language),
    );
  }

  private multiviewPrimaryAllowsSplit(): boolean {
    return this.documents.active ? this.docAllowsMultiviewEditorPane(this.documents.active) : false;
  }

  private pickSecondaryDocIdForMultiview(): string | null {
    const active = this.documents.active;
    if (!active) return null;
    for (const d of this.documents.documents) {
      if (d.id === active.id) continue;
      if (this.docAllowsMultiviewEditorPane(d)) return d.id;
    }
    return null;
  }

  private collectMultiviewReservedDocIds(): Set<string> {
    const used = new Set<string>();
    const active = this.documents.active;
    if (active) used.add(active.id);
    const n = this.multiviewExtraSlotsUsed();
    for (let slot = 0; slot < n; slot++) {
      const id = this.multiviewExtraDocIds[slot];
      if (id) used.add(id);
    }
    return used;
  }

  private pickNextMultiviewExtraDocId(reserved: Set<string>): string | null {
    for (const d of this.documents.documents) {
      if (reserved.has(d.id)) continue;
      if (this.docAllowsMultiviewEditorPane(d)) return d.id;
    }
    return null;
  }

  private fillMultiviewExtraDocSlots(): void {
    const need = this.multiviewExtraSlotsUsed();
    const reserved = this.collectMultiviewReservedDocIds();
    for (let slot = 0; slot < need; slot++) {
      const id = this.multiviewExtraDocIds[slot];
      if (id) {
        const idx = this.documents.documents.findIndex((d) => d.id === id);
        const doc = idx >= 0 ? this.documents.documents[idx] : null;
      if (doc && this.docAllowsMultiviewEditorPane(doc) && doc.id !== this.documents.active?.id) {
        reserved.add(id);
        continue;
      }
      reserved.delete(id);
      this.multiviewExtraDocIds[slot] = null;
      }
    }
    for (let slot = 0; slot < need; slot++) {
      if (this.multiviewExtraDocIds[slot]) continue;
      const next = this.pickNextMultiviewExtraDocId(reserved);
      if (!next) break;
      this.multiviewExtraDocIds[slot] = next;
      reserved.add(next);
    }
  }

  private resetMultiviewEqualColumnFlex(): void {
    this.multiviewPrimaryCol.style.flex = "";
    this.multiviewPrimaryCol.style.flexGrow = "";
    this.multiviewPrimaryCol.style.flexShrink = "";
    this.multiviewPrimaryCol.style.flexBasis = "";
    for (const ex of this.multiviewExtras) {
      ex.column.style.flex = "";
      ex.column.style.flexGrow = "";
      ex.column.style.flexShrink = "";
      ex.column.style.flexBasis = "";
    }
  }

  private applyMultiviewPaneVisibilityAndFlex(): void {
    if (!this.multiviewEnabled) {
      this.multiviewChrome.hidden = true;
      for (let i = 0; i < this.multiviewExtras.length; i++) {
        const ex = this.multiviewExtras[i]!;
        ex.sash.hidden = true;
        ex.column.hidden = true;
      }
      this.resetMultiviewEqualColumnFlex();
      return;
    }
    this.multiviewChrome.hidden = false;
    const need = this.multiviewExtraSlotsUsed();
    for (let i = 0; i < this.multiviewExtras.length; i++) {
      const ex = this.multiviewExtras[i]!;
      const on = i < need;
      ex.sash.hidden = !on;
      ex.column.hidden = !on;
    }
    this.resetMultiviewEqualColumnFlex();
    if (this.multiviewPaneCount === 2) {
      this.multiviewPrimaryCol.style.flex = "1 1 50%";
      this.multiviewExtras[0]!.column.style.flex = "1 1 50%";
    } else {
      this.multiviewPrimaryCol.style.flex = "1 1 0";
      for (let i = 0; i < need; i++) {
        this.multiviewExtras[i]!.column.style.flex = "1 1 0";
      }
    }
  }

  private updateMultiviewSlotHeaderStyles(): void {
    const need = this.multiviewExtraSlotsUsed();
    for (let i = 0; i < this.multiviewExtras.length; i++) {
      const h = this.multiviewExtras[i]!.header;
      h.classList.toggle("npp-multiview-slot-header--target", this.multiviewEnabled && i < need && i === this.multiviewAltClickTargetSlot);
    }
  }

  private applyMultiviewEditorRowClass(): void {
    this.editorRow.classList.toggle("npp-editor-row--multiview-text-only", this.multiviewEnabled);
  }

  private applyMultiviewShellDataset(): void {
    if (this.multiviewEnabled) this.shell.dataset.multiview = "1";
    else delete this.shell.dataset.multiview;
  }

  private setMultiviewPaneCount(count: number): void {
    if (!this.multiviewEnabled) return;
    const n = Math.max(2, Math.min(MULTIVIEW_MAX_PANES, count));
    if (n === this.multiviewPaneCount) {
      this.applyMultiviewPaneVisibilityAndFlex();
      this.syncAllMultiviewExtraSlotHeaders();
      return;
    }
    this.flushExtraEditorsToBuffer();
    const prev = this.multiviewPaneCount;
    this.multiviewPaneCount = n;
    this.multiviewPaneCountSelect.value = String(n);
    if (this.multiviewAltClickTargetSlot >= n - 1) {
      this.multiviewAltClickTargetSlot = Math.max(0, n - 2);
    }
    if (n < prev) {
      for (let slot = n - 1; slot < MULTIVIEW_MAX_EXTRA; slot++) {
        this.multiviewExtraDocIds[slot] = null;
        const ex = this.multiviewExtras[slot]!;
        ex.editor.value = "";
        ex.highlightCode.className = "hljs npp-plaintext";
        ex.highlightCode.innerHTML = "";
      }
    }
    this.fillMultiviewExtraDocSlots();
    this.applyMultiviewPaneVisibilityAndFlex();
    this.loadAllMultiviewExtraEditors();
    this.updateMultiviewSlotHeaderStyles();
    this.syncAllMultiviewExtraSlotHeaders();
    this.tabBar?.refresh();
  }

  private setMultiviewEnabled(on: boolean): void {
    if (on) {
      if (this.multiviewEnabled) return;
      if (this.documents.documents.length < 2) {
        this.updateStatusLine("Multiview needs at least two tabs.");
        return;
      }
      if (!this.multiviewPrimaryAllowsSplit()) {
        this.updateStatusLine(
          "Multiview needs a plain text tab (not Markdown, TeX, Max, HTML, OEIS, CSV, images, or compare).",
        );
        return;
      }
      const raw = Number.parseInt(this.multiviewPaneCountSelect.value, 10);
      this.multiviewPaneCount = Number.isFinite(raw)
        ? Math.max(2, Math.min(MULTIVIEW_MAX_PANES, raw))
        : 2;
      this.multiviewPaneCountSelect.value = String(this.multiviewPaneCount);
      const sid = this.pickSecondaryDocIdForMultiview();
      if (!sid) {
        this.updateStatusLine("Multiview needs a second plain text tab (same rules as the active tab).");
        return;
      }
      this.flushEditorToBuffer();
      this.multiviewPreviewFlagsSnapshot = {
        mdMarkdownShowSource: this.mdMarkdownShowSource,
        texShowSource: this.texShowSource,
        maxShowSource: this.maxShowSource,
        htmlShowSource: this.htmlShowSource,
        oeisShowSource: this.oeisShowSource,
        csvShowSource: this.csvShowSource,
      };
      this.mdMarkdownShowSource = true;
      this.texShowSource = true;
      this.maxShowSource = true;
      this.htmlShowSource = true;
      this.oeisShowSource = true;
      this.csvShowSource = true;
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();
      for (let i = 0; i < MULTIVIEW_MAX_EXTRA; i++) this.multiviewExtraDocIds[i] = null;
      this.multiviewExtraDocIds[0] = sid;
      this.multiviewAltClickTargetSlot = 0;
      this.fillMultiviewExtraDocSlots();
      this.multiviewEnabled = true;
      this.multiviewRoot.classList.add("npp-multiview--on");
      this.applyMultiviewPaneVisibilityAndFlex();
      this.updateMultiviewSlotHeaderStyles();
      this.applyMultiviewEditorRowClass();
      this.loadAllMultiviewExtraEditors();
      this.syncAllMultiviewExtraSlotHeaders();
      this.updateSyntaxHighlightLayer();
    } else {
      if (this.multiviewEnabled) this.flushExtraEditorsToBuffer();
      this.multiviewEnabled = false;
      for (let i = 0; i < MULTIVIEW_MAX_EXTRA; i++) this.multiviewExtraDocIds[i] = null;
      this.multiviewRoot.classList.remove("npp-multiview--on");
      for (const ex of this.multiviewExtras) {
        ex.editor.value = "";
        ex.highlightCode.className = "hljs npp-plaintext";
        ex.highlightCode.innerHTML = "";
      }
      this.applyMultiviewPaneVisibilityAndFlex();
      this.syncAllMultiviewExtraSlotHeaders();
      if (this.multiviewPreviewFlagsSnapshot) {
        const s = this.multiviewPreviewFlagsSnapshot;
        this.mdMarkdownShowSource = s.mdMarkdownShowSource;
        this.texShowSource = s.texShowSource;
        this.maxShowSource = s.maxShowSource;
        this.htmlShowSource = s.htmlShowSource;
        this.oeisShowSource = s.oeisShowSource;
        this.csvShowSource = s.csvShowSource;
        this.multiviewPreviewFlagsSnapshot = null;
      }
      this.applyMarkdownPreviewLayoutClass();
      this.applyTexPreviewLayoutClass();
      this.applyMaxPreviewLayoutClass();
      this.applyHtmlPreviewLayoutClass();
      this.applyOeisPreviewLayoutClass();
      this.applyCsvPreviewLayoutClass();
      this.applyMultiviewEditorRowClass();
      this.updateSyntaxHighlightLayer();
      if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
      if (this.documents.active?.language === "tex") this.scheduleTexPreview();
      if (this.documents.active?.language === "max") this.scheduleMaxPreview();
      if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
      if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
      if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
    }
    this.applyMultiviewShellDataset();
    this.tabBar?.refresh();
    this.dispatcher.setCommandChecked(CMD.VIEW_MULTIVIEW, this.multiviewEnabled);
    this.menuBar?.syncCheckboxStates([CMD.VIEW_MULTIVIEW, CMD.VIEW_SINGLE_EDITOR]);
    this.updateStatusLine(
      on
        ? "Multiview on — choose pane column (header), then Alt+click a tab. Panes menu sets column count."
        : "Single editor view.",
    );
  }

  private refreshMultiviewAfterLoad(): void {
    if (!this.multiviewEnabled) return;
    if (this.documents.documents.length < 2) {
      this.setMultiviewEnabled(false);
      return;
    }
    if (!this.multiviewPrimaryAllowsSplit()) {
      this.setMultiviewEnabled(false);
      return;
    }
    this.fillMultiviewExtraDocSlots();
    const need = this.multiviewExtraSlotsUsed();
    let any = false;
    for (let slot = 0; slot < need; slot++) {
      if (this.multiviewExtraDocIds[slot]) {
        any = true;
        break;
      }
    }
    if (!any) {
      const sid = this.pickSecondaryDocIdForMultiview();
      if (!sid) {
        this.setMultiviewEnabled(false);
        return;
      }
      this.multiviewExtraDocIds[0] = sid;
      this.fillMultiviewExtraDocSlots();
    }
    const activeId = this.documents.active?.id;
    for (let slot = 0; slot < need; slot++) {
      if (this.multiviewExtraDocIds[slot] === activeId) this.multiviewExtraDocIds[slot] = null;
    }
    this.fillMultiviewExtraDocSlots();
    this.loadAllMultiviewExtraEditors();
    this.syncAllMultiviewExtraSlotHeaders();
    this.applyMultiviewEditorRowClass();
  }

  private loadMultiviewExtraEditorSlot(slot: number): void {
    if (!this.multiviewEnabled) return;
    const ex = this.multiviewExtras[slot];
    if (!ex) return;
    try {
      const idx = this.getMultiviewExtraTabIndexForSlot(slot);
      if (idx === null) {
        ex.editor.value = "";
        ex.editor.readOnly = true;
        ex.highlightCode.className = "hljs npp-plaintext";
        ex.highlightCode.innerHTML = "";
        return;
      }
      const d = this.documents.documents[idx];
      if (!d || d.viewMode !== "text") {
        ex.editor.value = "";
        ex.editor.readOnly = true;
        ex.highlightCode.className = "hljs npp-plaintext";
        ex.highlightCode.innerHTML = "";
        return;
      }
      ex.editor.readOnly = false;
      ex.editor.value = d.content;
      ex.editorHost.classList.toggle("npp-show-line-numbers", this.showLineNumbers);
      this.updateSyntaxHighlightLayerForExtraSlot(slot);
      this.syncMultiviewExtraHighlightScroll(slot);
      this.updateLineNumberGutterForExtraSlot(slot);
    } finally {
      this.syncMultiviewExtraSlotHeader(slot);
    }
  }

  private loadAllMultiviewExtraEditors(): void {
    if (!this.multiviewEnabled) return;
    const used = this.multiviewExtraSlotsUsed();
    for (let slot = 0; slot < used; slot++) this.loadMultiviewExtraEditorSlot(slot);
    this.syncTextareaSpellcheckState();
  }

  private updateSyntaxHighlightLayerForExtraSlot(slot: number): void {
    const ex = this.multiviewExtras[slot];
    if (!ex) return;
    const idx = this.getMultiviewExtraTabIndexForSlot(slot);
    if (idx === null) {
      ex.highlightCode.className = "hljs npp-plaintext";
      ex.highlightCode.innerHTML = "";
      return;
    }
    const doc = this.documents.documents[idx];
    if (!doc || doc.viewMode !== "text") {
      ex.highlightCode.className = "hljs npp-plaintext";
      ex.highlightCode.innerHTML = "";
      return;
    }
    ex.highlightCode.className = highlightCodeClassFor(doc.language);
    const proseSpellDecorate = this.computeMultiviewExtraEditorSpellcheckEnabled(doc, ex.editor);
    ex.highlightCode.innerHTML = wrapHighlightLines(
      renderHighlightedFragment(ex.editor.value, doc.language, { proseSpellDecorate }),
    );
    this.syncMultiviewExtraHighlightScroll(slot);
  }

  private updateLineNumberGutterForExtraSlot(slot: number): void {
    const ex = this.multiviewExtras[slot];
    if (!ex) return;
    ex.editorHost.classList.toggle("npp-show-line-numbers", this.showLineNumbers);
    ex.editorHost.classList.toggle("npp-gutter-datetime", this.showLineNumbers && this.gutterMode === "datetime");
    if (!this.showLineNumbers) return;
    const text = ex.editor.value;
    const ta = ex.editor;
    const key = this.lineNumberGutterLayoutKey(ta, text, slot);
    if (this.gutterLayoutKeyPerMultiviewSlot[slot] !== key) {
      this.gutterLayoutKeyPerMultiviewSlot[slot] = key;
      const docId = this.multiviewExtraDocIds[slot];
      ex.lineNumberPre.textContent = this.gutterMode === "datetime" && docId !== null
        ? this.buildDatetimeGutterContent(ta, text, docId)
        : this.buildLineNumberPreContent(ta, text);
    }
    ex.lineNumberGutter.scrollTop = ta.scrollTop;
  }

  private onMultiviewExtraEditorInput(slot: number): void {
    if (!this.multiviewEnabled) return;
    const idx = this.getMultiviewExtraTabIndexForSlot(slot);
    if (idx === null) return;
    const exTa = this.multiviewExtras[slot]?.editor;
    if (!this.rectEditInProgress && exTa && this.activeRectSel?.ta === exTa) this.dismissRectSel();
    this.bumpLineCountEpoch();
    this.documents.syncFromEditorAt(idx, this.multiviewExtras[slot]!.editor.value);
    this.updateSyntaxHighlightLayerForExtraSlot(slot);
    this.schedulePersistTabSession();
    this.tabBar?.refresh();
    this.updateStatusLine();
    this.syncTextareaSpellcheckState();
  }

  private mountMultiviewSash(): void {
    const sash = this.multiviewExtras[0]!.sash;
    const root = this.multiviewBodyRow;
    const left = this.multiviewPrimaryCol;
    const right = this.multiviewExtras[0]!.column;
    const onMove = (e: PointerEvent): void => {
      if (!this.multiviewEnabled || this.multiviewPaneCount !== 2) return;
      const rect = root.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / Math.max(1, rect.width);
      const r = Math.min(0.88, Math.max(0.12, ratio));
      left.style.flexGrow = "0";
      left.style.flexShrink = "0";
      left.style.flexBasis = `${(r * 100).toFixed(2)}%`;
      right.style.flex = "1 1 auto";
    };
    const onUp = (e: PointerEvent): void => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      try {
        sash.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onDown = (e: PointerEvent): void => {
      if (!this.multiviewEnabled || this.multiviewPaneCount !== 2) return;
      if (e.button !== 0) return;
      e.preventDefault();
      sash.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    };
    sash.addEventListener("pointerdown", onDown);
    this.multiviewSashCleanup = () => sash.removeEventListener("pointerdown", onDown);
  }

  private mountWorkspaceSash(): void {
    const sash = this.workspaceSash;
    if (!sash || !this.folderWorkspacePane) return;
    const workspaceEl = this.folderWorkspacePane.element as HTMLElement;

    const savedWidth = safeLocalStorageGet("npp-workspace-width");
    if (savedWidth) workspaceEl.style.width = savedWidth;

    const onMove = (e: PointerEvent): void => {
      const hostRect = workspaceEl.parentElement!.getBoundingClientRect();
      const newWidth = Math.max(96, Math.min(600, e.clientX - hostRect.left));
      workspaceEl.style.width = `${newWidth}px`;
      safeLocalStorageSet("npp-workspace-width", `${newWidth}px`);
    };
    const onUp = (e: PointerEvent): void => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      try {
        sash.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onDown = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      e.preventDefault();
      sash.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    };
    sash.addEventListener("pointerdown", onDown);
    sash.addEventListener("dblclick", () => {
      workspaceEl.style.width = "";
      safeLocalStorageRemove("npp-workspace-width");
    });
  }

  private mountPreviewSash(): void {
    const sash = this.previewSash;
    if (!sash) return;
    const row = this.editorRow;

    const savedSplit = safeLocalStorageGet("npp-preview-split-basis");
    if (savedSplit) row.style.setProperty("--npp-preview-split-basis", savedSplit);

    const onMove = (e: PointerEvent): void => {
      const rect = row.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / Math.max(1, rect.width);
      const r = Math.min(0.85, Math.max(0.15, ratio));
      const val = `${(r * 100).toFixed(2)}%`;
      row.style.setProperty("--npp-preview-split-basis", val);
      safeLocalStorageSet("npp-preview-split-basis", val);
    };
    const onUp = (e: PointerEvent): void => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      try {
        sash.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onDown = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      e.preventDefault();
      sash.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    };
    sash.addEventListener("pointerdown", onDown);
    sash.addEventListener("dblclick", () => {
      row.style.removeProperty("--npp-preview-split-basis");
      safeLocalStorageRemove("npp-preview-split-basis");
    });
  }

  private selectTabFromTabBar(index: number, event?: MouseEvent): void {
    if (this.multiviewEnabled && event?.altKey) {
      this.assignMultiviewTabToTargetExtraSlot(index);
      return;
    }
    if (index === this.documents.activeIndex) return;
    this.selectTab(index);
  }

  private assignMultiviewTabToTargetExtraSlot(index: number): void {
    if (!this.multiviewEnabled) {
      this.updateStatusLine("Turn on Multiview first (toolbar or View menu).");
      return;
    }
    const d = this.documents.documents[index];
    if (!this.docAllowsMultiviewEditorPane(d)) {
      this.updateStatusLine(
        "Extra panes: plain text tabs only (not Markdown, TeX, Max, HTML, OEIS, or CSV).",
      );
      return;
    }
    if (d.id === this.documents.active?.id) {
      this.updateStatusLine("Alt+click a tab other than the active one for an extra column.");
      return;
    }
    const slot = Math.min(this.multiviewAltClickTargetSlot, this.multiviewExtraSlotsUsed() - 1);
    if (slot < 0) {
      this.updateStatusLine("Increase pane count to assign extra columns.");
      return;
    }
    this.flushExtraEditorsToBuffer();
    const used = this.multiviewExtraSlotsUsed();
    for (let s = 0; s < used; s++) {
      if (s !== slot && this.multiviewExtraDocIds[s] === d.id) this.multiviewExtraDocIds[s] = null;
    }
    this.multiviewExtraDocIds[slot] = d.id;
    this.fillMultiviewExtraDocSlots();
    this.loadMultiviewExtraEditorSlot(slot);
    this.syncTextareaSpellcheckState();
    this.tabBar?.refresh();
    this.updateStatusLine(`Multiview: ${this.multiviewExtras[slot]!.header.textContent} → ${d.title}`);
  }

  private onTabReorder(fromIndex: number, insertIndex: number): void {
    this.flushEditorToBuffer();
    const flipBefore = this.tabBar?.captureLayoutForReorder();
    this.documents.moveTab(fromIndex, insertIndex);
    this.tabBar?.refresh();
    if (flipBefore && flipBefore.size > 0) {
      this.tabBar?.animateReorderFrom(flipBefore);
    }
    this.loadActiveIntoEditor();
    this.updateStatusLine("Tab order updated");
  }

  private selectTab(index: number): void {
    this.flushEditorToBuffer();
    this.documents.setActiveIndex(index);
    void this.finishTabActivationAfterDiskConflict();
  }

  /** Runs after `setActiveIndex`: may prompt if the file changed on disk while the buffer was dirty. */
  private async finishTabActivationAfterDiskConflict(): Promise<void> {
    const d = this.documents.active;
    const idx = this.documents.activeIndex;
    if (d?.diskChangedWhileDirty && d.dirty && d.fileHandle) {
      d.diskChangedWhileDirty = false;
      const reload = confirm(
        `"${d.title}" was modified outside NullaPad (e.g. another user saved the same file). Reload from disk and discard your unsaved changes?`,
      );
      if (reload) {
        await this.documents.reloadTabFromDiskHandle(idx);
      } else {
        try {
          d.fileSourceMtimeMs = (await d.fileHandle.getFile()).lastModified;
        } catch {
          /* ignore */
        }
      }
    }
    this.loadActiveIntoEditor();
    this.tabBar?.refresh();
    this.updateStatusLine();
    this.persistTabSessionToStorage();
  }

  private startExternalFilePolling(): void {
    if (this.externalFilePollTimer) return;
    this.externalFilePollTimer = setInterval(() => {
      void this.pollExternalFileChanges();
    }, AppShell.EXTERNAL_FILE_POLL_MS);
  }

  private async pollExternalFileChanges(): Promise<void> {
    if (this.externalFilePollInFlight) return;
    this.externalFilePollInFlight = true;
    const activeIdx = this.documents.activeIndex;
    let didChange = false;
    try {
      for (let i = 0; i < this.documents.documents.length; i++) {
        const d = this.documents.documents[i]!;
        if (!d.fileHandle || (d.viewMode !== "text" && d.viewMode !== "video")) continue;
        let perm = await d.fileHandle.queryPermission({ mode: "read" });
        if (perm !== "granted") continue;
        let file: File;
        try {
          file = await d.fileHandle.getFile();
        } catch {
          continue;
        }
        const mtime = file.lastModified;
        if (d.fileSourceMtimeMs === null) {
          d.fileSourceMtimeMs = mtime;
          continue;
        }
        if (mtime <= d.fileSourceMtimeMs) continue;

        if (!d.dirty) {
          const ok = await this.documents.reloadTabFromDiskHandle(i);
          if (ok) {
            didChange = true;
            if (i === activeIdx) {
              this.loadActiveIntoEditor();
              this.updateSyntaxHighlightLayer();
              if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
              if (this.documents.active?.language === "tex") this.scheduleTexPreview();
              if (this.documents.active?.language === "max") this.scheduleMaxPreview();
              if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
              if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
              if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
            }
          }
        } else {
          d.diskChangedWhileDirty = true;
          if (i === activeIdx) {
            d.diskChangedWhileDirty = false;
            const reload = confirm(
              `"${d.title}" was modified outside NullaPad while you have unsaved changes. Reload from disk and discard your edits?`,
            );
            if (reload) {
              const ok = await this.documents.reloadTabFromDiskHandle(i);
              if (ok) {
                didChange = true;
                this.loadActiveIntoEditor();
                this.updateSyntaxHighlightLayer();
                if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
                if (this.documents.active?.language === "tex") this.scheduleTexPreview();
                if (this.documents.active?.language === "max") this.scheduleMaxPreview();
                if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
                if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
                if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
              }
            } else {
              try {
                d.fileSourceMtimeMs = (await d.fileHandle.getFile()).lastModified;
              } catch {
                /* ignore */
              }
            }
          }
        }
      }
    } finally {
      this.externalFilePollInFlight = false;
    }
    if (didChange) {
      this.tabBar?.refresh();
      this.schedulePersistTabSession();
      this.updateStatusLine();
    }
  }

  private async navigateToFindHit(hit: {
    tabIndex?: number;
    rootFolderName?: string;
    relativePath?: string;
    fileHandle?: FileSystemFileHandle;
    offset: number;
  }): Promise<void> {
    this.flushEditorToBuffer();
    if (typeof hit.tabIndex === "number") {
      this.selectTab(hit.tabIndex);
    } else if (hit.rootFolderName && hit.relativePath && hit.fileHandle) {
      const ok = await this.documents.openFromWorkspace(hit.rootFolderName, hit.relativePath, hit.fileHandle);
      if (!ok) {
        this.updateStatusLine(`Could not open "${hit.relativePath}" from search result`);
        return;
      }
      this.tabBar?.refresh();
      this.loadActiveIntoEditor();
    } else {
      return;
    }
    this.editor.focus();
    const text = this.editor.value;
    const { start: lineStart, end: lineEnd } = logicalLineRangeAtOffset(text, hit.offset);
    this.editor.setSelectionRange(lineStart, lineEnd);
    this.scrollEditorToLineContainingOffset(lineStart);
    this.editorEnsureVisibleOnCoarsePointer(lineStart);
    this.updateStatusLine();
  }

  private closeTab(index: number): void {
    this.flushEditorToBuffer();
    if (!this.documents.closeTabAt(index)) return;
    this.loadActiveIntoEditor();
    this.tabBar?.refresh();
    this.updateStatusLine();
    this.persistTabSessionToStorage();
  }

  private closeOtherTabs(index: number): void {
    this.flushEditorToBuffer();
    for (let i = this.documents.documents.length - 1; i >= 0; i--) {
      if (i === index) continue;
      this.documents.closeTabAt(i);
    }
    const nextIndex = Math.min(index, this.documents.documents.length - 1);
    this.documents.setActiveIndex(Math.max(0, nextIndex));
    this.loadActiveIntoEditor();
    this.tabBar?.refresh();
    this.updateStatusLine();
    this.persistTabSessionToStorage();
  }

  private closeAllTabsFromContext(): void {
    this.flushEditorToBuffer();
    this.documents.closeAllTabs();
    this.loadActiveIntoEditor();
    this.tabBar?.refresh();
    this.updateStatusLine();
    this.persistTabSessionToStorage();
  }

  private newUntitledTabFromTabBar(): void {
    this.flushEditorToBuffer();
    this.documents.checkpointHistory(this.editor.value);
    this.documents.addEmptyTab();
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.rebuildMenubar();
    this.updateStatusLine("Command: IDM_FILE_NEW (ui)");
    this.persistTabSessionToStorage();
  }

  private duplicateTabFromTabBar(index: number): void {
    this.flushEditorToBuffer();
    const ok = this.documents.duplicateTab(index);
    if (!ok) return;
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateSyntaxHighlightLayer();
    this.updateStatusLine();
    this.rebuildMenubar();
    this.persistTabSessionToStorage();
  }

  private cycleTabColorFromTabBar(index: number): void {
    this.documents.cycleTabColor(index);
    this.tabBar?.refresh();
  }

  private setTabColorFromTabBar(index: number, color: TabColorId | null): void {
    this.documents.setTabColor(index, color);
    this.tabBar?.refresh();
  }

  private renameTabFromContext(index: number, newTitle: string): void {
    const doc = this.documents.documents[index];
    if (!doc) return;
    doc.title = newTitle;
    this.tabBar?.refresh();
    this.updateStatusLine();
  }

  private mergeSameColorFromTabBar(separator: string, color: TabColorId | null): void {
    if (color === null) {
      void alert("Select a group color first (click a swatch in the title bar).");
      return;
    }
    this.flushEditorToBuffer();
    this.documents.checkpointHistory(this.editor.value);
    const r = this.documents.mergeTextTabsByColor(color, separator);
    if (!r.ok) {
      if (r.error !== "Cancelled.") void alert(r.error);
      return;
    }
    this.tabBar?.clearColorFilter();
    this.rebuildMenubar();
    this.loadActiveIntoEditor();
    this.updateSyntaxHighlightLayer();
    if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
    if (this.documents.active?.language === "tex") this.scheduleTexPreview();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
    if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
    if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
    this.updateLineNumberGutter();
    this.updateStatusLine("Merged tabs with same color");
  }

  private splitActiveFromTabBar(separator: string): void {
    this.flushEditorToBuffer();
    this.documents.checkpointHistory(this.editor.value);
    const r = this.documents.splitActiveTextBySeparator(separator);
    if (!r.ok) {
      if (r.error !== "Cancelled.") void alert(r.error);
      return;
    }
    if (this.tabBar?.getColorFilter() != null) this.tabBar.clearColorFilter();
    else this.tabBar?.refresh();
    this.rebuildMenubar();
    this.loadActiveIntoEditor();
    this.updateSyntaxHighlightLayer();
    if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
    if (this.documents.active?.language === "tex") this.scheduleTexPreview();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
    if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
    if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
    this.updateLineNumberGutter();
    this.updateStatusLine("Split active tab by separator");
  }

  private async copyPathOrTitleForTab(index: number): Promise<void> {
    const doc = this.documents.documents[index];
    if (!doc) return;
    const p = doc.filePath ?? doc.title;
    if (!p) return;
    await navigator.clipboard.writeText(p);
    this.updateStatusLine("Path / title copied to clipboard");
  }

  private updateNullapadSecretSyncUi(): void {
    const candidate = normalizeNullapadInstanceSecretInput(this.nullapadSecretInput.value);
    const current = getOrCreateNullapadInstanceSecret();
    const valid = isValidNullapadInstanceSecretFormat(this.nullapadSecretInput.value);
    this.nullapadSecretSyncBtn.disabled = !valid || candidate === current;
  }

  /**
   * After the persisted instance secret changes, reset share-inbox client state and restart polling
   * so GET /inbox uses the new `recipientId` immediately.
   */
  private nullapadInstanceSecretRefreshShareInbox(): void {
    this.shareInboxMultipartInflightKey = null;
    this.shareInboxLastHandledFingerprint = null;
    this.hideShareInboxOfferBar();
    this.restartShareInboxPolling();
    this.updateNullapadSecretSyncUi();
  }

  private startNullapadInstanceSecretAutosync(): void {
    if (this.nullapadInstanceSecretAutosyncTimer != null) return;
    this.nullapadInstanceSecretAutosyncTimer = setInterval(
      () => this.periodicNullapadInstanceSecretAutosync(),
      AppShell.NULLAPAD_INSTANCE_SECRET_AUTOSYNC_MS,
    );
  }

  /**
   * Every 10s: keep the title-bar id aligned with `localStorage` (survives refresh), re-persist when
   * the field matches the stored id (durability), auto-save when the field holds a **new** valid id,
   * restore the field when it is empty (one immediate inbox poll), and refresh inbox polling when the
   * persisted id changes (same as manual Sync).
   */
  private periodicNullapadInstanceSecretAutosync(): void {
    const stored = getOrCreateNullapadInstanceSecret();
    const raw = this.nullapadSecretInput.value;
    const candidate = normalizeNullapadInstanceSecretInput(raw);

    if (!candidate) {
      this.nullapadSecretInput.value = stored;
      this.updateNullapadSecretSyncUi();
      void this.pollShareInbox();
      return;
    }

    if (!isValidNullapadInstanceSecretFormat(raw)) {
      return;
    }

    if (candidate !== stored) {
      try {
        persistNullapadInstanceSecret(candidate);
        this.nullapadSecretInput.value = getOrCreateNullapadInstanceSecret();
        this.nullapadInstanceSecretRefreshShareInbox();
        this.updateStatusLine("Instance secret auto-saved; share inbox refreshed for new id.");
      } catch {
        /* storage blocked — avoid alert spam every 10s */
      }
      return;
    }

    try {
      persistNullapadInstanceSecret(candidate);
    } catch {
      /* ignore periodic persist errors */
    }
  }

  private onNullapadInstanceSecretSyncClick(): void {
    try {
      persistNullapadInstanceSecret(this.nullapadSecretInput.value);
      this.nullapadSecretInput.value = getOrCreateNullapadInstanceSecret();
      this.nullapadInstanceSecretRefreshShareInbox();
      this.updateStatusLine("Instance secret saved; polling share inbox for this id.");
    } catch (e) {
      void alert(e instanceof Error ? e.message : String(e));
    }
  }

  private async copyNullapadInstanceSecretToClipboard(): Promise<void> {
    const t = this.nullapadSecretInput.value.trim();
    if (!t) {
      this.updateStatusLine("Nothing to copy.");
      return;
    }
    try {
      await navigator.clipboard.writeText(t);
      this.updateStatusLine("Instance secret copied to clipboard.");
    } catch {
      this.nullapadSecretInput.focus();
      this.nullapadSecretInput.select();
      try {
        const ok = document.execCommand("copy");
        if (ok) this.updateStatusLine("Instance secret copied to clipboard.");
        else void alert("Could not copy to clipboard.");
      } catch {
        void alert("Could not copy to clipboard.");
      }
    }
  }

  /**
   * After Send-to-NullaPad from a context menu, return focus to the editor / Nulla buffer (not the
   * footer status line). Runs on a microtask so it wins over the browser moving focus when the menu
   * is hidden. If the share was for a non-active tab (tab strip menu), that tab becomes active first.
   */
  private restoreFocusAfterOutgoingShare(sharedTabIndex: number): void {
    const focusEditorSurface = (): void => {
      const d = this.documents.active;
      if (d?.viewMode === "text" || d?.viewMode === "video") {
        this.editor.focus({ preventScroll: true });
      }
    };
    queueMicrotask(() => {
      if (sharedTabIndex !== this.documents.activeIndex) {
        this.flushEditorToBuffer();
        this.documents.setActiveIndex(sharedTabIndex);
        void this.finishTabActivationAfterDiskConflict().then(() => focusEditorSurface());
      } else {
        focusEditorSurface();
      }
    });
  }

  /** Tab context menu → POST chunked share (same-origin inbox + recipient secret field). */
  private async shareTabFromContextMenu(tabIndex: number, recipientId: string): Promise<void> {
    // Always flush the *active* tab so its buffer is saved before we read another tab’s `doc.content`.
    this.flushEditorToBuffer();
    const doc = this.documents.documents[tabIndex];
    if (!doc) {
      throw new Error("Tab not found.");
    }
    if (doc.viewMode === "image" || doc.viewMode === "pdf" || doc.viewMode === "diff") {
      void alert("Share to NullaPad is only available for text tabs and Max (.max) tabs (not images, PDFs, or Compare tabs).");
      throw new Error("Unsupported tab type for share.");
    }
    const endpoint = getSavedNullapadShareEndpoint();
    const shareName = nullapadShareSuggestedTabTitle(doc);
    // Bundle any markup marks into the payload so the recipient receives them too.
    const marks = this.getOrLoadMarksForDoc(doc.id, doc.filePath);
    const tabText = bundleSharePayload(doc.content, marks);
    try {
      await shareTabViaShareSession({
        endpoint,
        recipientId,
        tabText,
        tabTitle: shareName,
        onProgress: (p) => {
          if (p.phase === "segment") {
            this.updateStatusLine(`Sharing large tab: part ${p.index}/${p.count}…`);
          } else if (p.phase === "finalize") {
            this.updateStatusLine("Sharing large tab: finishing…");
          } else {
            this.updateStatusLine("Sharing large tab: starting…");
          }
        },
      });
      this.updateStatusLine(`Shared "${shareName}" to NullaPad.`);
      // Align inbox polling (10s) for this app; recipients with the same `recipientId` slot see updates on the same cadence.
      this.restartShareInboxPolling();
    } finally {
      this.restoreFocusAfterOutgoingShare(tabIndex);
    }
  }

  private static formatShareInboxByteSize(byteLength: number): string {
    if (!Number.isFinite(byteLength) || byteLength < 0) return "unknown size";
    if (byteLength < 1024) return `${byteLength} bytes`;
    if (byteLength < 1024 * 1024) return `${(byteLength / 1024).toFixed(1)} KiB`;
    return `${(byteLength / (1024 * 1024)).toFixed(2)} MiB`;
  }

  /** Append-only status suffix for share inbox; throttled unless `force` (e.g. startup). */
  private reportShareInboxPollStatus(message: string, minIntervalMs: number, force = false): void {
    const now = Date.now();
    if (!force && now - this.shareInboxStatusLastAt < minIntervalMs) return;
    this.shareInboxStatusLastAt = now;
    this.updateStatusLine(message);
  }

  private startShareInboxPolling(): void {
    void this.pollShareInbox();
    if (this.shareInboxPollTimer != null) return;
    this.shareInboxPollTimer = setInterval(
      () => void this.pollShareInbox(),
      NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS,
    );
    const ep = getSavedNullapadShareEndpoint();
    const sec = NULLAPAD_SHARE_INBOX_POLL_INTERVAL_MS / 1000;
    this.reportShareInboxPollStatus(
      ep
        ? `Share inbox: polling every ${sec}s at ${ep}/inbox (same id = same slot) …`
        : "Share inbox: not active (page must be http:// or https:// for same-origin /inbox)",
      0,
      true,
    );
  }

  /** Clears the timer and starts again — immediate poll + aligned interval (e.g. after outbound share or secret Sync). */
  private restartShareInboxPolling(): void {
    if (this.shareInboxPollTimer != null) {
      clearInterval(this.shareInboxPollTimer);
      this.shareInboxPollTimer = null;
    }
    this.startShareInboxPolling();
  }

  private async pollShareInbox(): Promise<void> {
    if (this.shareInboxPollInFlight) {
      this.shareInboxPollReplayPending = true;
      return;
    }
    const endpoint = getSavedNullapadShareEndpoint();
    if (!endpoint) {
      this.shareInboxLastHandledFingerprint = null;
      this.hideShareInboxOfferBar();
      this.reportShareInboxPollStatus(
        "Share inbox: not polling (need http(s) page — same host must serve GET /inbox)",
        45_000,
      );
      return;
    }
    this.shareInboxPollInFlight = true;
    try {
      const recipientId = getOrCreateNullapadInstanceSecret();
      const msg = await fetchNullapadShareInboxMessage({ endpoint, recipientId });
      if (msg === null) {
        this.shareInboxMultipartInflightKey = null;
        this.shareInboxMultipartResolvedKey = null;
        this.shareInboxLastHandledFingerprint = null;
        const hasPendingOffer =
          (!this.shareInboxOfferBar.hidden && this.shareInboxOfferState !== null) ||
          this.shareInboxStagedFp !== null;
        if (!hasPendingOffer) {
          this.hideShareInboxOfferBar();
        }
        // force: clear a prior "poll error" line immediately (throttle would keep errors visible up to 10s).
        this.reportShareInboxPollStatus(
          `Share inbox: polled OK, empty · ${new Date().toLocaleTimeString()}`,
          10_000,
          true,
        );
        return;
      }
      if (msg.mode === "multipart") {
        this.shareInboxReceiveMultipartOffer(msg, endpoint, recipientId);
        return;
      }
      const fp = await resolveNullapadShareInboxFingerprint(msg);
      if (fp === this.shareInboxLastHandledFingerprint) {
        this.hideShareInboxOfferBar();
        this.reportShareInboxPollStatus(
          "Share inbox: polled, queue unchanged (already accepted/declined this payload)",
          20_000,
          true,
        );
        return;
      }
      await this.stageShareInboxPayloadIfNeeded(msg, fp);
      if (document.visibilityState !== "visible") {
        this.reportShareInboxPollStatus(
          "Share inbox: new message (saved locally) — focus this tab to open or decline",
          12_000,
          true,
        );
        return;
      }
      if (this.shareInboxOfferState?.fp === fp && !this.shareInboxOfferBar.hidden) {
        return;
      }
      await this.presentShareInboxOfferBar(msg, fp);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      this.reportShareInboxPollStatus(`Share inbox poll error: ${detail.slice(0, 240)}`, 8000);
    } finally {
      this.shareInboxPollInFlight = false;
      if (this.shareInboxPollReplayPending) {
        this.shareInboxPollReplayPending = false;
        void this.pollShareInbox();
      }
    }
  }

  /**
   * GET /inbox returned multipart metadata: fetch all segments, then reuse the same Accept bar as small shares.
   */
  private shareInboxReceiveMultipartOffer(
    meta: NullapadShareInboxMultipartMeta,
    endpoint: string,
    recipientId: string,
  ): void {
    const key = `${meta.shareSessionId}:${meta.totalBytes}`;
    if (this.shareInboxMultipartResolvedKey === key) {
      return;
    }
    if (this.shareInboxMultipartInflightKey === key) {
      return;
    }
    this.shareInboxMultipartInflightKey = key;
    void (async () => {
      try {
        this.reportShareInboxPollStatus(
          `Share inbox: downloading large share (${meta.segmentCount} parts)…`,
          0,
          true,
        );
        const simple = await downloadMultipartInboxAsUtf8({
          endpoint,
          recipientId,
          meta,
          onProgress: (i, n) => {
            this.reportShareInboxPollStatus(`Share inbox: part ${i}/${n}…`, 0, true);
          },
        });
        const fp = await resolveNullapadShareInboxFingerprint(simple);
        if (fp === this.shareInboxLastHandledFingerprint) {
          this.hideShareInboxOfferBar();
          this.shareInboxMultipartResolvedKey = key;
          this.reportShareInboxPollStatus(
            "Share inbox: polled, queue unchanged (already accepted/declined this payload)",
            20_000,
            true,
          );
          return;
        }
        await this.stageShareInboxPayloadIfNeeded(simple, fp);
        this.shareInboxMultipartResolvedKey = key;
        if (document.visibilityState !== "visible") {
          this.reportShareInboxPollStatus(
            "Share inbox: new message (saved locally) — focus this tab to open or decline",
            12_000,
            true,
          );
          return;
        }
        if (this.shareInboxOfferState?.fp === fp && !this.shareInboxOfferBar.hidden) {
          return;
        }
        await this.presentShareInboxOfferBar(simple, fp);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        this.reportShareInboxPollStatus(`Share inbox poll error: ${detail.slice(0, 240)}`, 8000);
      } finally {
        if (this.shareInboxMultipartInflightKey === key) {
          this.shareInboxMultipartInflightKey = null;
        }
      }
    })();
  }

  private hideShareInboxOfferBar(): void {
    this.cancelShareInboxAcceptEnableTimer();
    this.shareInboxMultipartResolvedKey = null;
    this.shareInboxOfferBar.hidden = true;
    this.shareInboxOfferState = null;
    this.shareInboxAcceptBtn.disabled = true;
    this.shareInboxAcceptBtn.title = "Available once this tab is active";
    this.shareInboxStagedFp = null;
    void clearStagedShare().catch(() => {});
  }

  private cancelShareInboxAcceptEnableTimer(): void {
    if (this.shareInboxAcceptEnableTimer !== null) {
      clearTimeout(this.shareInboxAcceptEnableTimer);
      this.shareInboxAcceptEnableTimer = null;
    }
  }

  /** Accept stays disabled briefly and while the tab is in the background so the payload is not taken before the window is active. */
  private scheduleShareInboxAcceptEnable(expectedFp: string): void {
    this.cancelShareInboxAcceptEnableTimer();
    this.shareInboxAcceptBtn.disabled = true;
    this.shareInboxAcceptBtn.title = "Available once this tab is active";
    this.shareInboxAcceptEnableTimer = setTimeout(() => {
      this.shareInboxAcceptEnableTimer = null;
      if (this.shareInboxOfferState?.fp !== expectedFp || this.shareInboxOfferBar.hidden) return;
      if (document.visibilityState !== "visible") return;
      this.shareInboxAcceptBtn.disabled = false;
      this.shareInboxAcceptBtn.removeAttribute("title");
    }, 240);
  }

  /** Persist GET /inbox payload to IndexedDB before the user clicks Accept (survives refresh). */
  private async stageShareInboxPayloadIfNeeded(msg: NullapadShareInboxSimpleMessage, fp: string): Promise<void> {
    if (this.shareInboxStagedFp === fp) return;
    try {
      await writeStagedShare({
        fp,
        utf8: msg.utf8,
        tabTitle: msg.tabTitle,
        byteLength: msg.byteLength,
        savedAt: Date.now(),
      });
      this.shareInboxStagedFp = fp;
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      this.reportShareInboxPollStatus(
        `Share inbox: could not cache offer (${detail.slice(0, 120)})`,
        12_000,
        true,
      );
    }
  }

  /** After reload or while the server slot is empty, surface a previously staged offer from IndexedDB. */
  private async tryPresentStagedShareIfNoBar(): Promise<void> {
    if (!this.shareInboxOfferBar.hidden) return;
    let rec: StagedShareRecord | null = null;
    try {
      rec = await readStagedShare();
    } catch {
      return;
    }
    if (!rec) return;
    if (rec.fp === this.shareInboxLastHandledFingerprint) {
      this.shareInboxStagedFp = null;
      await clearStagedShare().catch(() => {});
      return;
    }
    this.shareInboxStagedFp = rec.fp;
    const msg: NullapadShareInboxSimpleMessage = {
      utf8: rec.utf8,
      tabTitle: rec.tabTitle,
      byteLength: rec.byteLength,
    };
    await this.presentShareInboxOfferBar(msg, rec.fp);
  }

  private async presentShareInboxOfferBar(msg: NullapadShareInboxSimpleMessage, fp: string): Promise<void> {
    await this.stageShareInboxPayloadIfNeeded(msg, fp);
    this.shareInboxOfferState = { msg, fp };
    const displayTitle = (msg.tabTitle?.trim() || "(no suggested name)").slice(0, 160);
    this.shareInboxOfferText.textContent = `Incoming share · ${displayTitle} · ${AppShell.formatShareInboxByteSize(msg.byteLength)}`;
    this.shareInboxOfferBar.hidden = false;
    this.scheduleShareInboxAcceptEnable(fp);
    if (this.documents.active?.viewMode === "text") {
      // Keep typing uninterrupted when the async offer bar appears during polling.
      this.editor.focus({ preventScroll: true });
    }
  }

  /** Bar Accept runs {@link completeShareInboxAccept} directly — there is no intermediate modal. */
  private async onShareInboxAcceptClick(): Promise<void> {
    if (this.shareInboxAcceptBtn.disabled) return;
    if (!this.shareInboxOfferState?.fp) return;
    await this.completeShareInboxAccept();
  }

  private async completeShareInboxAccept(): Promise<void> {
    let st = this.shareInboxOfferState;
    if (!st) {
      try {
        const rec = await readStagedShare();
        if (!rec) return;
        st = {
          msg: { utf8: rec.utf8, tabTitle: rec.tabTitle, byteLength: rec.byteLength },
          fp: rec.fp,
        };
      } catch {
        return;
      }
    }
    const { msg, fp } = st;
    this.hideShareInboxOfferBar();
    this.flushEditorToBuffer();
    // Unbundle markup marks that may have been embedded by the sender.
    const { text: cleanText, marks: receivedMarks } = unbundleSharePayload(msg.utf8);
    this.documents.addTabFromShareInboxPayload({ utf8: cleanText, tabTitle: msg.tabTitle });
    if (receivedMarks && receivedMarks.length > 0) {
      const newDoc = this.documents.active;
      if (newDoc) {
        this.markupLayerData.set(newDoc.id, receivedMarks);
        persistMarkupToStorage(this.markupStorageKeyForDoc(newDoc), receivedMarks);
      }
    }
    this.shareInboxLastHandledFingerprint = fp;
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateSyntaxHighlightLayer();
    if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
    if (this.documents.active?.language === "tex") this.scheduleTexPreview();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
    if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
    if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
    this.updateLineNumberGutter();
    this.rebuildMenubar();
    this.schedulePersistTabSession();
    await this.tryDeleteShareInboxSlot("accepted", fp);
    this.updateStatusLine(`Opened from share inbox: ${this.documents.active?.title ?? ""}`);
    if (this.documents.active?.viewMode === "text") {
      this.editor.focus({ preventScroll: true });
    }
  }

  private async onShareInboxDeclineClick(): Promise<void> {
    let st = this.shareInboxOfferState;
    if (!st) {
      try {
        const rec = await readStagedShare();
        if (!rec) return;
        st = {
          msg: { utf8: rec.utf8, tabTitle: rec.tabTitle, byteLength: rec.byteLength },
          fp: rec.fp,
        };
      } catch {
        return;
      }
    }
    const { fp } = st;
    this.hideShareInboxOfferBar();
    this.shareInboxLastHandledFingerprint = fp;
    await this.tryDeleteShareInboxSlot("declined", fp);
    this.updateStatusLine("Share inbox offer declined.");
    if (this.documents.active?.viewMode === "text") {
      this.editor.focus({ preventScroll: true });
    }
  }

  /**
   * Remove the message from the server so reloads and the next sender do not see a stale slot.
   *
   * The `ifFingerprint` guard prevents us from clobbering a second message the sender raced in
   * between our GET and this DELETE: the server returns 409 (mapped to `"superseded"`) and we
   * leave the newer payload in place. We also clear the dedup cursor and kick an immediate poll so
   * the new message shows up without waiting for the next 10 s tick.
   */
  private async tryDeleteShareInboxSlot(
    context: "accepted" | "declined",
    ifFingerprint: string,
  ): Promise<void> {
    const endpoint = getSavedNullapadShareEndpoint();
    if (!endpoint) return;
    try {
      const result = await deleteNullapadShareInboxMessage({
        endpoint,
        recipientId: getOrCreateNullapadInstanceSecret(),
        ifFingerprint,
      });
      if (result === "superseded") {
        this.shareInboxLastHandledFingerprint = null;
        void this.pollShareInbox();
      }
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      this.reportShareInboxPollStatus(
        `Share inbox: could not clear server slot (${context}) — ${detail.slice(0, 200)}`,
        8000,
        true,
      );
    }
  }

  private mountNullaMicTrackToolbarButton(host: HTMLElement): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-toolbar-btn npp-toolbar-btn--nulla-mic-track";
    btn.title =
      "Max mic (hex64): on a .max tab records WebM into the buffer; elsewhere opens Recording-*.max first";
    btn.setAttribute("aria-label", btn.title);
    btn.innerHTML = `<span class="npp-toolbar-icon npp-ico-nulla-mic" aria-hidden="true"></span>`;
    this.nullaMicTrackToolbarBtn = btn;
    btn.addEventListener("click", () => {
      // Call directly: toolbar must work even if command dispatch is skipped (e.g. disabled flag drift).
      this.toggleNullaMicFromToolbar();
      this.shell.dispatchEvent(
        new CustomEvent(NPP_COMMAND_EVENT, {
          bubbles: true,
          composed: true,
          detail: { commandId: CMD.FILE_NULLA_MIC_INJECT, source: "ui" as const },
        }),
      );
    });
    host.appendChild(btn);
  }

  private mountOeisToolbarButton(host: HTMLElement): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-toolbar-btn npp-toolbar-btn--oeis";
    btn.title = "New OEIS sequence tab";
    btn.setAttribute("aria-label", btn.title);
    btn.innerHTML = `<span class="npp-toolbar-oeis-label" aria-hidden="true">OEIS</span>`;
    btn.addEventListener("mousedown", (ev) => {
      ev.preventDefault();
    });
    btn.addEventListener("click", () => {
      void this.dispatcher.executeCommand(CMD.FILE_NEW);
      void this.dispatcher.executeCommand(CMD.LANG_OEIS);
    });
    host.appendChild(btn);
  }

  private mountDictationToolbarButton(host: HTMLElement): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-toolbar-btn npp-toolbar-btn--voice-transcribe";
    btn.title = "Speech to text — click to dictate into the editor (model downloads on first use)";
    btn.setAttribute("aria-label", btn.title);
    btn.innerHTML = `<span class="npp-toolbar-icon npp-ico-mic-transcribe" aria-hidden="true"></span>`;
    this.voiceBtn = btn;
    btn.addEventListener("click", () => this.toggleVoiceInput());
    host.appendChild(btn);
  }

  /** Yellow mic: speech-to-text into the text editor (hidden on legacy Nulla video tabs — use the red mic for hex audio there). */
  private syncDictationToolbarButton(): void {
    const btn = this.voiceBtn;
    if (!btn) return;
    const d = this.documents.active;
    const nullaVideo = d?.viewMode === "video";
    if (nullaVideo) {
      btn.hidden = true;
      btn.classList.remove("npp-toolbar-btn--voice-active", "npp-toolbar-btn--voice-loading");
    } else {
      btn.hidden = false;
      btn.disabled = false;
      const t = "Speech to text — click to dictate into the editor (model downloads on first use)";
      btn.title = t;
      btn.setAttribute("aria-label", t);
      btn.classList.toggle("npp-toolbar-btn--voice-active", this.voiceRecording);
      btn.classList.toggle(
        "npp-toolbar-btn--voice-loading",
        this.voiceModelLoading && this.voiceRecording,
      );
    }
    this.syncNullaMicTrackToolbarButton();
  }

  /** Red-dot mic: hex64 WebM into an open `.max` text buffer, or a new `Recording-*.max` tab; legacy Nulla video tabs still use section mic-track. */
  private syncNullaMicTrackToolbarButton(): void {
    const btn = this.nullaMicTrackToolbarBtn;
    if (!btn) return;
    const d = this.documents.active;
    const nullaVideo = d?.viewMode === "video";
    btn.hidden = false;

    const mainRec = Boolean(this.nullaRecorder && this.nullaRecorder.state !== "inactive");
    const clipRec = Boolean(this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive");
    const maxMicRec = Boolean(this.maxHexMicRecorder && this.maxHexMicRecorder.state !== "inactive");

    if (!nullaVideo) {
      btn.classList.remove("npp-toolbar-btn--nulla-mic-track-active");
      const maxHex = this.docIsMaxHexRecordTarget(d);
      if (maxHex) {
        // Keep enabled while Max mic-only recording is active so the second click can stop it.
        btn.disabled = mainRec || clipRec;
        btn.classList.toggle("npp-toolbar-btn--nulla-mic-track-active", maxMicRec);
        const t = mainRec
          ? "Stop Max camera recording before a mic-only hex capture."
          : clipRec
            ? "Stop the in-progress Nulla mic-track capture first."
            : maxMicRec
              ? "Stop mic recording — hex64 WebM will be inserted into this .max buffer"
              : "Record mic only as hex64 audio (WebM) into this .max buffer";
        btn.title = t;
        btn.setAttribute("aria-label", t);
        return;
      }
      btn.disabled = mainRec || clipRec;
      const t = mainRec
        ? "Stop camera recording before opening a new .max tab for mic-only hex capture."
        : clipRec
          ? "Stop the in-progress Nulla mic-track capture first (or switch to that tab)."
          : "Record mic as hex64 WebM — opens a new empty Recording-*.max tab if needed";
      btn.title = t;
      btn.setAttribute("aria-label", t);
      return;
    }

    const raw = d?.content ?? "";
    const invalidNullaBuffer = raw.trim().length > 0 && !isNullaContent(raw);
    const lastIdx = raw ? lastNullaMicTargetContentSectionIndex(raw) : -1;
    const recordingLastMicTarget =
      Boolean(this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive") &&
      this.nullaMicTrackSectionIndex === lastIdx;
    const otherMicTargetRecording =
      Boolean(this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive") &&
      !recordingLastMicTarget;

    btn.disabled =
      invalidNullaBuffer || (mainRec && !recordingLastMicTarget) || otherMicTargetRecording;
    if (invalidNullaBuffer) {
      const t = "This tab's buffer is not valid Nulla — fix it before recording a mic track.";
      btn.title = t;
      btn.setAttribute("aria-label", t);
    } else if (lastIdx >= 0) {
      const t =
        "Nulla mic track: each click appends a new audio block, then records hex64 into it — click again to stop while recording";
      btn.title = t;
      btn.setAttribute("aria-label", t);
    } else {
      const t =
        "Nulla mic track — a minimal clip layout is added if needed, then each capture appends a new audio section";
      btn.title = t;
      btn.setAttribute("aria-label", t);
    }
    btn.classList.toggle("npp-toolbar-btn--nulla-mic-track-active", recordingLastMicTarget);
  }

  private toggleNullaMicFromToolbar(): void {
    const d = this.documents.active;
    if (!d) return;
    if (this.docIsMaxHexRecordTarget(d)) {
      void this.toggleMaxHexMicRecording();
      return;
    }
    this.flushEditorToBuffer();
    this.documents.checkpointHistory(this.editor.value);
    this.documents.addEmptyMaxRecordingTab();
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateStatusLine();
    this.schedulePersistTabSession();
    this.syncDictationToolbarButton();
    const next = this.documents.active;
    if (!next || !this.docIsMaxHexRecordTarget(next)) return;
    void this.toggleMaxHexMicRecording();
  }

  private ensureVoiceWorker(): Worker {
    if (this.voiceWorker) return this.voiceWorker;
    const worker = new Worker(new URL("./editor/voiceWorker.ts", import.meta.url), { type: "module" });
    worker.addEventListener("message", (ev: MessageEvent) => this.onVoiceWorkerMessage(ev.data));
    this.voiceWorker = worker;
    return worker;
  }

  private onVoiceWorkerMessage(msg: { type: string; [k: string]: unknown }): void {
    switch (msg.type) {
      case "progress":
        this.updateStatusLine(`Downloading speech-to-text model… ${msg.file} ${msg.progress}%`);
        break;
      case "loaded":
        this.voiceModelReady = true;
        this.voiceModelLoading = false;
        this.syncDictationToolbarButton();
        if (this.voiceRecording) {
          this.updateStatusLine("Dictation: listening…");
        } else {
          this.updateStatusLine("Speech-to-text model ready");
        }
        break;
      case "result": {
        const text = msg.text as string;
        if (!text) break;
        const spacer = this.voiceTotalInserted > 0 ? " " : "";
        const ed = this.editor;
        const insertAt = this.voiceInsertionPoint + this.voiceTotalInserted;
        const before = ed.value.slice(0, insertAt);
        const after = ed.value.slice(insertAt);
        ed.value = before + spacer + text + after;
        this.voiceTotalInserted += spacer.length + text.length;
        ed.selectionStart = ed.selectionEnd = insertAt + spacer.length + text.length;
        this.onEditorInput();
        if (this.voiceRecording) {
          this.updateStatusLine("Dictation: listening…");
        }
        break;
      }
      case "error": {
        const errMsg = `Speech-to-text error: ${msg.message}`;
        this.stopVoiceRecording(errMsg);
        break;
      }
    }
  }

  private toggleVoiceInput(): void {
    if (this.voiceRecording) {
      this.stopVoiceRecording();
      return;
    }
    if (this.voiceModelLoading) return;
    void this.startVoiceRecording();
  }

  private async startVoiceRecording(): Promise<void> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      this.updateStatusLine(`Microphone error: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }

    this.voiceStream = stream;
    this.voiceRecording = true;
    this.voiceInsertionPoint = this.editor.selectionStart;
    this.voiceTotalInserted = 0;
    this.voiceChunkBuffer = [];

    const worker = this.ensureVoiceWorker();
    if (!this.voiceModelReady && !this.voiceModelLoading) {
      this.voiceModelLoading = true;
      this.updateStatusLine("Downloading speech-to-text model (first use)…");
      worker.postMessage({ type: "load" });
    } else if (this.voiceModelReady) {
      this.updateStatusLine("Dictation: listening…");
    }
    this.syncDictationToolbarButton();

    const audioCtx = new AudioContext({ sampleRate: 16000 });
    this.voiceAudioCtx = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      if (!this.voiceRecording) return;
      const input = e.inputBuffer.getChannelData(0);
      this.voiceChunkBuffer.push(new Float32Array(input));
    };
    source.connect(processor);
    processor.connect(audioCtx.destination);
    this.voiceSourceNode = source;
    this.voiceProcessorNode = processor;

    this.voiceChunkInterval = setInterval(() => this.flushVoiceChunk(), 3000);
  }

  private flushVoiceChunk(): void {
    if (!this.voiceModelReady || this.voiceChunkBuffer.length === 0) return;
    const totalLen = this.voiceChunkBuffer.reduce((n, b) => n + b.length, 0);
    const merged = new Float32Array(totalLen);
    let offset = 0;
    for (const buf of this.voiceChunkBuffer) {
      merged.set(buf, offset);
      offset += buf.length;
    }
    this.voiceChunkBuffer = [];

    const hasSignal = merged.some((v) => Math.abs(v) > 0.01);
    if (!hasSignal) return;

    this.voiceWorker?.postMessage({ type: "transcribe", audio: merged }, [merged.buffer]);
    this.updateStatusLine("Dictation: transcribing…");
  }

  private stopVoiceRecording(statusOverride?: string): void {
    this.flushVoiceChunk();
    this.voiceRecording = false;
    if (this.voiceChunkInterval) {
      clearInterval(this.voiceChunkInterval);
      this.voiceChunkInterval = null;
    }
    if (this.voiceProcessorNode) {
      this.voiceProcessorNode.disconnect();
      this.voiceProcessorNode = null;
    }
    if (this.voiceSourceNode) {
      this.voiceSourceNode.disconnect();
      this.voiceSourceNode = null;
    }
    if (this.voiceStream) {
      for (const track of this.voiceStream.getTracks()) track.stop();
      this.voiceStream = null;
    }
    if (this.voiceAudioCtx) {
      void this.voiceAudioCtx.close();
      this.voiceAudioCtx = null;
    }
    if (statusOverride) {
      this.updateStatusLine(statusOverride);
    } else if (this.voiceTotalInserted > 0) {
      this.updateStatusLine(`Dictation: inserted ${this.voiceTotalInserted} characters`);
    } else {
      this.updateStatusLine("Dictation stopped");
    }
    this.syncDictationToolbarButton();
  }

  private async toggleNullaRecording(): Promise<void> {
    if (this.nullaRecorder && this.nullaRecorder.state !== "inactive") {
      this.nullaRecorder.stop();
      return;
    }
    if (this.maxHexMicRecorder && this.maxHexMicRecorder.state !== "inactive") {
      this.updateStatusLine("Stop the Max mic-only recording on this tab before camera recording.");
      return;
    }
    if (this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive") {
      this.updateStatusLine("Stop the Nulla mic-track capture on this tab before starting camera recording.");
      return;
    }
    await this.startNullaRecording();
  }

  private async startMaxHexRecording(): Promise<void> {
    if (!this.docIsMaxHexRecordTarget(this.documents.active)) return;
    if (this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive") {
      this.updateStatusLine("Stop the Nulla mic-track capture before recording into this .max file.");
      return;
    }
    if (this.maxHexMicRecorder && this.maxHexMicRecorder.state !== "inactive") {
      this.updateStatusLine("Stop the Max mic-only recording before camera recording.");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (e) {
      this.updateStatusLine(`Camera error: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    this.maxHexRecordingActive = true;
    this.maxHexRecordTargetTab = this.documents.active;
    this.nullaStream = stream;
    this.nullaChunks = [];
    this.nullaAudioChunks = [];
    this.nullaAudioRecorder = null;

    this.flushEditorToBuffer();

    this.editorRow.classList.remove("npp-editor-row--diff-tab");
    this.editorRow.classList.add("npp-editor-row--binary-preview");
    this.editorRow.classList.remove(
      "npp-editor-row--markdown",
      "npp-editor-row--tex",
      "npp-editor-row--html",
      "npp-editor-row--oeis",
      "npp-editor-row--csv",
      "npp-md-preview-only",
      "npp-tex-preview-only",
      "npp-max-preview-only",
      "npp-html-preview-only",
      "npp-oeis-preview-only",
      "npp-csv-preview-only",
    );
    this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image");
    this.binaryPreview.classList.add("npp-binary-preview--video");
    this.binaryPreviewImg.removeAttribute("src");
    this.binaryPreviewPdf.removeAttribute("src");
    this.binaryPreviewVideo.srcObject = stream;
    this.binaryPreviewVideo.muted = true;
    this.binaryPreviewVideo.controls = false;
    this.binaryPreviewVideo.hidden = false;
    void this.binaryPreviewVideo.play();
    this.binaryPreview.classList.add("npp-binary-preview--recording");
    this.tabBar?.refresh();

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const recorder = new MediaRecorder(stream, { mimeType });
    this.nullaRecorder = recorder;
    this.nullaStartTime = Date.now();

    const audioMime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
    if (audioMime) {
      try {
        const ar = new MediaRecorder(stream, { mimeType: audioMime });
        this.nullaAudioRecorder = ar;
        ar.ondataavailable = (e) => {
          if (e.data.size > 0) this.nullaAudioChunks.push(e.data);
        };
        ar.start(1000);
      } catch {
        this.nullaAudioRecorder = null;
        this.nullaAudioChunks = [];
      }
    }

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.nullaChunks.push(e.data);
    };
    recorder.onstop = () => {
      void this.finalizeNullaRecording();
    };
    recorder.start(1000);
    const title = this.documents.active?.title ?? "Recording";
    this.updateStatusLine(`${title} | Max (.max) | Recording video (hex64)…`);
    this.syncDictationToolbarButton();
  }

  private async startNullaRecording(): Promise<void> {
    if (this.docIsMaxHexRecordTarget(this.documents.active)) {
      await this.startMaxHexRecording();
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (e) {
      this.updateStatusLine(`Camera error: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    this.nullaStream = stream;
    this.nullaChunks = [];
    this.nullaAudioChunks = [];
    this.nullaAudioRecorder = null;

    this.flushEditorToBuffer();
    this.documents.checkpointHistory(this.editor.value);
    this.documents.addEmptyMaxRecordingTab();
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateStatusLine();
    this.schedulePersistTabSession();

    this.maxHexRecordingActive = true;
    this.maxHexRecordTargetTab = this.documents.active;

    this.editorRow.classList.remove("npp-editor-row--diff-tab");
    this.editorRow.classList.add("npp-editor-row--binary-preview");
    this.editorRow.classList.remove(
      "npp-editor-row--markdown",
      "npp-editor-row--tex",
      "npp-editor-row--html",
      "npp-editor-row--oeis",
      "npp-editor-row--csv",
      "npp-md-preview-only",
      "npp-tex-preview-only",
      "npp-html-preview-only",
      "npp-oeis-preview-only",
      "npp-csv-preview-only",
    );
    this.binaryPreview.classList.remove("npp-binary-preview--pdf", "npp-binary-preview--image");
    this.binaryPreview.classList.add("npp-binary-preview--video");
    this.binaryPreviewImg.removeAttribute("src");
    this.binaryPreviewPdf.removeAttribute("src");
    this.binaryPreviewVideo.srcObject = stream;
    this.binaryPreviewVideo.muted = true;
    this.binaryPreviewVideo.controls = false;
    this.binaryPreviewVideo.hidden = false;
    void this.binaryPreviewVideo.play();
    this.binaryPreview.classList.add("npp-binary-preview--recording");
    this.tabBar?.refresh();

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const recorder = new MediaRecorder(stream, { mimeType });
    this.nullaRecorder = recorder;
    this.nullaStartTime = Date.now();

    const audioMime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
    if (audioMime) {
      try {
        const ar = new MediaRecorder(stream, { mimeType: audioMime });
        this.nullaAudioRecorder = ar;
        ar.ondataavailable = (e) => {
          if (e.data.size > 0) this.nullaAudioChunks.push(e.data);
        };
        ar.start(1000);
      } catch {
        this.nullaAudioRecorder = null;
        this.nullaAudioChunks = [];
      }
    }

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.nullaChunks.push(e.data);
    };
    recorder.onstop = () => {
      void this.finalizeNullaRecording();
    };
    recorder.start(1000);
    const title = this.documents.active?.title ?? "Recording";
    this.updateStatusLine(`${title} | Max (.max) | Recording video (hex64)…`);
    this.syncDictationToolbarButton();
  }

  private async drainParallelNullaAudio(): Promise<Blob | null> {
    const ar = this.nullaAudioRecorder;
    this.nullaAudioRecorder = null;
    if (ar && ar.state !== "inactive") {
      await new Promise<void>((res) => {
        ar.addEventListener("stop", () => res(), { once: true });
        ar.stop();
      });
    }
    const chunks = this.nullaAudioChunks;
    this.nullaAudioChunks = [];
    if (chunks.length === 0) return null;
    return new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
  }

  /**
   * Serialize in-buffer Nulla updates so two async finalizes (e.g. back-to-back camera stops while
   * `blobToVideoSection` is still running) cannot both read stale `content` and overwrite each other.
   */
  private enqueueNullaContentWrite(task: () => Promise<void>): Promise<void> {
    const next = this.nullaContentWriteChain.then(
      () => task(),
      () => task(),
    );
    this.nullaContentWriteChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  private async finalizeNullaRecording(): Promise<void> {
    const duration = (Date.now() - this.nullaStartTime) / 1000;
    const audioBlob = await this.drainParallelNullaAudio();
    if (this.nullaStream) {
      for (const track of this.nullaStream.getTracks()) track.stop();
      this.nullaStream = null;
    }
    this.binaryPreview.classList.remove("npp-binary-preview--recording");
    this.binaryPreviewVideo.srcObject = null;
    this.binaryPreviewVideo.muted = false;
    this.binaryPreviewVideo.controls = true;
    this.binaryPreviewVideo.hidden = true;

    const blob = new Blob(this.nullaChunks, { type: this.nullaChunks[0]?.type || "video/webm" });
    this.nullaChunks = [];
    this.nullaRecorder = null;

    if (this.maxHexRecordingActive) {
      await this.finalizeMaxHexRecordingIntoBuffer(duration, blob, audioBlob);
      return;
    }

    const targetTab = this.documents.active;
    if (!targetTab || targetTab.viewMode !== "video") return;

    this.updateStatusLine("Encoding video…");
    let videoSection = await blobToVideoSection(blob, duration);
    if (audioBlob && audioBlob.size > 0) {
      const voice = await blobToVoiceEnrichment(audioBlob, duration);
      videoSection = {
        ...videoSection,
        meta: { ...videoSection.meta, voice },
      };
    }

    try {
      await this.enqueueNullaContentWrite(async () => {
        if (this.documents.active === targetTab) {
          this.flushNullaSectionsToBuffer();
        }

        let sections: NullaSection[];
        if (targetTab.content) {
          sections = parseSections(targetTab.content);
          const textIdx = sections.filter((s) => s.type === "text").length - 1;
          sections = insertVideoAfterText(
            sections,
            textIdx,
            videoSection as NullaSection & { type: "video" },
          );
        } else {
          sections = [
            { type: "text", content: "" },
            videoSection,
            { type: "text", content: "" },
          ];
        }

        targetTab.content = encodeSections(sections);
        targetTab.dirty = true;

        if (this.documents.active === targetTab) {
          this.renderNullaSections(targetTab);
        }
        this.tabBar?.refresh();
        this.schedulePersistTabSession();
        this.syncDictationToolbarButton();

        const videoCount = sections.filter((s) => s.type === "video").length;
    const voiceNote =
      audioBlob && audioBlob.size > 0 ? " + parallel mic track (hex64 on video)" : "";
        this.updateStatusLine(
          `${targetTab.title} | Nulla video (${videoCount} clips) | ${Math.round(duration * 10) / 10}s — Recording saved${voiceNote}`,
        );
      });
    } catch {
      this.updateStatusLine("Encoding video failed.");
      this.syncDictationToolbarButton();
    }
  }

  private async finalizeMaxHexRecordingIntoBuffer(
    duration: number,
    videoBlob: Blob,
    audioBlob: Blob | null,
  ): Promise<void> {
    this.maxHexRecordingActive = false;
    const targetTab = this.maxHexRecordTargetTab;
    this.maxHexRecordTargetTab = null;
    try {
      this.editorRow.classList.remove("npp-editor-row--binary-preview");
      this.binaryPreview.classList.remove("npp-binary-preview--video", "npp-binary-preview--recording");

      if (!targetTab || !this.docIsMaxHexRecordTarget(targetTab)) {
        this.updateStatusLine("Max recording discarded.");
        return;
      }
      if (this.documents.active !== targetTab) {
        this.updateStatusLine("Max recording discarded — stay on this .max tab until encoding finishes.");
        return;
      }
      if (videoBlob.size === 0) {
        this.updateStatusLine("Recording empty; nothing inserted.");
        return;
      }
      if (videoBlob.size > MAX_MAX_HEX_MEDIA_CLIP_BYTES) {
        this.updateStatusLine("Video too large for Max hex embed (40 MB cap).");
        return;
      }

      let audioBytes: Uint8Array | null = null;
      if (audioBlob && audioBlob.size > 0) {
        if (audioBlob.size > MAX_MAX_HEX_MEDIA_CLIP_BYTES) {
          this.updateStatusLine("Parallel mic track too large for hex embed — inserted video only.");
        } else {
          audioBytes = new Uint8Array(await audioBlob.arrayBuffer());
        }
      }

      const vbytes = new Uint8Array(await videoBlob.arrayBuffer());
      const stem =
        targetTab.title
          .replace(/\.max$/i, "")
          .replace(/\.[^.\\/]+$/, "")
          .replace(/[_-]+/g, " ")
          .trim() || "Clip";

      let block = buildMaxVideoHexMarkdownLink(`${stem} · video ${Math.round(duration * 10) / 10}s`, vbytes);
      if (audioBytes?.length) {
        block += buildMaxAudioHexMarkdownLink(`${stem} · mic`, audioBytes);
      }

      this.flushEditorToBuffer();
      const ed = this.editor;
      const lo = Math.min(ed.selectionStart, ed.selectionEnd);
      const hi = Math.max(ed.selectionStart, ed.selectionEnd);
      const next = ed.value.slice(0, lo) + block + ed.value.slice(hi);
      const cursor = lo + block.length;
      this.commitEditor(next, cursor, cursor);
      targetTab.dirty = true;
      this.tabBar?.refresh();
      this.schedulePersistTabSession();
      this.scheduleMaxPreview();
      const micNote = audioBytes?.length ? " + mic (hex64)" : "";
      this.updateStatusLine(`${targetTab.title} | Inserted WebM into Max buffer${micNote}`);
    } catch (e) {
      this.updateStatusLine(`Max recording failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      this.applyEditorLayoutForLanguage();
      this.syncDictationToolbarButton();
    }
  }

  private async toggleMaxHexMicRecording(): Promise<void> {
    if (!this.docIsMaxHexRecordTarget(this.documents.active)) return;
    if (this.nullaRecorder && this.nullaRecorder.state !== "inactive") {
      void alert("Stop camera recording on this tab before a mic-only capture.");
      return;
    }
    if (this.maxHexMicRecorder && this.maxHexMicRecorder.state !== "inactive") {
      this.maxHexMicRecorder.stop();
      return;
    }
    await this.startMaxHexMicRecording();
  }

  private async startMaxHexMicRecording(): Promise<void> {
    const d = this.documents.active;
    if (!d || !this.docIsMaxHexRecordTarget(d)) return;
    if (this.nullaRecorder && this.nullaRecorder.state !== "inactive") return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      this.updateStatusLine(`Mic error: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    this.maxHexMicStream = stream;
    this.maxHexMicChunks = [];
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
    if (!mimeType) {
      for (const t of stream.getTracks()) t.stop();
      this.maxHexMicStream = null;
      this.updateStatusLine("No supported audio/webm MediaRecorder type.");
      return;
    }
    const rec = new MediaRecorder(stream, { mimeType });
    this.maxHexMicRecorder = rec;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) this.maxHexMicChunks.push(e.data);
    };
    rec.onstop = () => {
      void this.finalizeMaxHexMicRecording();
    };
    rec.start(500);
    this.updateStatusLine(`${d.title} | Max mic (hex64) — recording… click mic again to stop`);
    this.syncDictationToolbarButton();
  }

  private async finalizeMaxHexMicRecording(): Promise<void> {
    try {
      if (this.maxHexMicStream) {
        for (const t of this.maxHexMicStream.getTracks()) t.stop();
        this.maxHexMicStream = null;
      }
      this.maxHexMicRecorder = null;
      const chunks = this.maxHexMicChunks;
      this.maxHexMicChunks = [];
      const targetTab = this.documents.active;
      const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
      if (!this.docIsMaxHexRecordTarget(targetTab)) {
        this.updateStatusLine("Mic recording discarded.");
        return;
      }
      if (blob.size === 0) {
        this.updateStatusLine("Mic recording empty.");
        return;
      }
      if (blob.size > MAX_MAX_HEX_MEDIA_CLIP_BYTES) {
        this.updateStatusLine("Mic recording too large for hex embed (40 MB cap).");
        return;
      }
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const stem =
        (targetTab?.title ?? "Clip")
          .replace(/\.max$/i, "")
          .replace(/\.[^.\\/]+$/, "")
          .replace(/[_-]+/g, " ")
          .trim() || "Clip";
      const block = buildMaxAudioHexMarkdownLink(`${stem} · mic`, bytes);
      this.flushEditorToBuffer();
      const ed = this.editor;
      const lo = Math.min(ed.selectionStart, ed.selectionEnd);
      const hi = Math.max(ed.selectionStart, ed.selectionEnd);
      const next = ed.value.slice(0, lo) + block + ed.value.slice(hi);
      const cursor = lo + block.length;
      this.commitEditor(next, cursor, cursor);
      if (targetTab) targetTab.dirty = true;
      this.tabBar?.refresh();
      this.schedulePersistTabSession();
      this.scheduleMaxPreview();
      this.updateStatusLine(`${targetTab?.title ?? ""} | Inserted mic clip (hex64) into Max buffer`);
    } finally {
      this.syncDictationToolbarButton();
    }
  }

  private toggleNullaMicTrackRecording(sectionIndex: number): void {
    if (this.maxHexMicRecorder && this.maxHexMicRecorder.state !== "inactive") {
      void alert("Stop the Max mic-only recording on this tab first.");
      this.syncDictationToolbarButton();
      return;
    }
    if (this.nullaRecorder && this.nullaRecorder.state !== "inactive") {
      void alert("Stop Nulla camera recording before starting a separate mic-track capture.");
      this.syncDictationToolbarButton();
      return;
    }
    if (this.nullaMicTrackRecorder && this.nullaMicTrackRecorder.state !== "inactive") {
      if (this.nullaMicTrackSectionIndex !== sectionIndex) {
        void alert("Stop the other mic-track capture first (or finish it on that section).");
        this.syncDictationToolbarButton();
        return;
      }
      this.nullaMicTrackRecorder.stop();
      this.syncDictationToolbarButton();
      return;
    }
    void this.startNullaMicTrackRecording(sectionIndex);
  }

  private async startNullaMicTrackRecording(sectionIndex: number): Promise<void> {
    try {
      const d = this.documents.active;
      if (!d || d.viewMode !== "video") return;
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        this.updateStatusLine(`Mic error: ${e instanceof Error ? e.message : String(e)}`);
        return;
      }
      this.nullaMicTrackStream = stream;
      this.nullaMicTrackChunks = [];
      this.nullaMicTrackSectionIndex = sectionIndex;
      this.nullaMicTrackTargetTab = d;
      this.nullaMicTrackStartTime = Date.now();
      this.nullaMicTrackDiscard = false;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      if (!mimeType) {
        for (const t of stream.getTracks()) t.stop();
        this.nullaMicTrackStream = null;
        this.nullaMicTrackTargetTab = null;
        this.nullaMicTrackSectionIndex = -1;
        this.updateStatusLine("Nulla mic track: no supported audio/webm MediaRecorder type in this browser.");
        return;
      }

      const rec = new MediaRecorder(stream, { mimeType });
      this.nullaMicTrackRecorder = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) this.nullaMicTrackChunks.push(e.data);
      };
      rec.onstop = () => {
        void this.finalizeNullaMicTrackRecording();
      };
      rec.start(500);
      this.updateStatusLine(
        `${d.title} | Nulla mic track (hex64) — recording… (click the mic-track button again to stop)`,
      );
    } finally {
      this.syncDictationToolbarButton();
    }
  }

  private async finalizeNullaMicTrackRecording(): Promise<void> {
    try {
    const discard = this.nullaMicTrackDiscard;
    this.nullaMicTrackDiscard = false;
    const sectionIndex = this.nullaMicTrackSectionIndex;
    this.nullaMicTrackSectionIndex = -1;
    const targetTab = this.nullaMicTrackTargetTab;
    this.nullaMicTrackTargetTab = null;

    if (this.nullaMicTrackStream) {
      for (const t of this.nullaMicTrackStream.getTracks()) t.stop();
      this.nullaMicTrackStream = null;
    }
    this.nullaMicTrackRecorder = null;

    const duration = (Date.now() - this.nullaMicTrackStartTime) / 1000;
    const chunks = this.nullaMicTrackChunks;
    this.nullaMicTrackChunks = [];

    if (discard || sectionIndex < 0) {
      if (targetTab && this.documents.active === targetTab && targetTab.viewMode === "video" && targetTab.content) {
        this.renderNullaSections(targetTab);
      }
      return;
    }

    const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
    if (blob.size === 0) {
      if (targetTab && this.documents.active === targetTab && targetTab.viewMode === "video" && targetTab.content) {
        this.renderNullaSections(targetTab);
      }
      this.updateStatusLine("Nulla mic track empty; nothing saved.");
      return;
    }

    if (!targetTab || this.documents.active !== targetTab || targetTab.viewMode !== "video" || !targetTab.content) {
      this.updateStatusLine("Nulla mic track discarded (stay on this tab until encoding finishes to save).");
      return;
    }

    this.updateStatusLine("Encoding Nulla mic track (hex64)…");

    await this.enqueueNullaContentWrite(async () => {
      if (!targetTab.content) return;
      if (this.documents.active === targetTab) {
        this.flushNullaSectionsToBuffer();
      }
      const sections = parseSections(targetTab.content);
      const s = sections[sectionIndex];
      if (!s || (s.type !== "video" && s.type !== "audio")) {
        this.updateStatusLine(
          "Nulla mic track not saved (that section index is no longer video/audio — document may have changed).",
        );
        return;
      }
      const voice = await blobToVoiceEnrichment(blob, duration);
      if (s.type === "video") {
        const updated: NullaSection & { type: "video" } = {
          type: "video",
          meta: { ...s.meta, voice },
          b64: s.b64,
        };
        sections[sectionIndex] = updated;
      } else {
        sections[sectionIndex] = {
          type: "audio",
          meta: { mime: voice.mime, duration: voice.duration },
          hex64: voice.hex64,
        };
      }
      targetTab.content = encodeSections(sections);
      targetTab.dirty = true;

      if (this.documents.active === targetTab) {
        this.renderNullaSections(targetTab);
      }
      this.tabBar?.refresh();
      this.schedulePersistTabSession();
      this.updateStatusLine(
        `${targetTab.title} | Nulla mic track saved (hex64) — section ${sectionIndex + 1}`,
      );
    });
    } finally {
      this.syncDictationToolbarButton();
    }
  }

  private clearColumnDragListeners(): void {
    this.columnDragCleanup?.();
    this.columnDragCleanup = null;
    // Only remove the drag overlay if it hasn't been adopted as the persistent activeRectSel.
    if (this.columnDragRectOverlay && this.columnDragRectOverlay !== this.activeRectSel?.overlay) {
      this.columnDragRectOverlay.remove();
    }
    this.columnDragRectOverlay = null;
  }

  private dismissRectSel(): void {
    if (!this.activeRectSel) return;
    this.activeRectSel.overlay.remove();
    this.activeRectSel = null;
  }

  private getRectBounds(rs: NonNullable<AppShell["activeRectSel"]>): {
    l0: number;
    l1: number;
    c0: number;
    c1: number;
  } {
    const text = rs.ta.value;
    const a = lineMetricsAtOffset(text, rs.anchor);
    const b = lineMetricsAtOffset(text, rs.focus);
    return {
      l0: Math.min(a.lineIndex0, b.lineIndex0),
      l1: Math.max(a.lineIndex0, b.lineIndex0),
      c0: Math.min(a.col1 - 1, b.col1 - 1),
      c1: Math.max(a.col1 - 1, b.col1 - 1),
    };
  }

  /**
   * Core rectangular edit engine.
   *
   * Replaces columns `c0..c1` (derived from `rs`) on lines `l0..l1` with the content from
   * `pasteLines` (or empty string if `null`).
   *
   * `keepAtCol` — when provided, the rect selection stays alive with cursor collapsed to that
   * column after the edit (used for typing / delete / backspace in multi-line edit mode).
   * When omitted the selection is dismissed and the caret is placed at the start of the
   * edited column.
   */
  private applyRectEdit(
    rs: NonNullable<AppShell["activeRectSel"]>,
    pasteLines: string[] | null,
    keepAtCol?: number,
  ): void {
    const ta = rs.ta;
    const text = ta.value;
    const { l0, l1, c0, c1 } = this.getRectBounds(rs);

    // Preserve original line-ending style.
    const lineEnding = text.indexOf("\r\n") >= 0 ? "\r\n" : text.indexOf("\r") >= 0 ? "\r" : "\n";
    const lines = text.split(lineEnding === "\r\n" ? /\r\n/ : lineEnding === "\r" ? /\r/ : /\n/);

    for (let i = l0; i <= l1 && i < lines.length; i++) {
      const pasteIdx = i - l0;
      const insert =
        pasteLines !== null
          ? (pasteLines[pasteIdx] ?? pasteLines[pasteLines.length - 1] ?? "")
          : "";
      const line = lines[i]!;
      // Pad short lines to reach c0.
      const padded = line.length < c0 ? line + " ".repeat(c0 - line.length) : line;
      lines[i] = padded.slice(0, c0) + insert + padded.slice(c1 + 1);
    }

    const newText = lines.join(lineEnding);
    const savedScroll = { top: ta.scrollTop, left: ta.scrollLeft };

    if (keepAtCol === undefined) {
      // Dismiss BEFORE setting ta.value so onEditorInput doesn't see activeRectSel.
      this.dismissRectSel();
    }

    ta.value = newText;
    ta.scrollTop = savedScroll.top;
    ta.scrollLeft = savedScroll.left;

    if (keepAtCol !== undefined) {
      // Stay in rect mode: update anchor/focus to the new cursor column on every selected row.
      const newAnchor = Math.min(newText.length, offsetAtLineIndex0(newText, l0) + keepAtCol);
      const newFocus = Math.min(newText.length, offsetAtLineIndex0(newText, l1) + keepAtCol);
      try {
        ta.setSelectionRange(newAnchor, newAnchor);
      } catch {
        /* ignore */
      }
      this.activeRectSel = { ta, overlay: rs.overlay, anchor: newAnchor, focus: newFocus };
      rs.overlay.paint(ta, newAnchor, newFocus);
      // Use flag so onEditorInput doesn't dismiss the rect sel we just updated.
      this.rectEditInProgress = true;
      try {
        ta.dispatchEvent(new Event("input", { bubbles: true }));
      } finally {
        this.rectEditInProgress = false;
      }
    } else {
      // Dismissed: place caret at the start of the edited column.
      const caretOffset = Math.min(newText.length, offsetAtLineIndex0(newText, l0) + c0);
      try {
        ta.setSelectionRange(caretOffset, caretOffset);
      } catch {
        /* ignore */
      }
      ta.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  /** Backspace in rect mode: clear selection if non-collapsed, else delete the char before cursor. */
  private applyRectBackspace(rs: NonNullable<AppShell["activeRectSel"]>): void {
    const { l0, l1, c0, c1 } = this.getRectBounds(rs);
    if (c0 < c1) {
      // Has width: clear the selection, cursor at c0.
      this.applyRectEdit(rs, null, c0);
    } else if (c0 > 0) {
      // Collapsed cursor: delete the char at c0-1 on every selected row.
      const text = rs.ta.value;
      const newAnchor = Math.min(text.length, offsetAtLineIndex0(text, l0) + (c0 - 1));
      const newFocus = Math.min(text.length, offsetAtLineIndex0(text, l1) + (c0 - 1));
      this.applyRectEdit({ ...rs, anchor: newAnchor, focus: newFocus }, null, c0 - 1);
    }
    // c0 === 0 and collapsed: nothing to delete.
  }

  private async pasteIntoRectSel(rs: NonNullable<AppShell["activeRectSel"]>): Promise<void> {
    const text = await navigator.clipboard.readText().catch(() => "");
    if (!text) return;
    this.applyRectEdit(rs, text.split(/\r?\n/));
  }

  private onTextareaRectSelKeydown = (e: KeyboardEvent): void => {
    const rs = this.activeRectSel;
    if (!rs) return;
    if (document.activeElement !== rs.ta) return;

    const isCtrl = e.ctrlKey || e.metaKey;

    if (e.key === "Escape") {
      e.preventDefault();
      this.dismissRectSel();
      return;
    }

    if (isCtrl && !e.altKey && e.key.toLowerCase() === "c") {
      e.preventDefault();
      const block = buildColumnBlockPlainText(rs.ta.value, rs.anchor, rs.focus);
      void navigator.clipboard.writeText(block).catch(() => {});
      this.setStickyStatus(`Rect: ${block.split("\n").length}-line column copied`);
      return; // keep selection alive
    }

    if (isCtrl && !e.altKey && e.key.toLowerCase() === "x") {
      e.preventDefault();
      const block = buildColumnBlockPlainText(rs.ta.value, rs.anchor, rs.focus);
      void navigator.clipboard.writeText(block).catch(() => {});
      const { c0 } = this.getRectBounds(rs);
      this.applyRectEdit(rs, null, c0); // cut: clear selection, keep cursor at c0
      return;
    }

    if (isCtrl && !e.altKey && e.key.toLowerCase() === "v") {
      e.preventDefault();
      void this.pasteIntoRectSel(rs);
      return;
    }

    if (e.key === "Delete") {
      e.preventDefault();
      const { c0 } = this.getRectBounds(rs);
      this.applyRectEdit(rs, null, c0); // delete selection (or char at cursor), cursor stays at c0
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      this.applyRectBackspace(rs);
      return;
    }

    // Printable character: replace the rect selection on all lines and advance the cursor.
    if (!isCtrl && !e.altKey && e.key.length === 1) {
      e.preventDefault();
      const { c0, c1 } = this.getRectBounds(rs);
      this.applyRectEdit(rs, [e.key], c0 + e.key.length); // insert char, cursor after it
      void 0; // suppress unused-expression lint
      // Update status hint after entering multi-line insert mode.
      if (c0 === c1) {
        // Already in cursor mode — no need to update status every keystroke.
      } else {
        this.updateStatusLine(
          `Rect insert mode — type to continue · Backspace/Del · Ctrl+V paste · Esc dismiss`,
        );
      }
      return;
    }

    // Navigation and other keys: dismiss the rect selection so caret moves normally.
    if (
      e.key === "Enter" ||
      e.key === "Tab" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "PageUp" ||
      e.key === "PageDown"
    ) {
      this.dismissRectSel();
    }
  };

  private onTextareaTabKeydown(ta: HTMLTextAreaElement, ev: KeyboardEvent): void {
    if (ev.key !== "Tab" || ev.defaultPrevented) return;
    if (document.activeElement !== ta) return;
    if (ta.readOnly) return;
    const docForTa =
      ta === this.editor
        ? this.documents.active
        : (() => {
            const slot = this.multiviewExtras.findIndex((ex) => ex.editor === ta);
            if (slot < 0) return null;
            const idx = this.getMultiviewExtraTabIndexForSlot(slot);
            return idx !== null ? this.documents.documents[idx] ?? null : null;
          })();
    if (!docForTa || docForTa.viewMode !== "text") return;
    ev.preventDefault();
    const s = ta.selectionStart;
    const t = ta.selectionEnd;
    const sp4 = "    ";
    let r: { text: string; selStart: number; selEnd: number };
    if (ev.shiftKey) {
      r = outdentLines(ta.value, s, t);
    } else if (ev.ctrlKey) {
      // Former plain-Tab block indent, now four spaces per line (not U+0009).
      r = indentLines(ta.value, s, t);
    } else {
      const lo = Math.min(s, t);
      const hi = Math.max(s, t);
      if (lo === hi) {
        const ins = insertAt(ta.value, lo, sp4);
        r = { text: ins.text, selStart: ins.caret, selEnd: ins.caret };
      } else {
        r = {
          text: ta.value.slice(0, lo) + sp4 + ta.value.slice(hi),
          selStart: lo + sp4.length,
          selEnd: lo + sp4.length,
        };
      }
    }
    if (ta === this.editor) {
      this.commitEditor(r.text, r.selStart, r.selEnd);
      return;
    }
    const slot = this.multiviewExtras.findIndex((ex) => ex.editor === ta);
    if (slot < 0) return;
    ta.value = r.text;
    ta.setSelectionRange(r.selStart, r.selEnd);
    ta.focus();
    const idx = this.getMultiviewExtraTabIndexForSlot(slot);
    if (idx !== null) this.documents.syncFromEditorAt(idx, r.text);
    this.bumpLineCountEpoch();
    this.updateSyntaxHighlightLayerForExtraSlot(slot);
    this.tabBar?.refresh();
    this.updateStatusLine();
  }

  private onTextareaAltColumnPointerDown(ta: HTMLTextAreaElement, e: PointerEvent): void {
    // Chrome can target the textarea’s inner #text node; strict `e.target === ta` would skip the gesture.
    if (!(e.target instanceof Node) || !ta.contains(e.target)) return;
    const altHeld =
      e.altKey || (typeof e.getModifierState === "function" && e.getModifierState("Alt"));
    if (!altHeld) {
      // Regular click: dismiss any persistent rect selection on this textarea.
      if (this.activeRectSel?.ta === ta) this.dismissRectSel();
      return;
    }
    // New alt+drag: discard any previous persistent rect selection first.
    this.dismissRectSel();
    // Primary (0); some macOS builds report Option+primary as auxiliary (1).
    if (e.button !== 0 && e.button !== 1) return;
    const docIdx =
      ta === this.editor
        ? this.documents.activeIndex
        : (() => {
            const slot = this.multiviewExtras.findIndex((ex) => ex.editor === ta);
            if (slot < 0) return null;
            return this.getMultiviewExtraTabIndexForSlot(slot);
          })();
    if (docIdx === null) return;
    const doc = this.documents.documents[docIdx];
    if (!doc || doc.viewMode !== "text") return;
    if (ta.readOnly) return;
    e.preventDefault();
    this.clearColumnDragListeners();
    const anchor = textareaOffsetAtClientPoint(ta, e.clientX, e.clientY);
    const pid = e.pointerId;
    let lastX = e.clientX;
    let lastY = e.clientY;
    let finished = false;
    try {
      ta.setPointerCapture(pid);
    } catch {
      /* ignore */
    }
    const overlayHost =
      ta === this.editor
        ? this.editorHost
        : (this.multiviewExtras.find((ex) => ex.editor === ta)?.editorHost ?? null);
    const rectOverlay = overlayHost ? new RectSelectOverlay(overlayHost) : null;
    this.columnDragRectOverlay = rectOverlay;
    const docOpts = { capture: true, passive: false } as const;
    const paintColumnDragSelectionAt = (clientX: number, clientY: number): void => {
      const off = textareaOffsetAtClientPoint(ta, clientX, clientY);
      const n = ta.value.length;
      const f = Math.max(0, Math.min(n, off));
      // Keep caret at focus (collapsed) so the highlight layer stays visible during drag.
      try {
        ta.setSelectionRange(f, f);
      } catch {
        /* ignore */
      }
      this.syncNativeTextSelectionVisualFor(ta);
      rectOverlay?.paint(ta, anchor, f);
    };
    ta.focus();
    paintColumnDragSelectionAt(e.clientX, e.clientY);
    const finishColumnDrag = (): void => {
      if (finished) return;
      finished = true;
      const focus = textareaOffsetAtClientPoint(ta, lastX, lastY);
      ta.focus();
      const n = ta.value.length;
      const f = Math.max(0, Math.min(n, focus));
      try {
        ta.setSelectionRange(f, f);
      } catch {
        /* ignore */
      }
      this.syncNativeTextSelectionVisualFor(ta);
      // Adopt the overlay as persistent rect selection before clearColumnDragListeners runs
      // (which would otherwise remove it).
      if (rectOverlay) {
        this.columnDragRectOverlay = null;
        this.activeRectSel = { ta, overlay: rectOverlay, anchor, focus };
        const block = buildColumnBlockPlainText(ta.value, anchor, focus);
        void navigator.clipboard.writeText(block).catch(() => {});
        this.updateStatusLine(
          `Rect selection: ${block.split("\n").length} line(s) — type to insert · Ctrl+C copy · Ctrl+V paste · Del · Esc`,
        );
      }
      this.clearColumnDragListeners();
    };
    const onDocMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pid) return;
      lastX = ev.clientX;
      lastY = ev.clientY;
      ev.preventDefault();
      paintColumnDragSelectionAt(lastX, lastY);
    };
    const onDocUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== pid) return;
      ev.preventDefault();
      finishColumnDrag();
    };
    const onDocCancel = (ev: PointerEvent): void => {
      if (ev.pointerId !== pid) return;
      finishColumnDrag();
    };
    document.addEventListener("pointermove", onDocMove, docOpts);
    document.addEventListener("pointerup", onDocUp, docOpts);
    document.addEventListener("pointercancel", onDocCancel, docOpts);
    this.columnDragCleanup = (): void => {
      document.removeEventListener("pointermove", onDocMove, docOpts);
      document.removeEventListener("pointerup", onDocUp, docOpts);
      document.removeEventListener("pointercancel", onDocCancel, docOpts);
      try {
        ta.releasePointerCapture(pid);
      } catch {
        /* ignore */
      }
    };
  }

  /** Browser spellcheck on the main and multiview textareas; off for previews-only and embedded binary. */
  private syncTextareaSpellcheckState(): void {
    const d = this.documents.active;
    this.editor.spellcheck = this.computePrimaryEditorSpellcheckEnabled(d);

    const used = this.multiviewEnabled ? this.multiviewExtraSlotsUsed() : 0;
    for (let s = 0; s < this.multiviewExtras.length; s++) {
      const ex = this.multiviewExtras[s]!;
      if (!this.multiviewEnabled || s >= used) {
        ex.editor.spellcheck = false;
        continue;
      }
      const idx = this.getMultiviewExtraTabIndexForSlot(s);
      const doc = idx !== null ? this.documents.documents[idx] : undefined;
      ex.editor.spellcheck = this.computeMultiviewExtraEditorSpellcheckEnabled(doc, ex.editor);
    }
  }

  private computePrimaryEditorSpellcheckEnabled(d: TabDocument | null | undefined): boolean {
    if (!this.nativeSpellcheckEnabled) return false;
    if (!d || d.viewMode !== "text") return false;
    if (this.editor.readOnly) return false;
    if (this.editorRow.classList.contains("npp-editor-row--diff-tab")) return false;
    if (d.sessionRestorePlaceholder) return false;
    if (d.language === "md" && !this.mdMarkdownShowSource) return false;
    if (d.language === "tex" && !this.texShowSource) return false;
    if (d.language === "max" && !this.maxShowSource) return false;
    if (d.language === "html" && !this.htmlShowSource) return false;
    if (d.language === "oeis" && !this.oeisShowSource) return false;
    if (this.isSpreadsheetLanguage(d.language) && !this.csvShowSource) return false;
    if (tabTextEditorSpellcheckBlockedByBuffer(this.editor.value)) return false;
    return true;
  }

  private computeMultiviewExtraEditorSpellcheckEnabled(
    d: TabDocument | undefined,
    ed: HTMLTextAreaElement,
  ): boolean {
    if (!this.nativeSpellcheckEnabled) return false;
    if (!d || d.viewMode !== "text") return false;
    if (ed.readOnly) return false;
    if (!this.docAllowsMultiviewEditorPane(d)) return false;
    if (tabTextEditorSpellcheckBlockedByBuffer(ed.value)) return false;
    return true;
  }

  private syncSpellcheckToggleLabels(): void {
    const on = this.nativeSpellcheckEnabled;
    this.editorContextSpellcheckToggleBtn.textContent = on ? "Disable spell checking" : "Enable spell checking";
    this.tabBar?.syncSpellcheckToggleLabel();
  }

  private toggleNativeSpellcheckFromMenu(): void {
    this.setNativeSpellcheckEnabled(!this.nativeSpellcheckEnabled);
  }

  // ── Markup layer ────────────────────────────────────────────────────────────

  private markupStorageKeyForDoc(doc: { id: string; filePath: string | null }): string {
    return doc.filePath ? markupStorageKey(doc.filePath) : markupStorageKey(`tab:${doc.id}`);
  }

  private getOrLoadMarksForDoc(docId: string, filePath: string | null): MarkEntry[] {
    if (!this.markupLayerData.has(docId)) {
      const key = filePath ? markupStorageKey(filePath) : markupStorageKey(`tab:${docId}`);
      this.markupLayerData.set(docId, loadMarkupFromStorage(key));
    }
    return this.markupLayerData.get(docId)!;
  }

  private updateMarkupOverlay(): void {
    const doc = this.documents.active;
    if (!doc || doc.viewMode !== "text") {
      this.markupOverlayCode.innerHTML = "";
      this.markupOverlayPre.hidden = true;
      return;
    }
    const marks = this.getOrLoadMarksForDoc(doc.id, doc.filePath);
    if (marks.length === 0 || !this.markupLayerVisible) {
      this.markupOverlayCode.innerHTML = "";
      this.markupOverlayPre.hidden = true;
      return;
    }
    this.markupOverlayPre.hidden = false;
    this.markupOverlayCode.innerHTML = renderMarkupOverlayHtml(this.editor.value, marks);
    this.layoutSyntaxOverlayOverTextarea(this.editor, this.markupOverlayPre);
    this.markupOverlayPre.scrollTop = this.editor.scrollTop;
    this.markupOverlayPre.scrollLeft = this.editor.scrollLeft;
  }

  private noteMarkupEditStarted(): void {
    const doc = this.documents.active;
    if (!doc) return;
    if (!this.markupEditSessionStarts.has(doc.id)) {
      this.markupEditSessionStarts.set(doc.id, new Date().toISOString());
    }
  }

  private commitMarkupMarks(): void {
    const doc = this.documents.active;
    if (!doc) return;
    const marks = this.markupLayerData.get(doc.id);
    if (!marks || marks.length === 0) return;
    const now = new Date().toISOString();
    let changed = false;
    for (const mark of marks) {
      if (mark.endDate === null) {
        mark.endDate = now;
        changed = true;
      }
    }
    if (changed) {
      persistMarkupToStorage(this.markupStorageKeyForDoc(doc), marks);
      this.markupLayerEpoch++;
      if (this.gutterMode === "datetime") this.updateLineNumberGutter();
    }
    this.markupEditSessionStarts.delete(doc.id);
  }

  private addMarkToSelection(color: string): void {
    const doc = this.documents.active;
    if (!doc || doc.viewMode !== "text") return;
    const ta = this.editorContextTargetTextarea ?? this.editor;
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;
    if (selStart === selEnd) return;
    const startDate = this.markupEditSessionStarts.get(doc.id) ?? new Date().toISOString();
    const mark: MarkEntry = {
      id: newMarkId(),
      text: ta.value.slice(selStart, selEnd),
      start: selStart,
      end: selEnd,
      color,
      startDate,
      endDate: null,
    };
    const marks = this.getOrLoadMarksForDoc(doc.id, doc.filePath);
    marks.push(mark);
    persistMarkupToStorage(this.markupStorageKeyForDoc(doc), marks);
    this.markupLayerEpoch++;
    this.updateMarkupOverlay();
    if (this.gutterMode === "datetime") this.updateLineNumberGutter();
  }

  private removeMarkAtCursor(): void {
    const doc = this.documents.active;
    if (!doc || doc.viewMode !== "text") return;
    const ta = this.editorContextTargetTextarea ?? this.editor;
    const cursor = ta.selectionStart;
    const selEnd = ta.selectionEnd;
    const marks = this.markupLayerData.get(doc.id);
    if (!marks || marks.length === 0) return;
    const rangeEnd = selEnd > cursor ? selEnd : cursor + 1;
    const remaining = marks.filter((m) => !(m.start < rangeEnd && m.end > cursor));
    this.markupLayerData.set(doc.id, remaining);
    persistMarkupToStorage(this.markupStorageKeyForDoc(doc), remaining);
    this.markupLayerEpoch++;
    this.updateMarkupOverlay();
    if (this.gutterMode === "datetime") this.updateLineNumberGutter();
  }

  private toggleMarkupVisibility(): void {
    this.markupLayerVisible = !this.markupLayerVisible;
    this.updateMarkupOverlay();
  }

  private toggleGutterMode(): void {
    this.gutterMode = this.gutterMode === "lines" ? "datetime" : "lines";
    // Bust gutter caches so content is rebuilt immediately
    this.gutterContentLayoutKey = "";
    for (let i = 0; i < this.gutterLayoutKeyPerMultiviewSlot.length; i++) {
      this.gutterLayoutKeyPerMultiviewSlot[i] = "";
    }
    this.updateLineNumberGutter();
  }

  /**
   * Build gutter content for datetime mode: each logical line shows "HH:MM" of the
   * newest mark overlapping it; continuation rows in word-wrap mode show "".
   */
  private buildDatetimeGutterContent(ta: HTMLTextAreaElement, text: string, docId: string): string {
    const marks = this.markupLayerData.get(docId) ?? [];
    const logicalLines = text.split("\n");
    let lineStart = 0;

    if (!this.textareaUsesWordWrap(ta)) {
      const rows: string[] = new Array(logicalLines.length);
      for (let i = 0; i < logicalLines.length; i++) {
        const lineEnd = lineStart + logicalLines[i]!.length;
        rows[i] = gutterTimestampForLine(marks, lineStart, lineEnd);
        lineStart = lineEnd + 1;
      }
      return rows.join("\n");
    }

    const counts = textareaLogicalLineWrapRowCounts(ta, text);
    const parts: string[] = [];
    for (let i = 0; i < counts.length; i++) {
      const line = logicalLines[i] ?? "";
      const lineEnd = lineStart + line.length;
      parts.push(gutterTimestampForLine(marks, lineStart, lineEnd));
      const n = counts[i]!;
      for (let k = 1; k < n; k++) parts.push(""); // continuation rows
      lineStart = lineEnd + 1;
    }
    return parts.join("\n");
  }

  private setNativeSpellcheckEnabled(on: boolean): void {
    if (this.nativeSpellcheckEnabled === on) return;
    this.nativeSpellcheckEnabled = on;
    const prev = loadWebPrefs();
    saveWebPrefs({ ...prev, nativeSpellcheckEnabled: on });
    this.syncTextareaSpellcheckState();
    for (const el of this.nullaSectionEls) {
      if (el instanceof HTMLTextAreaElement) el.spellcheck = on;
    }
    this.syncSpellcheckToggleLabels();
    this.scheduleSurfaceRefresh();
    this.updateStatusLine(on ? "Spell checking enabled." : "Spell checking disabled.");
  }

  private onEditorInput(): void {
    if (!this.rectEditInProgress && this.activeRectSel?.ta === this.editor) this.dismissRectSel();
    this.noteMarkupEditStarted();
    this.documents.syncFromEditor(this.editor.value);
    this.schedulePersistTabSession();
    this.bumpLineCountEpoch();
    this.tabBar?.refresh();
    if (this.debounceCheckpoint) clearTimeout(this.debounceCheckpoint);
    this.debounceCheckpoint = setTimeout(() => {
      this.documents.checkpointHistory(this.editor.value);
      this.debounceCheckpoint = null;
    }, 400);
    this.scheduleSurfaceRefresh();
    this.updateLineNumberGutter();
    this.updateStatusLine();
    this.syncTextareaSpellcheckState();
  }

  private setStickyStatus(message: string, ms = 4000): void {
    this.stickyStatusExtra = message;
    if (this.stickyStatusTimer) {
      clearTimeout(this.stickyStatusTimer);
      this.stickyStatusTimer = null;
    }
    this.updateStatusLine(message);
    this.stickyStatusTimer = setTimeout(() => {
      this.stickyStatusTimer = null;
      this.stickyStatusExtra = null;
      this.updateStatusLine();
    }, Math.max(300, ms));
  }

  private updateStatusLine(extra?: string): void {
    const effectiveExtra = extra ?? this.stickyStatusExtra ?? undefined;
    const d = this.documents.active;
    if (!d) return;
    if (d.viewMode === "image") {
      const base = `${d.title} | Image preview | UTF-8 | Picture (read-only)`;
      this.status.textContent = effectiveExtra ? `${base} — ${effectiveExtra}` : base;
      return;
    }
    if (d.viewMode === "pdf") {
      const base = `${d.title} | PDF preview | UTF-8 | Document (read-only)`;
      this.status.textContent = effectiveExtra ? `${base} — ${effectiveExtra}` : base;
      return;
    }
    if (d.viewMode === "video") {
      const sections = d.content ? parseSections(d.content) : [];
      const clips = sections.filter((s) => s.type === "video" || s.type === "audio");
      const clipInfo = clips.length > 0 ? `${clips.length} clip${clips.length > 1 ? "s" : ""}` : "empty";
      const base = `${d.title} | Nulla video | ${clipInfo}`;
      this.status.textContent = effectiveExtra ? `${base} — ${effectiveExtra}` : base;
      return;
    }
    if (d.viewMode === "diff" && d.diffPair) {
      const layout = d.diffSplitView ? "Two-pane split" : "Monaco diff";
      const base = `Compare | ${d.diffPair.leftLabel} ↔ ${d.diffPair.rightLabel} | ${layout} (read-only)`;
      this.status.textContent = effectiveExtra ? `${base} — ${effectiveExtra}` : base;
      return;
    }
    const text = this.editor.value;
    const caret = Math.max(0, Math.min(this.activeCaretOffset(), text.length));
    const m = lineMetricsAtOffset(text, caret);
    this.positionActiveLineHighlight(m.lineIndex0);
    const base = `${d.title} | ${languageLabel(d.language)} | ${encodingLabel(d.encoding)} | EOL: ${d.eol.toUpperCase()} | Ln ${m.line1}, Col ${m.col1}${d.dirty ? " | Modified" : ""}`;
    this.status.textContent = effectiveExtra ? `${base} — ${effectiveExtra}` : base;
  }

  private applyZoomCss(): void {
    const px = (this.editorBaseFontPx * this.zoomPercent) / 100;
    const fs = `${px}px`;
    this.editor.style.fontSize = fs;
    this.highlightPre.style.fontSize = fs;
    this.lineNumberPre.style.fontSize = fs;
    for (const ex of this.multiviewExtras) {
      ex.editor.style.fontSize = fs;
      ex.highlightPre.style.fontSize = fs;
      ex.lineNumberPre.style.fontSize = fs;
    }
    this.updateActiveLineHighlight();
    this.updateLineNumberGutter();
  }

  private updateLineNumberGutter(): void {
    this.editorHost.classList.toggle("npp-show-line-numbers", this.showLineNumbers);
    this.editorHost.classList.toggle("npp-gutter-datetime", this.showLineNumbers && this.gutterMode === "datetime");
    if (!this.showLineNumbers) {
      this.gutterContentLayoutKey = "";
      for (let i = 0; i < this.gutterLayoutKeyPerMultiviewSlot.length; i++) this.gutterLayoutKeyPerMultiviewSlot[i] = "";
      return;
    }
    const text = this.editor.value;
    const ed = this.editor;
    const key = this.lineNumberGutterLayoutKey(ed, text, "primary");
    if (this.gutterContentLayoutKey !== key) {
      this.gutterContentLayoutKey = key;
      const doc = this.documents.active;
      this.lineNumberPre.textContent = this.gutterMode === "datetime" && doc
        ? this.buildDatetimeGutterContent(ed, text, doc.id)
        : this.buildLineNumberPreContent(ed, text);
    }
    this.lineNumberGutter.scrollTop = ed.scrollTop;
    if (this.multiviewEnabled) {
      const used = this.multiviewExtraSlotsUsed();
      for (let s = 0; s < used; s++) this.updateLineNumberGutterForExtraSlot(s);
    }
  }

  private updateActiveLineHighlight(): void {
    const text = this.editor.value;
    const caret = Math.max(0, Math.min(this.activeCaretOffset(), text.length));
    const { lineIndex0 } = lineMetricsAtOffset(text, caret);
    this.positionActiveLineHighlight(lineIndex0);
  }

  private activeCaretOffset(): number {
    return focusCaretOffsetFromSelection(
      this.editor.selectionStart,
      this.editor.selectionEnd,
      this.editor.selectionDirection,
    );
  }

  /**
   * Pin the syntax `<pre>` to the textarea’s **client** box (not the full `.npp-editor-host`), so
   * horizontal/vertical scroll extents match the visible text surface (scrollbar no longer widens the overlay).
   */
  private layoutSyntaxOverlayOverTextarea(ta: HTMLTextAreaElement, highlightPre: HTMLPreElement): void {
    highlightPre.style.left = `${ta.offsetLeft + ta.clientLeft}px`;
    highlightPre.style.top = `${ta.offsetTop + ta.clientTop}px`;
    highlightPre.style.width = `${ta.clientWidth}px`;
    highlightPre.style.height = `${ta.clientHeight}px`;
    highlightPre.style.right = "auto";
    highlightPre.style.bottom = "auto";
  }

  private isSpreadsheetLanguage(lang: LanguageMode | undefined): boolean {
    return lang === "csv" || lang === "excel";
  }

  private syncExcelSheetToolbar(): void {
    const d = this.documents.active;
    if (!d || d.language !== "excel") {
      this.excelSheetSelect.hidden = true;
      return;
    }
    this.excelSheetSelect.hidden = false;
    const doc =
      parseNppExcelDocOrNull(this.editor.value) ?? parseNppExcelDocOrNull(emptyWorkbookJson())!;
    const want = Math.max(0, Math.min(doc.sheets.length - 1, doc.activeSheetIndex));
    this.excelSheetSelect.replaceChildren();
    doc.sheets.forEach((s, i) => {
      const o = document.createElement("option");
      o.value = String(i);
      o.textContent = s.name || `Sheet${i + 1}`;
      this.excelSheetSelect.appendChild(o);
    });
    this.excelSheetSelect.value = String(want);
  }

  private applyEditorLayoutForLanguage(): void {
    const lang = this.documents.active?.language;
    const isMd = lang === "md";
    const isTex = lang === "tex";
    const isMax = lang === "max";
    const isHtml = lang === "html";
    const isOeis = lang === "oeis";
    const isExcel = lang === "excel";
    const isSpreadsheet = this.isSpreadsheetLanguage(lang);
    const wasMarkdownLayout = this.editorRow.classList.contains("npp-editor-row--markdown");
    const wasTexLayout = this.editorRow.classList.contains("npp-editor-row--tex");
    const wasMaxLayout = this.editorRow.classList.contains("npp-editor-row--max");
    const wasHtmlLayout = this.editorRow.classList.contains("npp-editor-row--html");
    const wasOeisLayout = this.editorRow.classList.contains("npp-editor-row--oeis");
    const wasSpreadsheetLayout = this.editorRow.classList.contains("npp-editor-row--csv");

    this.editorRow.classList.toggle("npp-editor-row--markdown", !!isMd);
    this.editorRow.classList.toggle("npp-editor-row--tex", !!isTex);
    this.editorRow.classList.toggle("npp-editor-row--max", !!isMax);
    this.editorRow.classList.toggle("npp-editor-row--html", !!isHtml);
    this.editorRow.classList.toggle("npp-editor-row--oeis", !!isOeis);
    this.editorRow.classList.toggle("npp-editor-row--excel", !!isExcel);
    this.editorRow.classList.toggle("npp-editor-row--csv", !!isSpreadsheet);
    this.mdPreview.hidden = !isMd;
    this.texPreview.hidden = !isTex;
    this.maxPreview.hidden = !isMax;
    this.htmlPreview.hidden = !isHtml;
    this.oeisPreview.hidden = !isOeis;
    this.csvPreview.hidden = !isSpreadsheet;

    const activeDoc = this.documents.active;
    if (!isMd) {
      this.mdMarkdownShowSource = false;
    } else {
      this.mdMarkdownShowSource = activeDoc?.showSource ?? false;
    }
    this.applyMarkdownPreviewLayoutClass();

    if (!isTex) {
      this.texShowSource = false;
    } else {
      this.texShowSource = activeDoc?.showSource ?? false;
    }
    this.applyTexPreviewLayoutClass();

    if (!isMax) {
      this.maxShowSource = false;
    } else {
      this.maxShowSource = activeDoc?.showSource ?? false;
    }
    this.applyMaxPreviewLayoutClass();

    if (!isHtml) {
      this.htmlShowSource = false;
    } else {
      this.htmlShowSource = activeDoc?.showSource ?? false;
    }
    this.applyHtmlPreviewLayoutClass();

    if (!isOeis) {
      this.oeisShowSource = false;
    } else {
      this.oeisShowSource = activeDoc?.showSource ?? false;
    }
    this.applyOeisPreviewLayoutClass();

    if (!isSpreadsheet) {
      this.csvShowSource = false;
      this.destroyCsvGrid();
    } else {
      this.csvShowSource = activeDoc?.showSource ?? false;
    }
    this.applyCsvPreviewLayoutClass();
    this.syncExcelSheetToolbar();
    this.applyEditorWhiteSpaceFromWrapPreference();
  }

  /**
   * Word wrap follows View → Word wrap except Max buffers, which always use `pre` (no soft wrap).
   */
  private applyEditorWhiteSpaceFromWrapPreference(): void {
    const wrapOn = this.dispatcher.isCommandChecked(CMD.VIEW_WRAP);
    const apply = (editor: HTMLTextAreaElement, highlightPre: HTMLElement, doc: TabDocument | null | undefined) => {
      const forceNoWrap = doc?.viewMode === "text" && doc?.language === "max";
      const ws = forceNoWrap ? "pre" : wrapOn ? "pre-wrap" : "pre";
      editor.style.whiteSpace = ws;
      highlightPre.style.whiteSpace = ws;
    };
    apply(this.editor, this.highlightPre, this.documents.active);
    const used = this.multiviewEnabled ? this.multiviewExtraSlotsUsed() : 0;
    for (let s = 0; s < used; s++) {
      const ex = this.multiviewExtras[s]!;
      const idx = this.getMultiviewExtraTabIndexForSlot(s);
      const doc = idx !== null ? this.documents.documents[idx] : undefined;
      apply(ex.editor, ex.highlightPre, doc);
    }
    this.updateLineNumberGutter();
    for (let s = 0; s < used; s++) this.updateLineNumberGutterForExtraSlot(s);
  }

  private applyMarkdownPreviewLayoutClass(): void {
    const isMd = this.documents.active?.language === "md";
    if (!isMd) {
      this.editorRow.classList.remove("npp-md-preview-only");
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.mdMarkdownShowSource;
    this.editorRow.classList.toggle("npp-md-preview-only", previewOnly);
    this.mdViewToggle.textContent = previewOnly ? "Edit source" : "Preview only";
    this.syncTextareaSpellcheckState();
  }

  private applyTexPreviewLayoutClass(): void {
    const isTex = this.documents.active?.language === "tex";
    if (!isTex) {
      this.editorRow.classList.remove("npp-tex-preview-only");
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.texShowSource;
    this.editorRow.classList.toggle("npp-tex-preview-only", previewOnly);
    this.texViewToggle.textContent = previewOnly ? "Edit source" : "Preview only";
    this.syncTextareaSpellcheckState();
  }

  private applyMaxPreviewLayoutClass(): void {
    const isMax = this.documents.active?.language === "max";
    if (!isMax) {
      this.editorRow.classList.remove("npp-max-preview-only");
      this.syncMaxInsertImageBrowseUi();
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.maxShowSource;
    this.editorRow.classList.toggle("npp-max-preview-only", previewOnly);
    this.maxViewToggle.textContent = previewOnly ? "Edit source" : "Preview only";
    this.syncMaxInsertImageBrowseUi();
    this.syncTextareaSpellcheckState();
  }

  /**
   * Show the Max Insert menu only for `.max` buffers (path or title), in Max language mode, with source
   * visible (Edit source / split; not preview-only).
   */
  private syncMaxInsertImageBrowseUi(): void {
    const d = this.documents.active;
    const show =
      !!d &&
      d.viewMode === "text" &&
      d.language === "max" &&
      this.maxShowSource &&
      !this.editorRow.classList.contains("npp-editor-row--diff-tab");
    if (!show) this.maxAttachWrap.open = false;
    this.maxAttachWrap.hidden = !show;
  }

  private insertMaxMarkdownImageFromRasterFile(file: File): void {
    void (async () => {
      try {
        const url = await readImageFileAsDataUrl(file);
        const alt =
          file.name
            .replace(/\.[^.\\/]+$/, "")
            .replace(/[_-]+/g, " ")
            .trim() || "image";
        const line = buildMaxMarkdownImageLine(alt, url);
        const ed = this.editor;
        const lo = Math.min(ed.selectionStart, ed.selectionEnd);
        const hi = Math.max(ed.selectionStart, ed.selectionEnd);
        const next = ed.value.slice(0, lo) + line + ed.value.slice(hi);
        const cursor = lo + line.length;
        this.commitEditor(next, cursor, cursor);
        this.updateStatusLine("Inserted image from file");
      } catch (e) {
        this.updateStatusLine(`Insert image failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }

  private insertMaxMarkdownPdfFromFile(file: File): void {
    void (async () => {
      try {
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        if (!isPdf) {
          this.updateStatusLine("Insert PDF: choose a .pdf file.");
          return;
        }
        const n = await getPdfPageCountFromFile(file);
        const hint = `Pages (1–${n}). Examples: 1 · 1-4 · 1,3,5 · 1-${n} · all`;
        const raw = window.prompt(hint, "1");
        if (raw === null) return;
        let pages = parsePdfInsertPageSelection(raw, n);
        if (pages.length === 0) {
          this.updateStatusLine("Insert PDF: no valid pages (try 1, 1-3, or all).");
          return;
        }
        if (pages.length > MAX_PDF_INSERT_PAGES_PER_ACTION) {
          this.updateStatusLine(
            `Insert PDF: at most ${MAX_PDF_INSERT_PAGES_PER_ACTION} pages per action (${pages.length} requested).`,
          );
          return;
        }
        if (pages.length > 18) {
          const ok = window.confirm(
            `Insert ${pages.length} pages? Each line repeats the PDF (large buffer). Continue?`,
          );
          if (!ok) return;
        }
        const baseDataUrl = await readPdfFileAsDataUrl(file);
        const stem =
          file.name
            .replace(/\.[^.\\/]+$/, "")
            .replace(/[_-]+/g, " ")
            .trim() || "PDF";
        let block = "";
        for (const p of pages) {
          const ref = pdfDataUrlWithPage(baseDataUrl, p);
          block += buildMaxMarkdownImageLine(`${stem} (p.${p})`, ref);
        }
        const ed = this.editor;
        const lo = Math.min(ed.selectionStart, ed.selectionEnd);
        const hi = Math.max(ed.selectionStart, ed.selectionEnd);
        const next = ed.value.slice(0, lo) + block + ed.value.slice(hi);
        const cursor = lo + block.length;
        this.commitEditor(next, cursor, cursor);
        const pageLabel =
          pages.length === 1
            ? `page ${pages[0]} of ${n}`
            : `${pages.length} pages (${pages[0]}–${pages[pages.length - 1]}) of ${n}`;
        this.updateStatusLine(`Inserted PDF (${pageLabel})`);
      } catch (e) {
        this.updateStatusLine(`Insert PDF failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }

  private insertMaxVideoEmbedFromFile(file: File): void {
    void (async () => {
      try {
        const dataUrl = await readVideoFileAsDataUrl(file);
        const title =
          file.name
            .replace(/\.[^.\\/]+$/, "")
            .replace(/[_-]+/g, " ")
            .trim() || "Video";
        const block = buildMaxVideoEmbedHtml(title, dataUrl);
        const ed = this.editor;
        const lo = Math.min(ed.selectionStart, ed.selectionEnd);
        const hi = Math.max(ed.selectionStart, ed.selectionEnd);
        const next = ed.value.slice(0, lo) + block + ed.value.slice(hi);
        const cursor = lo + block.length;
        this.commitEditor(next, cursor, cursor);
        this.updateStatusLine("Inserted embedded video");
      } catch (e) {
        this.updateStatusLine(`Insert video failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }

  private insertMaxHtmlEmbedFromFile(file: File): void {
    void (async () => {
      try {
        const isHtml =
          file.type === "text/html" ||
          file.type === "application/xhtml+xml" ||
          /\.html?$/i.test(file.name);
        if (!isHtml) {
          this.updateStatusLine("Insert HTML: choose an .html file.");
          return;
        }
        const raw = await readHtmlFileAsUtf8Text(file);
        const block = buildMaxHtmlHexEmbedMarkdownFromRawHtml(raw, file.name);
        if (!block) {
          this.updateStatusLine("HTML file is empty after sanitization.");
          return;
        }
        const ed = this.editor;
        const lo = Math.min(ed.selectionStart, ed.selectionEnd);
        const hi = Math.max(ed.selectionStart, ed.selectionEnd);
        const next = ed.value.slice(0, lo) + block + ed.value.slice(hi);
        const cursor = lo + block.length;
        this.commitEditor(next, cursor, cursor);
        this.updateStatusLine("Inserted sanitized HTML");
      } catch (e) {
        this.updateStatusLine(`Insert HTML failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }

  private applyHtmlPreviewLayoutClass(): void {
    const isHtml = this.documents.active?.language === "html";
    if (!isHtml) {
      this.editorRow.classList.remove("npp-html-preview-only");
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.htmlShowSource;
    this.editorRow.classList.toggle("npp-html-preview-only", previewOnly);
    this.htmlViewToggle.textContent = previewOnly ? "Edit source" : "Preview only";
    this.syncTextareaSpellcheckState();
  }

  private applyOeisPreviewLayoutClass(): void {
    const isOeis = this.documents.active?.language === "oeis";
    if (!isOeis) {
      this.editorRow.classList.remove("npp-oeis-preview-only");
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.oeisShowSource;
    this.editorRow.classList.toggle("npp-oeis-preview-only", previewOnly);
    this.oeisViewToggle.textContent = previewOnly ? "Edit source" : "Preview only";
    this.syncTextareaSpellcheckState();
  }

  private applyCsvPreviewLayoutClass(): void {
    if (!this.isSpreadsheetLanguage(this.documents.active?.language)) {
      this.editorRow.classList.remove("npp-csv-preview-only");
      this.syncTextareaSpellcheckState();
      return;
    }
    const previewOnly = !this.csvShowSource;
    this.editorRow.classList.toggle("npp-csv-preview-only", previewOnly);
    const isExcel = this.documents.active?.language === "excel";
    this.csvViewToggle.textContent = previewOnly
      ? isExcel
        ? "Workbook JSON"
        : "Edit as text"
      : "Table only";
    this.syncTextareaSpellcheckState();
  }

  private ensureCsvGrid(): void {
    if (!this.isSpreadsheetLanguage(this.documents.active?.language)) return;
    if (this.csvGridApi) return;
    const dm = this.documents;
    this.csvGridApi = createCsvGridEditor(this.csvTableHost, this.csvOpsHost, {
      getEol: () => dm.active!.eol,
      onCommit: (text) => {
        const doc = dm.active;
        if (!doc) return;
        const next =
          doc.language === "excel"
            ? mergeActiveSheetCsvIntoWorkbookJson(this.editor.value, text)
            : text;
        this.editor.value = next;
        this.bumpLineCountEpoch();
        dm.syncFromEditor(next);
        dm.checkpointHistory(next);
        this.tabBar?.refresh();
        this.updateSyntaxHighlightLayer();
        this.syncExcelSheetToolbar();
        this.updateStatusLine();
        this.syncTextareaSpellcheckState();
      },
    });
  }

  private destroyCsvGrid(): void {
    if (this.csvPreviewTimer) {
      clearTimeout(this.csvPreviewTimer);
      this.csvPreviewTimer = null;
    }
    this.csvGridApi?.destroy();
    this.csvGridApi = null;
  }

  private scheduleCsvGridRefresh(): void {
    if (this.csvPreviewTimer) clearTimeout(this.csvPreviewTimer);
    this.csvPreviewTimer = setTimeout(() => {
      this.csvPreviewTimer = null;
      const d = this.documents.active;
      if (!this.isSpreadsheetLanguage(d?.language)) return;
      this.ensureCsvGrid();
      const gridText =
        d!.language === "excel"
          ? activeSheetCsvFromWorkbookJson(this.editor.value, d!.eol)
          : this.editor.value;
      this.csvGridApi?.syncFromText(gridText);
      this.syncExcelSheetToolbar();
    }, 80);
  }

  private clearMarkdownPreview(): void {
    if (this.mdPreviewTimer) {
      clearTimeout(this.mdPreviewTimer);
      this.mdPreviewTimer = null;
    }
    this.mdPreviewBody.innerHTML = "";
  }

  private scheduleMarkdownPreview(): void {
    if (this.mdPreviewTimer) clearTimeout(this.mdPreviewTimer);
    this.mdPreviewTimer = setTimeout(() => {
      this.mdPreviewTimer = null;
      if (this.documents.active?.language !== "md") return;
      this.mdPreviewBody.innerHTML = this.markdownPreviewHtmlFromSource(this.editor.value);
    }, 120);
  }

  /** Markdown → HTML using saved preview style (line breaks). */
  private markdownPreviewHtmlFromSource(src: string): string {
    const br = loadWebPrefs().markdownPreviewStyle.lineBreaks === "gfm";
    return markdownToSafeHtmlAnnotated(src, { breaks: br });
  }

  /** Applies typography, CSS variables, and `data-*` on the Markdown preview from {@link loadWebPrefs}. */
  private syncMarkdownPreviewChromeFromPrefs(): void {
    applyMarkdownPreviewTheme(this.mdPreview, loadWebPrefs().markdownPreviewStyle);
  }

  private clearTexPreview(): void {
    if (this.texPreviewTimer) {
      clearTimeout(this.texPreviewTimer);
      this.texPreviewTimer = null;
    }
    this.texPreviewBody.innerHTML = "";
  }

  private clearMaxPreview(): void {
    if (this.maxPreviewTimer) {
      clearTimeout(this.maxPreviewTimer);
      this.maxPreviewTimer = null;
    }
    this.maxPreviewBody.innerHTML = "";
  }

  private clearHtmlPreview(): void {
    if (this.htmlPreviewTimer) {
      clearTimeout(this.htmlPreviewTimer);
      this.htmlPreviewTimer = null;
    }
    this.htmlPreviewBody.removeAttribute("srcdoc");
  }

  private scheduleTexPreview(): void {
    if (this.texPreviewTimer) clearTimeout(this.texPreviewTimer);
    this.texPreviewTimer = setTimeout(() => {
      this.texPreviewTimer = null;
      if (this.documents.active?.language !== "tex") return;
      this.texPreviewBody.innerHTML = buildTexPreviewHtml(this.editor.value);
      renderTexMathInPreviewRoot(this.texPreviewBody);
    }, 120);
  }

  private scheduleMaxPreview(): void {
    if (this.maxPreviewTimer) clearTimeout(this.maxPreviewTimer);
    this.maxPreviewTimer = setTimeout(() => {
      this.maxPreviewTimer = null;
      if (this.documents.active?.language !== "max") return;
      revokeMaxHexMediaBlobUrls(this.maxPreviewBody);
      this.maxPreviewBody.innerHTML = buildMaxPreviewHtmlAnnotated(this.editor.value);
      hydrateMaxPreviewRoot(this.maxPreviewBody);
    }, 120);
  }

  private scheduleHtmlPreview(): void {
    if (this.htmlPreviewTimer) clearTimeout(this.htmlPreviewTimer);
    this.htmlPreviewTimer = setTimeout(() => {
      this.htmlPreviewTimer = null;
      if (this.documents.active?.language !== "html") return;
      this.htmlPreviewBody.srcdoc = buildBrowserLikePreviewSrcdoc(this.editor.value);
    }, 120);
  }

  /** Same sanitized document as the inline preview, in a real browser window (no iframe sandbox). */
  private popOutHtmlMiniBrowser(): void {
    if (this.documents.active?.language !== "html") return;
    this.flushEditorToBuffer();
    const html = buildBrowserLikePreviewSrcdoc(this.editor.value);
    const features =
      "popup=yes,width=1024,height=768,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";
    const w = window.open("", "nppHtmlMiniBrowser", features);
    if (!w) {
      this.updateStatusLine("Mini browser blocked — allow pop-ups for this site.");
      return;
    }
    try {
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      this.updateStatusLine("HTML opened in mini browser window");
    } catch (e) {
      try {
        w.close();
      } catch {
        /* ignore */
      }
      this.updateStatusLine(`Mini browser failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  private clearOeisPreview(): void {
    this.oeisFetchSeq++;
    this.oeisTermsSearchHits = [];
    this.oeisTermsSearchHitsQuery = "";
    this.oeisTermsSearchNextStart = 0;
    this.oeisTermsSearchLoadMoreSeq++;
    if (this.oeisPreviewTimer) {
      clearTimeout(this.oeisPreviewTimer);
      this.oeisPreviewTimer = null;
    }
    this.oeisPreviewBody.innerHTML = "";
  }

  /** Keep the OEIS toolbar fields in sync with the buffer unless the user is editing a field. */
  private syncOeisSeqIdInputFromEditor(): void {
    const text = this.editor.value;
    const pq = resolveOeisPreviewQuery(text);
    if (document.activeElement !== this.oeisSeqIdInput) {
      this.oeisSeqIdInput.value = pq?.mode === "id" ? pq.id : "";
      this.oeisSeqIdInput.removeAttribute("aria-invalid");
    }
    if (document.activeElement !== this.oeisTermsSearchInput) {
      if (pq?.mode === "terms") {
        this.oeisTermsSearchInput.value = pq.query;
      } else {
        this.oeisTermsSearchInput.value = loadNullapadOeisLastTermsQuery();
      }
      this.oeisTermsSearchInput.removeAttribute("aria-invalid");
    }
  }

  private applyOeisSequenceIdAndRefresh(id: string): void {
    const dm = this.documents.active;
    if (!dm || dm.language !== "oeis") return;
    this.oeisTermsSearchHits = [];
    this.oeisTermsSearchHitsQuery = "";
    this.oeisTermsSearchNextStart = 0;
    this.oeisTermsSearchLoadMoreSeq++;
    this.oeisSeqIdInput.removeAttribute("aria-invalid");
    const next = applyOeisSequenceIdToSource(this.editor.value, id, dm.eol);
    this.oeisSeqIdInput.value = id;
    this.editor.value = next;
    this.bumpLineCountEpoch();
    this.documents.syncFromEditor(next);
    this.documents.checkpointHistory(next);
    this.tabBar?.refresh();
    this.updateSyntaxHighlightLayer();
    this.scheduleOeisPreview();
    this.updateStatusLine();
    this.syncTextareaSpellcheckState();
  }

  private commitOeisSequenceFromToolbar(): void {
    const dm = this.documents.active;
    if (!dm || dm.language !== "oeis") return;
    const id = parseOeisSeqIdFromInput(this.oeisSeqIdInput.value);
    if (!id) {
      this.oeisSeqIdInput.setAttribute("aria-invalid", "true");
      return;
    }
    this.applyOeisSequenceIdAndRefresh(id);
  }

  private commitOeisTermsSearchFromToolbar(): void {
    const dm = this.documents.active;
    if (!dm || dm.language !== "oeis") return;
    const terms = parseOeisTermsSearchFromInput(this.oeisTermsSearchInput.value);
    if (!terms) {
      this.oeisTermsSearchInput.setAttribute("aria-invalid", "true");
      return;
    }
    this.oeisTermsSearchInput.removeAttribute("aria-invalid");
    persistNullapadOeisLastTermsQuery(terms);
    const next = applyOeisTermsSearchLineToSource(this.editor.value, terms, dm.eol);
    this.oeisTermsSearchInput.value = terms;
    this.editor.value = next;
    this.bumpLineCountEpoch();
    this.documents.syncFromEditor(next);
    this.documents.checkpointHistory(next);
    this.tabBar?.refresh();
    this.updateSyntaxHighlightLayer();
    this.scheduleOeisPreview();
    this.updateStatusLine();
    this.syncTextareaSpellcheckState();
  }

  private scheduleOeisPreview(): void {
    if (this.oeisPreviewTimer) clearTimeout(this.oeisPreviewTimer);
    this.oeisFetchSeq++;
    const token = this.oeisFetchSeq;
    this.syncOeisSeqIdInputFromEditor();
    this.oeisPreviewBody.innerHTML = formatOeisLoadingHtml();
    this.oeisPreviewTimer = setTimeout(() => {
      this.oeisPreviewTimer = null;
      void this.runOeisPreviewFetch(token);
    }, 200);
  }

  /** OEIS JSON search returns up to 10 rows per `start` offset. */
  private static readonly OEIS_TERMS_SEARCH_PAGE = 10;

  private async appendOeisTermsSearchHitsPage(btn: HTMLButtonElement): Promise<void> {
    const d = this.documents.active;
    if (!d || d.language !== "oeis") return;
    const q = this.oeisTermsSearchHitsQuery;
    if (!q || this.oeisTermsSearchHits.length === 0) return;
    const seq = ++this.oeisTermsSearchLoadMoreSeq;
    const fetchSeq = this.oeisFetchSeq;
    btn.disabled = true;
    try {
      const start = this.oeisTermsSearchNextStart;
      const url = oeisSearchJsonUrlForRawQuery(q, start);
      const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
      const raw = await res.text();
      persistNullapadOeisSearchSnapshot({
        query: q,
        requestUrl: url,
        httpStatus: res.status,
        fetchedAtMs: Date.now(),
        body: raw,
      });
      if (seq !== this.oeisTermsSearchLoadMoreSeq || fetchSeq !== this.oeisFetchSeq) return;
      if (!res.ok) {
        this.updateStatusLine(`Load more: OEIS returned HTTP ${res.status}.`);
        return;
      }
      let data: unknown;
      try {
        data = JSON.parse(raw) as unknown;
      } catch {
        this.updateStatusLine("Load more: OEIS response was not valid JSON.");
        return;
      }
      if (seq !== this.oeisTermsSearchLoadMoreSeq || fetchSeq !== this.oeisFetchSeq) return;
      if (typeof data === "object" && data !== null && (data as { results?: unknown }).results === null) {
        this.updateStatusLine("OEIS: further pages unavailable (too many matches).");
        return;
      }
      const more = parseOeisSearchJsonRows(data);
      this.oeisTermsSearchNextStart += more.length;
      const before = this.oeisTermsSearchHits.length;
      this.oeisTermsSearchHits = mergeOeisSearchHitRows(this.oeisTermsSearchHits, more);
      const added = this.oeisTermsSearchHits.length - before;
      if (added === 0 && more.length > 0) {
        this.updateStatusLine("No new sequences on this page (duplicates only).");
      } else if (more.length === 0) {
        this.updateStatusLine("No more sequences on this page.");
      } else {
        this.updateStatusLine(added ? `Loaded ${added} more sequence(s).` : "No more sequences on this page.");
      }
      if (seq !== this.oeisTermsSearchLoadMoreSeq || fetchSeq !== this.oeisFetchSeq) return;
      const showLoadMore = more.length >= AppShell.OEIS_TERMS_SEARCH_PAGE;
      this.oeisPreviewBody.innerHTML = formatOeisSearchPickListHtml(q, this.oeisTermsSearchHits, {
        showLoadMore,
      });
    } catch (e) {
      if (seq === this.oeisTermsSearchLoadMoreSeq && fetchSeq === this.oeisFetchSeq) {
        const msg = e instanceof Error ? e.message : String(e);
        this.updateStatusLine(`Load more failed: ${msg}`);
      }
    } finally {
      if (seq === this.oeisTermsSearchLoadMoreSeq) {
        const next = this.oeisPreviewBody.querySelector("button.npp-oeis-search-load-more");
        if (next instanceof HTMLButtonElement) next.disabled = false;
      }
    }
  }

  private async runOeisPreviewFetch(token: number): Promise<void> {
    if (this.documents.active?.language !== "oeis") return;
    if (token !== this.oeisFetchSeq) return;
    const text = this.editor.value;
    const pq = resolveOeisPreviewQuery(text);
    if (!pq) {
      if (token !== this.oeisFetchSeq) return;
      this.oeisPreviewBody.innerHTML = formatOeisHintHtml();
      return;
    }
    try {
      if (pq.mode === "terms") {
        this.oeisTermsSearchHits = [];
        this.oeisTermsSearchNextStart = 0;
      }
      const url = pq.mode === "id" ? oeisSearchJsonUrl(pq.id) : oeisSearchJsonUrlForRawQuery(pq.query);
      const fetchOeis = () => fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
      let res = await fetchOeis();
      /* Retry once for transient 5xx — oeis.org returns 500 intermittently. */
      if (res.status >= 500) {
        if (token !== this.oeisFetchSeq) return;
        this.oeisPreviewBody.innerHTML = formatOeisLoadingHtml();
        await new Promise<void>((resolve) => setTimeout(resolve, 1500));
        if (token !== this.oeisFetchSeq) return;
        res = await fetchOeis();
      }
      const raw = await res.text();
      if (pq.mode === "terms") {
        persistNullapadOeisSearchSnapshot({
          query: pq.query,
          requestUrl: url,
          httpStatus: res.status,
          fetchedAtMs: Date.now(),
          body: raw,
        });
      }
      if (token !== this.oeisFetchSeq) return;
      if (!res.ok) {
        const oeisUrl = pq.mode === "id" ? `https://oeis.org/${pq.id}` : undefined;
        this.oeisPreviewBody.innerHTML = formatOeisErrorHtml(
          `OEIS returned HTTP ${res.status}.`,
          { retryHint: res.status >= 500, oeisUrl },
        );
        return;
      }
      let data: unknown;
      try {
        data = JSON.parse(raw) as unknown;
      } catch {
        if (token !== this.oeisFetchSeq) return;
        this.oeisPreviewBody.innerHTML = formatOeisErrorHtml("OEIS response was not valid JSON.");
        return;
      }
      if (token !== this.oeisFetchSeq) return;
      if (pq.mode === "id") {
        const entry = parseOeisSearchJson(data);
        if (!entry) {
          this.oeisPreviewBody.innerHTML = formatOeisErrorHtml(`No sequence found for ${pq.id}.`);
          return;
        }
        this.oeisPreviewBody.innerHTML = formatOeisEntryHtml(pq.id, entry);
        renderTexMathInPreviewRoot(this.oeisPreviewBody);
        return;
      }
      const terms = pq.query;
      if (typeof data === "object" && data !== null && (data as { results?: unknown }).results === null) {
        this.oeisPreviewBody.innerHTML = formatOeisErrorHtml(
          "OEIS returned too many matches for that terms query; narrow the list on oeis.org and paste an A-number here.",
        );
        return;
      }
      const rows = parseOeisSearchJsonRows(data);
      if (rows.length >= 2) {
        this.oeisTermsSearchHitsQuery = terms;
        this.oeisTermsSearchHits = [...rows];
        this.oeisTermsSearchNextStart = rows.length;
        const showLoadMore = rows.length >= AppShell.OEIS_TERMS_SEARCH_PAGE;
        this.oeisPreviewBody.innerHTML = formatOeisSearchPickListHtml(terms, this.oeisTermsSearchHits, {
          showLoadMore,
        });
        return;
      }
      const entry = rows[0] ?? null;
      if (!entry) {
        this.oeisTermsSearchHitsQuery = terms;
        this.oeisTermsSearchHits = [...rows];
        this.oeisTermsSearchNextStart = rows.length;
        this.oeisPreviewBody.innerHTML = formatOeisSearchPickListHtml(terms, this.oeisTermsSearchHits, {
          showLoadMore: false,
        });
        return;
      }
      const hitId = oeisAnumberFromSearchHit(entry);
      if (!hitId) {
        this.oeisTermsSearchHits = [];
        this.oeisTermsSearchHitsQuery = "";
        this.oeisTermsSearchNextStart = 0;
        this.oeisPreviewBody.innerHTML = formatOeisErrorHtml(`OEIS returned a hit without an A-number for terms ${terms}.`);
        return;
      }
      this.oeisTermsSearchHits = [];
      this.oeisTermsSearchHitsQuery = "";
      this.oeisTermsSearchNextStart = 0;
      this.oeisPreviewBody.innerHTML = formatOeisEntryHtml(hitId, entry);
      renderTexMathInPreviewRoot(this.oeisPreviewBody);
    } catch (e) {
      if (token !== this.oeisFetchSeq) return;
      const msg = e instanceof Error ? e.message : String(e);
      this.oeisPreviewBody.innerHTML = formatOeisErrorHtml(`Could not load OEIS: ${msg}`);
    }
  }

  private updateSyntaxHighlightLayer(): void {
    const doc = this.documents.active;
    if (!doc) return;
    if (doc.viewMode !== "text") {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    if (doc.language === "md" && !this.mdMarkdownShowSource) {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    if (doc.language === "tex" && !this.texShowSource) {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    if (doc.language === "max" && !this.maxShowSource) {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    if (doc.language === "oeis" && !this.oeisShowSource) {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    if (this.isSpreadsheetLanguage(doc.language) && !this.csvShowSource) {
      this.highlightCode.className = "hljs npp-plaintext";
      this.highlightCode.innerHTML = "";
      this.activeLineDiv = null;
      return;
    }
    this.highlightCode.className = highlightCodeClassFor(doc.language);
    const proseSpellDecorate = this.computePrimaryEditorSpellcheckEnabled(doc);
    this.highlightCode.innerHTML = wrapHighlightLines(
      renderHighlightedFragment(this.editor.value, doc.language, { proseSpellDecorate }),
    );
    this.activeLineDiv = null;
    this.syncHighlightScroll();
    this.updateMarkupOverlay();
    if (this.multiviewEnabled) {
      const used = this.multiviewExtraSlotsUsed();
      for (let s = 0; s < used; s++) this.updateSyntaxHighlightLayerForExtraSlot(s);
    }
  }

  private scheduleSurfaceRefresh(): void {
    if (this.surfaceRefreshScheduled) return;
    this.surfaceRefreshScheduled = true;
    requestAnimationFrame(() => {
      this.surfaceRefreshScheduled = false;
      this.updateSyntaxHighlightLayer();
      if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
      if (this.documents.active?.language === "tex") this.scheduleTexPreview();
      if (this.documents.active?.language === "max") this.scheduleMaxPreview();
      if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
      if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
      if (this.isSpreadsheetLanguage(this.documents.active?.language) && this.csvShowSource) {
        this.scheduleCsvGridRefresh();
      }
    });
  }

  private refreshEditorSurface(): void {
    this.applyEditorLayoutForLanguage();
    this.updateSyntaxHighlightLayer();
    if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
    else this.clearMarkdownPreview();
    if (this.documents.active?.language === "tex") this.scheduleTexPreview();
    else this.clearTexPreview();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    else this.clearMaxPreview();
    if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
    else this.clearHtmlPreview();
    if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
    else this.clearOeisPreview();
    if (this.isSpreadsheetLanguage(this.documents.active?.language)) {
      this.ensureCsvGrid();
      this.scheduleCsvGridRefresh();
    }
    this.syncTextareaSpellcheckState();
  }

  /** Brand-row control: Language → Max for the active tab only, then preview-first layout. */
  private applyMaxModeToActiveTab(): void {
    this.flushEditorToBuffer();
    const ok = this.dispatcher.executeCommand(CMD.LANG_MAX);
    if (!ok) {
      this.updateStatusLine("Max mode is not available right now.");
      return;
    }
    this.maxShowSource = false;
    this.applyMaxPreviewLayoutClass();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    this.updateStatusLine("Max mode on this tab — KaTeX math and @@ calculation preview (Edit source for raw text).");
  }

  private commitEditor(text: string, selStart: number, selEnd: number): void {
    this.editor.value = text;
    this.bumpLineCountEpoch();
    this.editor.setSelectionRange(selStart, selEnd);
    this.editor.focus();
    this.documents.syncFromEditor(text);
    this.documents.checkpointHistory(text);
    this.tabBar?.refresh();
    this.updateSyntaxHighlightLayer();
    if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
    if (this.documents.active?.language === "tex") this.scheduleTexPreview();
    if (this.documents.active?.language === "max") this.scheduleMaxPreview();
    if (this.documents.active?.language === "html") this.scheduleHtmlPreview();
    if (this.documents.active?.language === "oeis") this.scheduleOeisPreview();
    if (this.isSpreadsheetLanguage(this.documents.active?.language)) this.scheduleCsvGridRefresh();
    this.updateStatusLine();
    this.syncTextareaSpellcheckState();
  }

  private findNextFromSession(): void {
    const s = getFindSession();
    const ed = this.editor;
    if (!s.needle) {
      openFindDialog({
        getText: () => ed.value,
        getSelectionStart: () => ed.selectionStart,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
        },
      });
      this.updateStatusLine("Set a search string in Find first");
      return;
    }
    const text = ed.value;
    const from = ed.selectionEnd;
    let i = findNextIndex(text, s.needle, from, s.caseSensitive, s.wholeWord);
    if (i < 0 && from > 0) {
      const ok = confirm("End of file reached. Continue from the start?");
      if (!ok) {
        this.updateStatusLine("Find next: end of file reached");
        return;
      }
      i = findNextIndex(text, s.needle, 0, s.caseSensitive, s.wholeWord);
    }
    if (i < 0) {
      this.updateStatusLine("Find: no matches");
      return;
    }
    this.revealEditorSelection(i, i + s.needle.length);
    this.updateStatusLine();
  }

  private findPrevFromSession(): void {
    const s = getFindSession();
    const ed = this.editor;
    if (!s.needle) {
      openFindDialog({
        getText: () => ed.value,
        getSelectionStart: () => ed.selectionStart,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
        },
      });
      return;
    }
    const text = ed.value;
    const before = ed.selectionStart;
    let i = findPrevIndex(text, s.needle, before, s.caseSensitive, s.wholeWord);
    if (i < 0) {
      const ok = confirm("Start of file reached. Continue from the end?");
      if (!ok) {
        this.updateStatusLine("Find prev: start of file reached");
        return;
      }
      i = findPrevIndex(text, s.needle, text.length, s.caseSensitive, s.wholeWord);
    }
    if (i < 0) {
      this.updateStatusLine("Find: no matches");
      return;
    }
    this.revealEditorSelection(i, i + s.needle.length);
    this.updateStatusLine();
  }

  private needleFromSelectionOrWord(): string | null {
    const ed = this.editor;
    const s = ed.selectionStart;
    const e = ed.selectionEnd;
    if (s !== e) {
      const slice = ed.value.slice(s, e);
      return slice.length ? slice : null;
    }
    const wb = wordBoundsAt(ed.value, s);
    return wb ? ed.value.slice(wb.start, wb.end) : null;
  }

  private setAndFindNext(): void {
    const n = this.needleFromSelectionOrWord();
    if (!n) {
      this.updateStatusLine("Select text or place caret in a word");
      return;
    }
    const smartCase = /[a-z]/.test(n) && /[A-Z]/.test(n);
    setFindSession({ needle: n, caseSensitive: smartCase, wholeWord: false });
    this.findNextFromSession();
  }

  private setAndFindPrev(): void {
    const n = this.needleFromSelectionOrWord();
    if (!n) {
      this.updateStatusLine("Select text or place caret in a word");
      return;
    }
    const smartCase = /[a-z]/.test(n) && /[A-Z]/.test(n);
    setFindSession({ needle: n, caseSensitive: smartCase, wholeWord: false });
    this.findPrevFromSession();
  }

  private volatileFindNext(): void {
    const ed = this.editor;
    const needle = this.needleFromSelectionOrWord();
    if (!needle) {
      this.updateStatusLine("Volatile find: select text or place caret in a word");
      return;
    }
    const { caseSensitive: cs, wholeWord: ww } = getFindSession();
    const text = ed.value;
    const from = ed.selectionEnd;
    let i = findNextIndex(text, needle, from, cs, ww);
    if (i < 0 && from > 0) {
      const ok = confirm("End of file reached. Continue from the start?");
      if (!ok) {
        this.updateStatusLine("Volatile find next: end of file reached");
        return;
      }
      i = findNextIndex(text, needle, 0, cs, ww);
    }
    if (i < 0) {
      this.updateStatusLine("Volatile find: no matches");
      return;
    }
    this.revealEditorSelection(i, i + needle.length);
    this.updateStatusLine();
  }

  private volatileFindPrev(): void {
    const ed = this.editor;
    const needle = this.needleFromSelectionOrWord();
    if (!needle) {
      this.updateStatusLine("Volatile find: select text or place caret in a word");
      return;
    }
    const { caseSensitive: cs, wholeWord: ww } = getFindSession();
    const text = ed.value;
    const before = ed.selectionStart;
    let i = findPrevIndex(text, needle, before, cs, ww);
    if (i < 0) {
      const ok = confirm("Start of file reached. Continue from the end?");
      if (!ok) {
        this.updateStatusLine("Volatile find prev: start of file reached");
        return;
      }
      i = findPrevIndex(text, needle, text.length, cs, ww);
    }
    if (i < 0) {
      this.updateStatusLine("Volatile find: no matches");
      return;
    }
    this.revealEditorSelection(i, i + needle.length);
    this.updateStatusLine();
  }

  private rebuildMenubar(): void {
    this.menuBar?.setModel(buildNotepadMenuModel(undefined, loadWebPrefs().shortcutOverrides));
  }

  private async applyLinuxObjectView(): Promise<void> {
    const doc = this.documents.active;
    if (doc?.viewMode !== "text") {
      this.updateStatusLine("Linux object view applies to text tabs only.");
      return;
    }
    const handle = doc?.fileHandle;
    if (!doc || !handle) {
      this.updateStatusLine("Linux object view requires a file-backed tab");
      return;
    }
    let file: File;
    try {
      file = await handle.getFile();
    } catch {
      this.updateStatusLine("Could not read file for Linux object view");
      return;
    }
    const view = await renderLinuxObjectPreview(file, true);
    if (view === null) {
      this.updateStatusLine("Could not render Linux object view");
      return;
    }
    this.editor.value = view;
    this.bumpLineCountEpoch();
    this.documents.syncFromEditor(view);
    this.documents.checkpointHistory(view);
    this.tabBar?.refresh();
    this.refreshEditorSurface();
    this.updateLineNumberGutter();
    this.updateStatusLine("Linux object view applied");
  }

  private reloadKeyboardShortcuts(): void {
    this.keyboardChordMap = buildChordToCommandMap(loadWebPrefs().shortcutOverrides);
  }

  private openShortcutMapperFromSettings(): void {
    openShortcutMapperDialog({
      initialOverrides: loadWebPrefs().shortcutOverrides ?? {},
      onSave: (overrides) => {
        const prev = loadWebPrefs();
        saveWebPrefs({
          ...prev,
          shortcutOverrides: Object.keys(overrides).length ? { ...overrides } : undefined,
        });
        this.reloadKeyboardShortcuts();
        this.rebuildMenubar();
      },
    });
  }

  private goMatchingBrace(): void {
    const ed = this.editor;
    this.flushEditorToBuffer();
    const off = matchingBraceOffset(ed.value, ed.selectionStart);
    if (off === null) {
      this.updateStatusLine("No bracket at caret");
      return;
    }
    ed.focus();
    ed.setSelectionRange(off, off + 1);
    this.updateStatusLine();
  }

  private tabTitleForImportedUrl(urlStr: string, ext: string): string {
    try {
      const u = new URL(urlStr);
      const parts = u.pathname.split("/").filter(Boolean);
      let base = parts.length > 0 ? parts[parts.length - 1]! : u.hostname;
      try {
        base = decodeURIComponent(base);
      } catch {
        /* keep */
      }
      base = base.replace(/[/\\?%*:|"<>]/g, "-").trim();
      if (!base) {
        base = u.hostname.replace(/[/\\?%*:|"<>]/g, "-").trim() || "page";
      }
      const dot = base.lastIndexOf(".");
      const stem = dot > 0 ? base.slice(0, dot) : base;
      const safe = (stem || "page").slice(0, 120);
      return `${safe}.${ext}`;
    } catch {
      return `import.${ext}`;
    }
  }

  private resetOeisTermsSearchUiState(): void {
    this.oeisTermsSearchHits = [];
    this.oeisTermsSearchHitsQuery = "";
    this.oeisTermsSearchNextStart = 0;
    this.oeisTermsSearchLoadMoreSeq++;
  }

  private finishOpenPreviewHyperlinkInNewTab(statusMessage: string): void {
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateStatusLine(statusMessage);
    this.rebuildMenubar();
    this.persistTabSessionToStorage();
    this.syncDictationToolbarButton();
    if (this.documents.active?.viewMode === "text") {
      this.editor.focus({ preventScroll: true });
    }
  }

  /**
   * Markdown / OEIS preview: primary-click on http(s) links opens a new NullaPad tab for OEIS sequences
   * and plain text; HTML-style pages are opened in the system browser from the click handler instead.
   */
  private async openPreviewHyperlinkInNewTab(absoluteHref: string): Promise<void> {
    const dm = this.documents;
    const ed = this.editor;

    let u: URL;
    try {
      u = new URL(absoluteHref);
    } catch {
      this.updateStatusLine("Invalid link URL.");
      return;
    }
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      this.updateStatusLine("Only http(s) links open in-app.");
      return;
    }

    const pathname = u.pathname;
    const activeEol = dm.active?.eol ?? "lf";
    const isOeis = isOeisOrgHostname(u.hostname);

    if (isOeis && urlPathnameLooksLikeDownloadedPlainText(pathname)) {
      const proxy = oeisOrgHttpUrlToApiProxyPath(u.href);
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      if (!proxy) {
        dm.addContentTab("link.txt", `${u.href}\n`, "text");
        this.finishOpenPreviewHyperlinkInNewTab("Could not map OEIS link to a proxy path.");
        return;
      }
      try {
        const res = await fetch(proxy, { credentials: "same-origin", cache: "no-store" });
        const text = await res.text();
        if (!res.ok) {
          dm.addContentTab(
            this.tabTitleForImportedUrl(u.href, "txt"),
            `${u.href}\n\nHTTP ${res.status}\n${text.slice(0, 4000)}`,
            "text",
          );
          this.finishOpenPreviewHyperlinkInNewTab(`OEIS returned HTTP ${res.status}.`);
          return;
        }
        dm.addContentTab(this.tabTitleForImportedUrl(u.href, "txt"), text, "text");
        this.finishOpenPreviewHyperlinkInNewTab("Loaded text from oeis.org.");
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        dm.addContentTab("oeis-fetch-error.txt", `${u.href}\n\n${msg}`, "text");
        this.finishOpenPreviewHyperlinkInNewTab("Network error loading OEIS text.");
      }
      return;
    }

    const seqId = oeisSequenceIdFromOeisOrgHttpUrl(u.href);
    if (seqId) {
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      this.resetOeisTermsSearchUiState();
      const body = applyOeisSequenceIdToSource("", seqId, activeEol);
      dm.addContentTab(`${seqId}.oeis`, body, "oeis");
      this.finishOpenPreviewHyperlinkInNewTab(`Opened OEIS sequence ${seqId} in a new tab.`);
      return;
    }

    if (urlPathnameLooksLikeDownloadedPlainText(pathname)) {
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      try {
        const res = await fetch(u.href, { credentials: "omit", mode: "cors" });
        const text = await res.text();
        if (!res.ok) {
          dm.addContentTab(
            this.tabTitleForImportedUrl(u.href, "txt"),
            `${u.href}\n\nHTTP ${res.status}\n${text.slice(0, 4000)}`,
            "text",
          );
          this.finishOpenPreviewHyperlinkInNewTab(`HTTP ${res.status} while loading text URL.`);
          return;
        }
        dm.addContentTab(this.tabTitleForImportedUrl(u.href, "txt"), text, "text");
        this.finishOpenPreviewHyperlinkInNewTab("Loaded text in a new tab.");
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        dm.addContentTab(
          this.tabTitleForImportedUrl(u.href, "txt"),
          `${u.href}\n\n${msg}\n`,
          "text",
        );
        this.finishOpenPreviewHyperlinkInNewTab("Could not load text URL (CORS or network).");
      }
      return;
    }

    this.flushEditorToBuffer();
    dm.checkpointHistory(ed.value);
    dm.addContentTab("link.txt", `${u.href}\n`, "text");
    this.finishOpenPreviewHyperlinkInNewTab("Opened link in a new text tab.");
  }

  /** After server-side HTML fetch succeeds (File → Open HTML from URL…): new HTML tab, UI refresh, editor focus. */
  private onFetchedHtmlPageLoaded(resolvedUrl: string, html: string): void {
    const dm = this.documents;
    const ed = this.editor;
    this.flushEditorToBuffer();
    dm.addTabFromFetchedHtmlPage(resolvedUrl, html);
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.updateStatusLine();
    this.rebuildMenubar();
    this.persistTabSessionToStorage();
    this.syncDictationToolbarButton();
    if (dm.active?.viewMode === "text") {
      ed.focus({ preventScroll: true });
    }
    this.updateStatusLine(`Loaded HTML from ${resolvedUrl}`);
  }

  private registerDefaultCommands(): void {
    const d = this.dispatcher;
    const dm = this.documents;
    const ed = this.editor;
    const refresh = (): void => {
      this.tabBar?.refresh();
      this.loadActiveIntoEditor();
      this.updateStatusLine();
      this.rebuildMenubar();
      this.persistTabSessionToStorage();
      this.syncDictationToolbarButton();
    };

    d.registerHandler(CMD.FILE_NEW, () => {
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      dm.addEmptyTab();
      refresh();
      if (dm.active?.viewMode === "text" && !ed.readOnly) {
        ed.focus();
      }
    });
    d.registerHandler(CMD.FILE_RECORD_NULLA, () => {
      void this.toggleNullaRecording();
    });
    d.registerHandler(CMD.FILE_NULLA_MIC_INJECT, () => {
      this.toggleNullaMicFromToolbar();
    });
    d.registerHandler(CMD.FILE_OPEN, async () => {
      this.flushEditorToBuffer();
      const ok = await dm.openIntoNewTab();
      if (ok) refresh();
    });
    d.registerHandler(CMD.FILE_OPEN_FOLDER, async () => {
      this.flushEditorToBuffer();
      const dir = await pickWorkspaceDirectory();
      if (!dir || !this.folderWorkspacePane) return;
      await this.folderWorkspacePane.setRoot(dir);
      dm.setWorkspaceRootHandle(dir);
      void persistWorkspaceFolderHandle(dir);
      this.rebuildMenubar();
      this.updateStatusLine(`Workspace: ${dir.name}`);
      void this.syncGitTerminalWorkspace();
    });
    d.registerHandler(CMD.FILE_OPEN_HTML_URL, () => {
      void openHtmlFromUrlDialog({
        onLoaded: (resolvedUrl, html) => this.onFetchedHtmlPageLoaded(resolvedUrl, html),
      });
    });
    d.registerHandler(CMD.FILE_CLOSE, () => {
      this.closeTab(dm.activeIndex);
    });
    d.registerHandler(CMD.WINDOW_TAB_MOVE_LEFT, () => {
      const ai = dm.activeIndex;
      if (ai <= 0) return;
      this.onTabReorder(ai, ai - 1);
    });
    d.registerHandler(CMD.WINDOW_TAB_MOVE_RIGHT, () => {
      const n = dm.documents.length;
      const ai = dm.activeIndex;
      if (ai < 0 || ai >= n - 1) return;
      this.onTabReorder(ai, ai + 2);
    });
    d.registerHandler(CMD.FILE_CLOSEALL, () => {
      this.flushEditorToBuffer();
      dm.closeAllTabs();
      refresh();
    });
    d.registerHandler(CMD.FILE_SAVE, async () => {
      this.flushEditorToBuffer();
      const vm = dm.active?.viewMode;
      if (vm !== "text" && vm !== "video") {
        this.updateStatusLine("This tab is read-only (preview or compare).");
        return;
      }
      await dm.saveActiveTab();
      this.commitMarkupMarks();
      refresh();
    });
    d.registerHandler(CMD.FILE_SAVEAS, async () => {
      this.flushEditorToBuffer();
      const vm2 = dm.active?.viewMode;
      if (vm2 !== "text" && vm2 !== "video") {
        this.updateStatusLine("This tab is read-only (preview or compare).");
        return;
      }
      await dm.saveActiveTabAs();
      refresh();
    });
    d.registerHandler(CMD.FILE_SAVEALL, async () => {
      this.flushEditorToBuffer();
      await dm.saveAllTabs();
      refresh();
    });
    d.registerHandler(CMD.FILE_PRINT, () => {
      window.print();
    });
    d.registerHandler(CMD.EXPORT_TO_PDF, () => {
      this.exportActiveRenderedToPdf();
    });
    d.registerHandler(CMD.EXPORT_MAX_RENDERED_MD, () => {
      this.exportActiveMaxRenderedMarkdown();
    });
    d.registerHandler(CMD.FILE_RECENTFILES, () => {
      openRecentFilesDialog(DocumentManager.readRecent());
    });
    d.registerHandler(CMD.FILE_RECENT_CLEAR, () => {
      dm.clearRecentFilesList();
      this.rebuildMenubar();
      this.updateStatusLine("Recent files list cleared");
    });
    d.registerHandler(CMD.FILE_RECENT_EMPTY, () => {});
    for (let slot = 0; slot < FILE_RECENT_SLOT_IDS.length; slot++) {
      const id = FILE_RECENT_SLOT_IDS[slot]!;
      d.registerHandler(id, async () => {
        const paths = DocumentManager.readRecent();
        const path = paths[slot];
        if (!path) return;
        this.flushEditorToBuffer();
        const ok = await dm.openRecentPath(path);
        if (!ok) {
          try {
            await navigator.clipboard.writeText(path);
          } catch {
            /* ignore */
          }
          void alert(
            "Could not reopen this file from the browser (no saved file handle, or permission was denied).\n\nThe path was copied to the clipboard — use File → Open to choose the file again.",
          );
          return;
        }
        refresh();
      });
    }
    d.registerHandler(CMD.FILE_COPY_PATH, async () => {
      const doc = dm.active;
      const p = doc?.filePath ?? doc?.title ?? "";
      if (!p) return;
      await navigator.clipboard.writeText(p);
      this.updateStatusLine("Path / title copied to clipboard");
    });
    d.registerHandler(CMD.FILE_SHARE_NULLAPAD, async () => {
      const doc = dm.active;
      if (!doc || (doc.viewMode !== "text" && doc.viewMode !== "video")) {
        void alert("Share to NullaPad is only available for text tabs and Max (.max) tabs (not images, PDFs, or Compare tabs).");
        return;
      }
      const rid = window.prompt("Recipient NullaPad secret (4–48 hex characters):")?.trim();
      if (!rid) return;
      try {
        await this.shareTabFromContextMenu(dm.activeIndex, rid);
      } catch (e) {
        void alert((e as Error).message || String(e));
      }
    });
    d.registerHandler(CMD.FILE_EXIT, () => {
      this.flushEditorToBuffer();
      if (dm.documents.every((x) => !x.dirty) || confirm("Quit with unsaved changes?")) {
        window.close();
      }
    });

    d.registerHandler(CMD.EDIT_UNDO, () => {
      const t = dm.undo();
      if (t !== null) {
        ed.value = t;
        this.bumpLineCountEpoch();
        dm.syncFromEditor(t);
        this.updateSyntaxHighlightLayer();
        if (dm.active?.language === "md") this.scheduleMarkdownPreview();
        if (dm.active?.language === "tex") this.scheduleTexPreview();
        if (dm.active?.language === "max") this.scheduleMaxPreview();
        if (dm.active?.language === "html") this.scheduleHtmlPreview();
        if (dm.active?.language === "oeis") this.scheduleOeisPreview();
        if (this.isSpreadsheetLanguage(dm.active?.language)) this.scheduleCsvGridRefresh();
        this.updateLineNumberGutter();
        this.updateStatusLine();
      }
    });
    d.registerHandler(CMD.EDIT_REDO, () => {
      const t = dm.redo();
      if (t !== null) {
        ed.value = t;
        this.bumpLineCountEpoch();
        dm.syncFromEditor(t);
        this.updateSyntaxHighlightLayer();
        if (dm.active?.language === "md") this.scheduleMarkdownPreview();
        if (dm.active?.language === "tex") this.scheduleTexPreview();
        if (dm.active?.language === "max") this.scheduleMaxPreview();
        if (dm.active?.language === "html") this.scheduleHtmlPreview();
        if (dm.active?.language === "oeis") this.scheduleOeisPreview();
        if (this.isSpreadsheetLanguage(dm.active?.language)) this.scheduleCsvGridRefresh();
        this.updateLineNumberGutter();
        this.updateStatusLine();
      }
    });
    d.registerHandler(CMD.EDIT_SELECTALL, () => {
      ed.focus();
      ed.select();
    });
    d.registerHandler(CMD.EDIT_CUT, async () => {
      await cutSelection(ed);
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.EDIT_COPY, async () => {
      await copySelection(ed);
    });
    d.registerHandler(CMD.EDIT_PASTE, async () => {
      await pasteAtCaret(ed);
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.EDIT_DELETE, () => {
      deleteSelectionOrChar(ed);
      this.flushEditorToBuffer();
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.EDIT_UPPERCASE, () => {
      const r = transformSelectionOrWord(ed.value, ed.selectionStart, ed.selectionEnd, (x) => x.toUpperCase());
      this.commitEditor(r.text, r.start, r.end);
    });
    d.registerHandler(CMD.EDIT_LOWERCASE, () => {
      const r = transformSelectionOrWord(ed.value, ed.selectionStart, ed.selectionEnd, (x) => x.toLowerCase());
      this.commitEditor(r.text, r.start, r.end);
    });
    d.registerHandler(CMD.EDIT_DUPLICATE_LINE, () => {
      const r = duplicateLineAtCaret(ed.value, ed.selectionStart);
      this.commitEditor(r.text, r.caret, r.caret);
    });
    d.registerHandler(CMD.EDIT_TRIM_TRAILING, () => {
      const t = trimTrailingWhitespace(ed.value);
      const pos = Math.min(ed.selectionStart, t.length);
      this.commitEditor(t, pos, pos);
    });
    d.registerHandler(CMD.EDIT_INSERT_DATETIME, () => {
      const ins = new Date().toLocaleString();
      const r = insertAt(ed.value, ed.selectionStart, ins);
      this.commitEditor(r.text, r.caret, r.caret);
    });
    d.registerHandler(CMD.EDIT_LINE_UP, () => {
      const r = moveLine(ed.value, ed.selectionStart, -1);
      if (!r) return;
      this.commitEditor(r.text, r.caret, r.caret);
    });
    d.registerHandler(CMD.EDIT_LINE_DOWN, () => {
      const r = moveLine(ed.value, ed.selectionStart, 1);
      if (!r) return;
      this.commitEditor(r.text, r.caret, r.caret);
    });
    d.registerHandler(CMD.EDIT_JOIN_LINES, () => {
      const r = joinLines(ed.value, ed.selectionStart, ed.selectionEnd);
      if (!r) {
        this.updateStatusLine("Join Lines: nothing to join");
        return;
      }
      this.commitEditor(r.text, r.selStart, r.selEnd);
    });
    d.registerHandler(CMD.EDIT_REMOVE_EMPTY_LINES, () => {
      const r = removeEmptyLines(ed.value, ed.selectionStart, ed.selectionEnd);
      this.commitEditor(r.text, r.selStart, r.selEnd);
    });
    d.registerHandler(CMD.EDIT_SORT_LINES, () => {
      const r = sortLines(ed.value, ed.selectionStart, ed.selectionEnd);
      this.commitEditor(r.text, r.selStart, r.selEnd);
    });
    d.registerHandler(CMD.EDIT_INDENT_INCREASE, () => {
      const r = indentLines(ed.value, ed.selectionStart, ed.selectionEnd);
      this.commitEditor(r.text, r.selStart, r.selEnd);
    });
    d.registerHandler(CMD.EDIT_INDENT_DECREASE, () => {
      const r = outdentLines(ed.value, ed.selectionStart, ed.selectionEnd);
      this.commitEditor(r.text, r.selStart, r.selEnd);
    });

    d.registerHandler(CMD.SEARCH_FIND, () => {
      openFindDialog({
        getText: () => ed.value,
        getSelectionStart: () => ed.selectionStart,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
        },
      });
    });
    d.registerHandler(CMD.SEARCH_REPLACE, () => {
      openReplaceDialog({
        getText: () => ed.value,
        setText: (t) => {
          ed.value = t;
          this.bumpLineCountEpoch();
          this.flushEditorToBuffer();
          dm.checkpointHistory(t);
          this.updateSyntaxHighlightLayer();
          if (dm.active?.language === "md") this.scheduleMarkdownPreview();
          if (dm.active?.language === "tex") this.scheduleTexPreview();
          if (dm.active?.language === "max") this.scheduleMaxPreview();
          if (dm.active?.language === "html") this.scheduleHtmlPreview();
          if (dm.active?.language === "oeis") this.scheduleOeisPreview();
          if (this.isSpreadsheetLanguage(dm.active?.language)) this.scheduleCsvGridRefresh();
        },
        getSelectionStart: () => ed.selectionStart,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
        },
      });
    });
    d.registerHandler(CMD.SEARCH_FINDNEXT, () => {
      this.findNextFromSession();
    });
    d.registerHandler(CMD.SEARCH_FINDPREV, () => {
      this.findPrevFromSession();
    });
    d.registerHandler(CMD.SEARCH_SETANDFINDNEXT, () => {
      this.setAndFindNext();
    });
    d.registerHandler(CMD.SEARCH_SETANDFINDPREV, () => {
      this.setAndFindPrev();
    });
    d.registerHandler(CMD.SEARCH_VOLATILE_FINDNEXT, () => {
      this.volatileFindNext();
    });
    d.registerHandler(CMD.SEARCH_VOLATILE_FINDPREV, () => {
      this.volatileFindPrev();
    });
    d.registerHandler(CMD.SEARCH_FINDINCREMENT, () => {
      openIncrementalSearchDialog({
        getText: () => ed.value,
        getCaret: () => ed.selectionStart,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
        },
      });
    });
    d.registerHandler(CMD.SEARCH_FINDINFILES, () => {
      this.flushEditorToBuffer();
      const sess = getFindSession();
      openFindInFilesDialog({
        tabs: dm.documents.map((d) => ({ title: d.title, content: d.content })),
        workspaceRoot: this.folderWorkspacePane?.getRoot() ?? null,
        initialNeedle: sess.needle,
        initialCase: sess.caseSensitive,
        initialWholeWord: sess.wholeWord,
        onFindAll: (hits, meta) => {
          const capped = hits.length >= MAX_FIND_ALL_HITS;
          const q = meta.needle.trim() ? `"${meta.needle}"` : "(empty)";
          const scope =
            meta.scope === "directory"
              ? `directory: ${meta.directory ?? "(selected)"}${meta.filters ? `, filter: ${meta.filters}` : ""}`
              : "open tabs";
          const summary = `${q} — ${hits.length} hit(s) in ${scope}${capped ? ` (showing max ${MAX_FIND_ALL_HITS})` : ""}`;
          this.findResultsPane?.show(summary, hits);
        },
      });
    });
    d.registerHandler(CMD.SEARCH_FINDINDIRECTORY, () => {
      this.flushEditorToBuffer();
      const sess = getFindSession();
      openFindInDirectoryDialog({
        tabs: dm.documents.map((d) => ({ title: d.title, content: d.content })),
        workspaceRoot: this.folderWorkspacePane?.getRoot() ?? null,
        initialNeedle: sess.needle,
        initialCase: sess.caseSensitive,
        initialWholeWord: sess.wholeWord,
        onFindAll: (hits, meta) => {
          const capped = hits.length >= MAX_FIND_ALL_HITS;
          const q = meta.needle.trim() ? `"${meta.needle}"` : "(empty)";
          const summary = `${q} — ${hits.length} hit(s) in directory: ${meta.directory ?? "(selected)"}${meta.filters ? `, filter: ${meta.filters}` : ""}${capped ? ` (showing max ${MAX_FIND_ALL_HITS})` : ""}`;
          this.findResultsPane?.show(summary, hits);
        },
      });
    });
    d.registerHandler(CMD.SEARCH_GOTOLINE, () => {
      openGotoLineDialog({
        getText: () => ed.value,
        setSelection: (a, b) => {
          this.revealEditorSelection(a, b);
          this.updateStatusLine();
        },
      });
    });
    d.registerHandler(CMD.SEARCH_GOTOMATCHINGBRACE, () => {
      this.goMatchingBrace();
    });
    d.registerHandler(CMD.SEARCH_COUNT, () => {
      const s = getFindSession();
      openCountDialog({
        getText: () => ed.value,
        initialNeedle: s.needle,
        initialCase: s.caseSensitive,
        initialWholeWord: s.wholeWord,
      });
    });

    d.registerHandler(CMD.VIEW_WRAP, () => {
      const on = !d.isCommandChecked(CMD.VIEW_WRAP);
      d.setCommandChecked(CMD.VIEW_WRAP, on);
      this.applyEditorWhiteSpaceFromWrapPreference();
      this.menuBar?.syncCheckboxStates([CMD.VIEW_WRAP]);
      const prev = loadWebPrefs();
      saveWebPrefs({ ...prev, defaultWordWrap: on });
    });
    d.setCommandChecked(CMD.VIEW_WRAP, false);
    d.registerHandler(CMD.VIEW_LINENUMBER, () => {
      const on = !d.isCommandChecked(CMD.VIEW_LINENUMBER);
      d.setCommandChecked(CMD.VIEW_LINENUMBER, on);
      this.showLineNumbers = on;
      this.menuBar?.syncCheckboxStates([CMD.VIEW_LINENUMBER]);
      this.updateLineNumberGutter();
      if (this.multiviewEnabled) {
        const used = this.multiviewExtraSlotsUsed();
        for (let s = 0; s < used; s++) this.updateLineNumberGutterForExtraSlot(s);
      }
    });
    d.setCommandChecked(CMD.VIEW_LINENUMBER, true);

    d.registerHandler(CMD.VIEW_ZOOMIN, () => {
      this.zoomPercent = Math.min(300, this.zoomPercent + 10);
      this.applyZoomCss();
    });
    d.registerHandler(CMD.VIEW_ZOOMOUT, () => {
      this.zoomPercent = Math.max(40, this.zoomPercent - 10);
      this.applyZoomCss();
    });
    d.registerHandler(CMD.VIEW_ZOOMRESTORE, () => {
      this.zoomPercent = 100;
      this.applyZoomCss();
    });
    d.registerHandler(CMD.VIEW_FULLSCREEN, async () => {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    });
    d.setCommandChecked(CMD.VIEW_MULTIVIEW, false);
    d.registerHandler(CMD.VIEW_MULTIVIEW, () => {
      if (this.multiviewEnabled) this.setMultiviewEnabled(false);
      else this.setMultiviewEnabled(true);
    });
    d.registerHandler(CMD.VIEW_SINGLE_EDITOR, () => {
      if (!this.multiviewEnabled) {
        this.updateStatusLine("Already in single editor view.");
        return;
      }
      this.setMultiviewEnabled(false);
    });
    d.registerHandler(CMD.VIEW_GIT_TERMINAL, () => {
      this.gitTerminalHost.hidden = false;
      this.gitTerminalPanel?.focusCommandInput();
    });
    d.registerHandler(CMD.FORMAT_TODOS, () => {
      dm.applyEol("crlf");
      ed.value = dm.active!.content;
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.FORMAT_TOUNIX, () => {
      dm.applyEol("lf");
      ed.value = dm.active!.content;
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.FORMAT_TOMAC, () => {
      dm.applyEol("cr");
      ed.value = dm.active!.content;
      dm.checkpointHistory(ed.value);
      refresh();
    });
    d.registerHandler(CMD.FORMAT_UTF_8, () => {
      dm.setEncoding("utf-8");
      this.updateStatusLine();
    });
    d.registerHandler(CMD.FORMAT_ANSI, () => {
      dm.setEncoding("windows-1252");
      this.updateStatusLine();
    });

    for (const row of LANGUAGE_MENU_ROWS) {
      if (row.kind !== "item") continue;
      d.registerHandler(row.id, async () => {
        dm.setActiveLanguage(row.mode);
        if (row.mode === "linuxobj") {
          await this.applyLinuxObjectView();
        }
        this.refreshEditorSurface();
        this.updateStatusLine();
      });
    }
    d.registerHandler(CMD.LANG_STYLE_DLG, () => {
      void alert("Style Configurator is not available in the WEB shell.");
    });

    d.registerHandler(CMD.SETTING_PREF, () => {
      openPreferencesDialog({
        initial: loadWebPrefs(),
        onOpenShortcutMapper: () => this.openShortcutMapperFromSettings(),
        onSave: (p) => {
          const prev = loadWebPrefs();
          const merged = { ...prev, ...p };
          saveWebPrefs(merged);
          this.editorBaseFontPx = merged.editorBaseFontPx;
          this.applyZoomCss();
          this.setNativeSpellcheckEnabled(merged.nativeSpellcheckEnabled === true);
        },
      });
    });

    d.registerHandler(CMD.SETTING_SHORTCUT, () => {
      this.openShortcutMapperFromSettings();
    });

    d.registerHandler(CMD.SETTING_MARKDOWN_PREVIEW_STYLE, () => {
      openMarkdownPreviewStyleDialog({
        initial: loadWebPrefs().markdownPreviewStyle,
        onSave: (markdownPreviewStyle) => {
          const prev = loadWebPrefs();
          saveWebPrefs({ ...prev, markdownPreviewStyle });
          this.syncMarkdownPreviewChromeFromPrefs();
          if (this.documents.active?.language === "md") this.scheduleMarkdownPreview();
        },
      });
    });

    d.registerHandler(CMD.TOOL_MD5, () => {
      void alert(
        "MD5 is not exposed in Web Crypto. Use SHA-256 on selection, or run desktop Notepad++ Tools menu.",
      );
    });
    d.registerHandler(CMD.TOOL_SHA256, async () => {
      const sel = ed.value.slice(ed.selectionStart, ed.selectionEnd) || ed.value;
      const hex = await sha256Hex(sel);
      void navigator.clipboard.writeText(hex);
      this.updateStatusLine("SHA-256 (selection or full doc) copied to clipboard");
    });
    d.registerHandler(CMD.TOOL_BASE64_ENCODE, () => {
      const s = ed.selectionStart;
      const e = ed.selectionEnd;
      const slice = ed.value.slice(s, e) || ed.value;
      if (!slice) return;
      const enc = utf8ToBase64(slice);
      if (s === e) this.commitEditor(enc, 0, enc.length);
      else this.commitEditor(ed.value.slice(0, s) + enc + ed.value.slice(e), s, s + enc.length);
    });
    d.registerHandler(CMD.TOOL_BASE64_DECODE, () => {
      const s = ed.selectionStart;
      const e = ed.selectionEnd;
      const slice = ed.value.slice(s, e) || ed.value;
      try {
        const dec = base64ToUtf8(slice);
        if (s === e) this.commitEditor(dec, 0, dec.length);
        else this.commitEditor(ed.value.slice(0, s) + dec + ed.value.slice(e), s, s + dec.length);
      } catch {
        void alert("Invalid Base64 data.");
      }
    });
    d.registerHandler(CMD.TOOL_GEMINI_BEAUTIFY_SELECTION, async () => {
      const s = ed.selectionStart;
      const e = ed.selectionEnd;
      const lo = Math.min(s, e);
      const hi = Math.max(s, e);
      const slice = ed.value.slice(lo, hi);
      if (!slice.trim()) {
        this.updateStatusLine("Select non-empty text to send to Gemini");
        return;
      }
      this.updateStatusLine("Gemini: requesting suggestion…");
      const result = await requestBeautifySelection(slice);
      if (!result.ok) {
        void alert(result.error);
        this.updateStatusLine("Gemini beautify failed");
        return;
      }
      openBeautifySelectionDialog({
        original: slice,
        suggested: result.suggestedText,
        onAccept: (edited) => {
          const next = ed.value.slice(0, lo) + edited + ed.value.slice(hi);
          this.commitEditor(next, lo, lo + edited.length);
          this.updateStatusLine("Gemini: suggestion applied");
        },
        onDecline: () => {
          this.updateStatusLine("Gemini: suggestion declined");
        },
      });
    });

    d.registerHandler(CMD.HELP_NULLAPAD_STORY, () => {
      openAboutNullaPadDialog();
    });
    d.registerHandler(CMD.HELP_NULLAPAD_USERS_GUIDE, () => {
      openNullaPadUsersGuideDialog();
    });
    d.registerHandler(CMD.HELP_ABOUT, () => {
      void alert("Notepad++ Web shell — static docs in repo (npp-web). No Node required for static/ build.");
    });
    d.registerHandler(CMD.HELP_NULLAPAD_IDENTITY, () => {
      this.nullapadSecretInput.focus();
      this.nullapadSecretInput.select();
    });
    d.registerHandler(CMD.HELP_CMDLINE, () => {
      void alert("WEB build: URL query parameters may be added later for file open (see winmain / Parameters in desktop).");
    });
    d.registerHandler(CMD.HELP_NULLAPAD_MAX_GUIDE, () => {
      openMaxLanguageGuideDialog();
    });
    d.registerHandler(CMD.EXAMPLE_QUICK_START, () => {
      this.openExampleTab("quickstart.max", quickstartMaxExample, "MAX — Quick Start", "Quick Start (MAX) example loaded.");
    });
    d.registerHandler(CMD.EXAMPLE_SHOWCASE, () => {
      this.openExampleTab("showcase.max", showcaseMaxExample, "MAX — Showcase", "Showcase (MAX) example loaded.");
    });
    d.registerHandler(CMD.EXAMPLE_MATH_PAPER, () => {
      this.openExampleTab("math_paper.max", mathPaperMaxExample, "MAX — Math Paper", "Math Paper (MAX) example loaded.");
    });
  }

  /** Open a new tab with bundled example content (sync; sources live under `demo/*.max`). */
  private openExampleTab(filename: string, content: string, titleHint: string, successMessage: string): void {
    this.flushEditorToBuffer();
    const dm = this.documents;
    const ed = this.editor;
    dm.checkpointHistory(ed.value);
    dm.addContentTab(filename, content, "max");
    this.tabBar?.refresh();
    this.loadActiveIntoEditor();
    this.rebuildMenubar();
    this.persistTabSessionToStorage();
    this.updateStatusLine(successMessage);
  }

  private onGlobalKeydown = (ev: KeyboardEvent): void => {
    if (ev.defaultPrevented) return;
    if ((ev.ctrlKey || ev.metaKey) && !ev.altKey) {
      if (ev.key === "=" || ev.key === "+") {
        ev.preventDefault();
        void this.dispatcher.executeCommand(CMD.VIEW_ZOOMIN);
        this.shell.dispatchEvent(
          new CustomEvent(NPP_COMMAND_EVENT, {
            bubbles: true,
            composed: true,
            detail: { commandId: CMD.VIEW_ZOOMIN, source: "keyboard" as const },
          }),
        );
        return;
      }
      if (ev.key === "-" || ev.key === "_") {
        ev.preventDefault();
        void this.dispatcher.executeCommand(CMD.VIEW_ZOOMOUT);
        this.shell.dispatchEvent(
          new CustomEvent(NPP_COMMAND_EVENT, {
            bubbles: true,
            composed: true,
            detail: { commandId: CMD.VIEW_ZOOMOUT, source: "keyboard" as const },
          }),
        );
        return;
      }
      if (ev.key === "/" && !ev.shiftKey) {
        ev.preventDefault();
        void this.dispatcher.executeCommand(CMD.VIEW_ZOOMRESTORE);
        this.shell.dispatchEvent(
          new CustomEvent(NPP_COMMAND_EVENT, {
            bubbles: true,
            composed: true,
            detail: { commandId: CMD.VIEW_ZOOMRESTORE, source: "keyboard" as const },
          }),
        );
        return;
      }
    }
    const k = shortcutFromKeyboardEvent(ev);
    if (!k) return;
    const id = this.keyboardChordMap[k];
    if (!id) return;
    ev.preventDefault();
    void this.dispatcher.executeCommand(id);
    this.shell.dispatchEvent(
      new CustomEvent(NPP_COMMAND_EVENT, {
        bubbles: true,
        composed: true,
        detail: { commandId: id, source: "keyboard" as const },
      }),
    );
  };
}

async function sha256Hex(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function copySelection(ta: HTMLTextAreaElement): Promise<void> {
  const s = ta.selectionStart;
  const e = ta.selectionEnd;
  await navigator.clipboard.writeText(ta.value.slice(s, e));
}

async function cutSelection(ta: HTMLTextAreaElement): Promise<void> {
  await copySelection(ta);
  ta.setRangeText("", ta.selectionStart, ta.selectionEnd, "end");
}

async function pasteAtCaret(ta: HTMLTextAreaElement): Promise<void> {
  const t = await navigator.clipboard.readText();
  ta.setRangeText(t, ta.selectionStart, ta.selectionEnd, "end");
}

function deleteSelectionOrChar(ta: HTMLTextAreaElement): void {
  const s = ta.selectionStart;
  const e = ta.selectionEnd;
  if (s !== e) ta.setRangeText("", s, e, "end");
  else if (s < ta.value.length) ta.setRangeText("", s, s + 1, "end");
}

